import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import { useConfirm } from '../context/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import { ROLES_CRUD } from '../constants/roles';

const STEPS = [
  { id: 0, title: 'B1: Datos IES', short: 'B1' },
  { id: 1, title: 'B1: Personal académico', short: 'B1' },
  { id: 2, title: 'B1: Redes académicas', short: 'B1' },
  { id: 3, title: 'B1: Mapeo de investigación', short: 'B1' },
  { id: 4, title: 'B2: Proyectos de investigación', short: 'B2' },
  { id: 5, title: 'B3: Producción científica y tecnológica', short: 'B3' },
  { id: 6, title: 'B4: Vigilancia tecnológica e innovación', short: 'B4' },
  { id: 7, title: 'B5: PND/ODS Alineación estratégica', short: 'B5' },
];

const PROYECTOS_DROPBOX_URL = 'https://www.dropbox.com/request/a8t2hzaowg138kjtxopo';
const PROYECTOS_TEMPLATE_URL = '/plantillas/informacion-proyectos.csv';
const PRODUCCION_DROPBOX_URL = 'https://www.dropbox.com/request/a8t2hzaowg138kjtxopo';
const PRODUCCION_CONGRESOS_TEMPLATE_URL = '/plantillas/informacion-congresos.xlsx';
const PRODUCCION_LIBROS_TEMPLATE_URL = '/plantillas/informacion-libros.xlsx';
const PRODUCCION_CONGRESOS_CSV_TEMPLATE_URL = '/plantillas/informacion-congresos.csv';
const PRODUCCION_LIBROS_CSV_TEMPLATE_URL = '/plantillas/informacion-libros.csv';

const PROPIEDAD_INTELECTUAL_CATEGORIAS = [
  { key: 'patentes_invencion', label: 'Patentes de invención' },
  { key: 'modelos_utilidad', label: 'Modelos de utilidad' },
  { key: 'disenos_industriales', label: 'Diseños industriales' },
  { key: 'marcas', label: 'Marcas' },
  { key: 'derechos_autor', label: 'Derechos de autor' },
];

const PROPIEDAD_INTELECTUAL_ESTADOS = [
  { value: '', label: 'Seleccione estado' },
  { value: 'solicitud', label: 'Solicitud' },
  { value: 'concesion', label: 'Concesión' },
  { value: 'finalizacion_negativa_cierre', label: 'Finalización negativa o cierre' },
];

const TRANSFERENCIA_TECNOLOGICA_TIPOS = [
  { value: '', label: 'Seleccione mecanismo' },
  { value: 'licenciamiento', label: 'Licenciamiento' },
  { value: 'spin_off', label: 'Spin-off' },
  { value: 'servicios_especializados', label: 'Servicios especializados' },
  { value: 'contrato_transferencia', label: 'Contrato de transferencia' },
  { value: 'convenio_explotacion', label: 'Convenio de explotación' },
  { value: 'otro', label: 'Otro mecanismo' },
];

const VT01_OPTIONS = [
  { v: 1, label: 'Sí, unidad y/o proceso formal de vigilancia tecnológica (VT)' },
  { v: 2, label: 'Sí, responsable asignado' },
  { v: 3, label: 'Sí, proceso informal' },
  { v: 4, label: 'No existe' },
];

const ODS_LIST = [
  { num: 1, label: '1. Fin de la pobreza' },
  { num: 2, label: '2. Hambre cero' },
  { num: 3, label: '3. Salud y bienestar' },
  { num: 4, label: '4. Educación de calidad' },
  { num: 5, label: '5. Igualdad de género' },
  { num: 6, label: '6. Agua limpia y saneamiento' },
  { num: 7, label: '7. Energía asequible y no contaminante' },
  { num: 8, label: '8. Trabajo decente y crecimiento económico' },
  { num: 9, label: '9. Industria, innovación e infraestructura' },
  { num: 10, label: '10. Reducción de las desigualdades' },
  { num: 11, label: '11. Ciudades y comunidades sostenibles' },
  { num: 12, label: '12. Producción y consumo responsables' },
  { num: 13, label: '13. Acción por el clima' },
  { num: 14, label: '14. Vida submarina' },
  { num: 15, label: '15. Vida de ecosistemas terrestres' },
  { num: 16, label: '16. Paz, justicia e instituciones sólidas' },
  { num: 17, label: '17. Alianzas para lograr los objetivos' },
];
const VALID_ODS_NUMBERS = new Set(ODS_LIST.map((item) => item.num));
const VALID_CINE_FIELD_IDS = new Set(Array.from({ length: 10 }, (_, index) => String(index + 1)));

const VT_P39_OPTIONS = [
  { value: 'unidad_proceso_formal', label: 'Sí, unidad y/o proceso formal de vigilancia tecnológica (VT)' },
  { value: 'responsable_proceso_formal', label: 'Sí, responsable asignado con proceso formal de vigilancia tecnológica (VT)' },
  { value: 'proceso_informal', label: 'Sí, proceso informal' },
  { value: 'no_existe', label: 'No existe' },
];

const VT_P48_OPTIONS = [
  { value: 'boletines_alertas', label: 'Boletines o alertas tecnológicas' },
  { value: 'informes_vigilancia', label: 'Informes de vigilancia o tendencias tecnológicas' },
  { value: 'estados_arte', label: 'Estados del arte o revisiones tecnológicas' },
  { value: 'informes_analisis', label: 'Informes de análisis científico o de patentes' },
  { value: 'reportes_estrategicos', label: 'Reportes estratégicos para apoyo a la toma de decisiones' },
  { value: 'no_productos_formales', label: 'No se generan productos formales de vigilancia tecnológica (VT)' },
];

const ODS_LIST_DETAILED = [
  { num: 1, label: 'Fin de la pobreza', color: '#E5243B' },
  { num: 2, label: 'Hambre cero', color: '#DDA63A' },
  { num: 3, label: 'Salud y bienestar', color: '#4C9F38' },
  { num: 4, label: 'Educación de calidad', color: '#C5192D' },
  { num: 5, label: 'Igualdad de género', color: '#FF3A21' },
  { num: 6, label: 'Agua limpia y saneamiento', color: '#26BDE2' },
  { num: 7, label: 'Energía asequible y no contaminante', color: '#FCC30B' },
  { num: 8, label: 'Trabajo decente y crecimiento económico', color: '#A21942' },
  { num: 9, label: 'Industria, innovación e infraestructura', color: '#FD6925' },
  { num: 10, label: 'Reducción de las desigualdades', color: '#DD1367' },
  { num: 11, label: 'Ciudades y comunidades sostenibles', color: '#FD9D24' },
  { num: 12, label: 'Producción y consumo responsables', color: '#BF8B2E' },
  { num: 13, label: 'Acción por el clima', color: '#3F7E44' },
  { num: 14, label: 'Vida submarina', color: '#0A97D9' },
  { num: 15, label: 'Vida de ecosistemas terrestres', color: '#56C02B' },
  { num: 16, label: 'Paz, justicia e instituciones sólidas', color: '#00689D' },
  { num: 17, label: 'Alianzas para lograr los objetivos', color: '#19486A' },
];

const VINCULACION_RESULTADOS_OPCIONES = [
  { key: 'educacion_continua', label: 'Educación continua' },
  { key: 'practicas_preprofesionales', label: 'Prácticas preprofesionales' },
  { key: 'proyectos_servicios_especializados', label: 'Proyectos y servicios especializados' },
  { key: 'divulgacion_aplicacion_conocimientos', label: 'Divulgación y resultados de aplicación de conocimientos científicos' },
  { key: 'proyectos_innovacion', label: 'Ejecución de proyectos de innovación' },
  { key: 'servicios_comunitarios_sociales', label: 'Ejecución de proyectos de servicios comunitarios o sociales' },
  { key: 'otros', label: 'Otros' },
];

const VINCULACION_OPCIONES = [
  'Mesas de trabajo sectoriales',
  'Convenios con ministerios',
  'Participación en comités técnicos',
  'Consultorías para política pública',
  'Investigación aplicada a problemas nacionales',
  'Programas de vinculación con la sociedad',
  'Observatorios sectoriales',
  'Participación en agendas de investigación',
];

const RANKINGS_CONOCIDOS = [
  { v: 'QS World University Rankings', label: 'QS World University Rankings' },
  { v: 'THE World University Rankings', label: 'THE (Times Higher Education)' },
  { v: 'SCImago Institutions Rankings', label: 'SCImago Institutions Rankings' },
  { v: 'Webometrics', label: 'Webometrics' },
  { v: 'ARWU (Shanghai)', label: 'ARWU – Shanghai Rankings' },
  { v: 'URAP', label: 'URAP' },
  { v: 'RUR Rankings', label: 'RUR Rankings' },
];

const ACREDITACIONES_CONOCIDAS = [
  { v: 'CACES', label: 'CACES (Ecuador)' },
  { v: 'ABET', label: 'ABET' },
  { v: 'AACSB', label: 'AACSB' },
  { v: 'EQUIS', label: 'EQUIS' },
  { v: 'AMBA', label: 'AMBA' },
  { v: 'ISO 9001', label: 'ISO 9001' },
  { v: 'RIACES', label: 'RIACES' },
];

const AMBITOS_POLITICA_PUBLICA = [
  'Educación',
  'Salud pública',
  'Ciencia, tecnología e innovación',
  'Desarrollo productivo',
  'Empleo y trabajo',
  'Ambiente y cambio climático',
  'Agricultura y soberanía alimentaria',
  'Seguridad ciudadana',
  'Inclusión social',
  'Género e igualdad',
  'Movilidad y transporte',
  'Ordenamiento territorial',
  'Economía y finanzas públicas',
  'Transformación digital',
  'Cultura y patrimonio',
  'Energía y recursos naturales',
  'Gestión de riesgos',
  'Desarrollo local / GAD',
  'Otros',
  'No aplica',
];

const P52_OTHER_LABEL = 'Otros';
const P52_OTHER_PREFIX = 'Otro: ';
const P52_NO_APLICA_LABEL = 'No aplica';

const SECTORES_CIIU = [
  'A - Agricultura, ganadería, silvicultura y pesca',
  'B - Minería y canteras',
  'C - Manufactura',
  'D - Electricidad, gas y vapor',
  'E - Agua y saneamiento',
  'F - Construcción',
  'G - Comercio',
  'H - Transporte y almacenamiento',
  'I - Alojamiento y comida',
  'J - Información y comunicación',
  'K - Finanzas y seguros',
  'L - Inmobiliaria',
  'M - Actividades profesionales y científicas',
  'N - Servicios administrativos',
  'O - Administración pública',
  'P - Enseñanza',
  'Q - Salud humana',
  'R - Artes y entretenimiento',
  'S - Otros servicios',
  'T/U - Hogares y organismos internacionales',
];

/* ─── Utility functions ─────────────────────────────────────────────────── */

function matrixYearKeys(anio) {
  const end = Number(anio);
  const y = Number.isFinite(end) ? end : new Date().getFullYear();
  return [y - 4, y - 3, y - 2, y - 1, y].map(String);
}

function deepMergeForm(base, over) {
  const out = { ...base };
  for (const k of Object.keys(over || {})) {
    const bv = out[k];
    const ov = over[k];
    if (ov && typeof ov === 'object' && !Array.isArray(ov) && bv && typeof bv === 'object' && !Array.isArray(bv)) {
      out[k] = deepMergeForm(bv, ov);
    } else {
      out[k] = ov;
    }
  }
  return out;
}

function buildEmptyMatrices(anio) {
  const years = matrixYearKeys(anio);
  const hmRow = () => Object.fromEntries(years.map((yy) => [yy, { h: '', m: '' }]));
  const cineRow = () =>
    Object.fromEntries(
      years.map((yy) => [yy, Object.fromEntries(Array.from({ length: 10 }, (_, i) => [String(i + 1), '']))])
    );
  return {
    docentes_tc_titulares_inv: hmRow(),
    docentes_tc_no_titulares_inv: hmRow(),
    proyectos_interno_cine: cineRow(),
    proyectos_sin_fin_cine: cineRow(),
    proyectos_externo_cine: cineRow(),
    proyectos_redes_cine: cineRow(),
    num_patentes_5_anios: '',
    estudiantes_pregrado_participacion_proyectos: '',
    estudiantes_posgrado_participacion_proyectos: '',
    matrices_texto_q28: '',
    matrices_texto_q29: '',
  };
}

function ensureMatricesShape(raw, anio) {
  return deepMergeForm(buildEmptyMatrices(anio), raw || {});
}

function emptyExtra() {
  return {
    proyectos_detalle: [],
    num_lineas_declarado: 0,
    lineas_detalle: [],
    num_grupos_declarado: 0,
    grupos_detalle: [],
    num_centros_declarado: 0,
    centros_detalle: [],
    tiene_centros: null,
    centros_promedio_personal_h: '',
    centros_promedio_personal_m: '',
    centros_presupuesto_anual_usd: '',
    p27_patentes: {},
    p37_transferencia: {},
    congresos_detalle: [],
    libros_detalle: [],
    congresos_por_cine: '',
    libros_por_cine: '',
    q1_5_anios: '',
    q2_5_anios: '',
    q3_5_anios: '',
    q4_5_anios: '',
    matrices_5_anios: {},
    pnd_objetivos_seleccion: [],
    pnd_integracion_2025: null,
    pnd_integracion_anterior: null,
    politicas_publicas_uso: null,
    vinculacion_resultados: [],
    articulacion_gobiernos: null,
    articulacion_sector_productivo: null,
    ods_formalmente_incorporados: null,
    ods_seguimiento_nivel: null,
    ods_priorizados: [],
    p52_ambitos_politica_publica: [],
    p53_sectores_ciiu_articulacion: [],
    p55_importancia_vinculacion_pnd: '',
    p56_grado_alineacion_pnd: '',
    p57_objetivos_pnd: [],
    p58_nivel_participacion_consejos: '',
    p59_articulacion_sector_productivo_pnd: '',
    p60_resultados_politicas_publicas: '',
    p61_integracion_ods_investigacion: '',
    p62_medicion_impacto_ods: '',
    p63_objetivos_ods: [],
    p64_articulacion_actores_desarrollo: '',
    p21_aplica: null,
    p22_aplica: null,
    p28_aplica: null,
    p28_tiene_desglose: '',
    p29_aplica: null,
    p29_tiene_desglose: '',
    tiene_personal_investigacion: null,
    tiene_personal_apoyo: null,
    responsable_nombre: '',
    responsable_cargo: '',
    responsable_correo: '',
    responsable_telefono: '',
    vt_operativo: {
      normativa: null,
      normativa_detalle: '',
      periodicidad: '',
      personas_tc: '',
      personas_mt: '',
      horas_mensuales: '',
      presupuesto_usd: '',
    },
    produccion: {
      p30_scopus_wos: {},
      p31_latindex: {},
      p32_sin_index: {},
      p33_congresos_nacionales: {},
      p33_congresos_internacionales: {},
      p34_libros: {},
      p35_q1: {},
      p36_q2: {},
      p37_q3: {},
      p38_q4: {},
    },
    produccion_ext: {
      citas: {},
      autoria_femenina_pct: {},
      colab_internacional_pct: {},
    },
  };
}

function mergeExtra(base, patch) {
  const def = emptyExtra();
  const merged = { ...def, ...(base || {}), ...(patch || {}) };
  merged.matrices_5_anios = deepMergeForm(
    deepMergeForm(def.matrices_5_anios || {}, (base || {}).matrices_5_anios || {}),
    (patch || {}).matrices_5_anios || {}
  );
  merged.produccion = deepMergeForm(
    deepMergeForm(def.produccion || {}, (base || {}).produccion || {}),
    (patch || {}).produccion || {}
  );
  merged.vt_operativo = {
    ...def.vt_operativo,
    ...((base || {}).vt_operativo || {}),
    ...((patch || {}).vt_operativo || {}),
  };
  merged.produccion_ext = deepMergeForm(
    deepMergeForm(def.produccion_ext || {}, (base || {}).produccion_ext || {}),
    (patch || {}).produccion_ext || {}
  );
  return merged;
}

function ensureListLen(arr, n) {
  const a = [...(arr || [])];
  while (a.length < n) a.push('');
  return a.slice(0, n);
}

function sanitizeInlineText(value) {
  return Array.from(String(value || ''))
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('')
    .trim();
}

function sanitizeFreeTextPreserveSpacing(value) {
  return Array.from(String(value || ''))
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
}

const MAX_FORM_NUMBER = 9999999;
const MAX_PHONE_LENGTH = 10;
const PHONE_REGEX = /^09\d{8}$/;
const YEAR_MIN = 1900;
const YEAR_MAX = 2100;

const PROYECTO_CSV_COLUMN_MAP = {
  CODIGO: 'codigo',
  NOMBRE: 'nombre',
  TITULO: 'titulo',
  TIPO_PROYECTO: 'tipo_proyecto',
  PROGRAMA: 'programa',
  OBJETIVO: 'objetivo',
  ESTADO: 'estado',
  LINEA_INVESTIGACION: 'linea_investigacion',
  LINEAS: 'lineas',
  FACULTAD_ENTIDAD_RESPONABLE: 'facultad_entidad_responsable',
  FECHA_INICIO: 'fecha_inicio',
  FECHA_FIN: 'fecha_fin',
  FECHA_FIN_PLANEADO: 'fecha_fin_planeado',
  FECHA_FIN_REAL: 'fecha_fin_real',
  COORDINADOR_DIRECTOR: 'coordinador_director',
  CORREO_ELECTRONICO_COORDINADOR: 'correo_electronico_coordinador',
  TELEFONO_COORDINADOR: 'telefono_coordinador',
  CAMPO_DETALLADO: 'campo_detallado',
  CAMPO_CINE: 'campo_cine',
  CAMPOS_CINE: 'campos_cine',
  ALCANCE_TERRITORIAL: 'alcance_territorial',
  IMPACTO_SOCIAL: 'impacto_social',
  IMPACTO_CIENTIFICO: 'impacto_cientifico',
  IMPACTO_ECONOMICO: 'impacto_economico',
  IMPACTO_POLITICO: 'impacto_politico',
  OTRO_IMPACTO: 'otro_impacto',
  IMPACTO_AMBIENTAL: 'impacto_ambiental',
  INVESTIGADORES_ACREDITADOS_SENESCYT: 'investigadores_acreditados_senescyt',
  FUENTE_FINANCIAMIENTO: 'fuente_financiamiento',
  DESCRIPCION_ACTIVIDAD_ID: 'descripcion_actividad_id',
  DESCRIPCION_ACTIVIDAD_I_D: 'descripcion_actividad_id',
  PARAMETRO_CUMPLIMIENTO: 'parametro_cumplimiento',
  GRUPO_GASTO: 'grupo_gasto',
  ITEM: 'item',
  COOPERACION: 'cooperacion',
  RED: 'red',
  RESULTADOS_VERIFICABLES: 'resultados_verificables',
  PRESUPUESTO: 'presupuesto',
  ODS: 'ods',
  OBJETIVOS_ODS: 'objetivos_ods',
  INVESTIGADORES: 'investigadores',
  INVESTIGADORES_IDS: 'investigadores_ids',
  INVESTIGADORES_ROLES: 'investigadores_roles',
  INVESTIGADORES_PROYECTO: 'investigadores',
  INVESTIGADOR_IDS: 'investigadores_ids',
  ROLES_INVESTIGADORES: 'investigadores_roles',
  NUM_INVESTIGADORES: 'num_investigadores',
  NUM_ESTUDIANTES_PREGRADO: 'num_estudiantes_pregrado',
  NUM_ESTUDIANTES_POSGRADO: 'num_estudiantes_posgrado',
};

const PRODUCCION_CONGRESO_CSV_COLUMN_MAP = {
  ANIO: 'anio',
  CAMPO_CINE: 'campo_cine',
  NOMBRE_CONGRESO_O_EVENTO: 'nombre_congreso_o_evento',
  NOMBRE_EVENTO: 'nombre_congreso_o_evento',
  ALCANCE: 'alcance',
  DOCENTES_ASISTENTES: 'docentes_asistentes',
  OBSERVACIONES: 'observaciones',
};

const PRODUCCION_LIBRO_CSV_COLUMN_MAP = {
  ANIO: 'anio',
  CAMPO_CINE: 'campo_cine',
  TITULO_OBRA: 'titulo_obra',
  TIPO_OBRA: 'tipo_obra',
  AUTORES: 'autores',
  EDITORIAL: 'editorial',
  OBSERVACIONES: 'observaciones',
};

function normalizeCsvHeaderKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\+/g, '_')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function parseDelimitedLine(line, delimiter = ';') {
  const cells = [];
  let current = '';
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (insideQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
      continue;
    }
    if (char === delimiter && !insideQuotes) {
      cells.push(current);
      current = '';
      continue;
    }
    current += char;
  }

  cells.push(current);
  return cells.map((cell) => cell.replace(/^\uFEFF/, '').trim());
}

function parseDelimitedText(text, delimiter = ';') {
  const normalizedText = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let current = '';
  let insideQuotes = false;

  for (let index = 0; index < normalizedText.length; index += 1) {
    const char = normalizedText[index];
    if (char === '"') {
      if (insideQuotes && normalizedText[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
        current += char;
      }
      continue;
    }
    if (char === '\n' && !insideQuotes) {
      if (current.trim()) {
        rows.push(parseDelimitedLine(current, delimiter));
      }
      current = '';
      continue;
    }
    current += char;
  }

  if (current.trim()) {
    rows.push(parseDelimitedLine(current, delimiter));
  }

  return rows;
}

function parseStringListValue(value) {
  return String(value || '')
    .split(/[;,|]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseProjectNumberList(rawValue, { allowedSet = null } = {}) {
  const tokens = parseStringListValue(rawValue);
  const valid = [];
  const invalid = [];
  tokens.forEach((token) => {
    const parsed = Number.parseInt(token, 10);
    if (Number.isNaN(parsed)) {
      invalid.push(token);
      return;
    }
    if (allowedSet && !allowedSet.has(parsed)) {
      invalid.push(token);
      return;
    }
    valid.push(parsed);
  });
  return { valid, invalid };
}

function parseProjectInvestigadores(rawValue, rawRolesValue) {
  const tokens = String(rawValue || '')
    .split(/[;,|]/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (!tokens.length) return { valid: [], invalid: [], roleCountMismatch: false };

  const roles = String(rawRolesValue || '')
    .split(/[;,|]/)
    .map((item) => item.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];
  tokens.forEach((token, index) => {
      const inlineParts = token.split(/[:=-]/).map((item) => item.trim()).filter(Boolean);
      const investigatorId = Number.parseInt(inlineParts[0] || '', 10);
      if (Number.isNaN(investigatorId)) {
        invalid.push(token);
        return;
      }
      const rol = inlineParts[1] || roles[index] || '';
      valid.push(rol ? { investigador_id: investigatorId, rol } : { investigador_id: investigatorId });
    });

  return {
    valid,
    invalid,
    roleCountMismatch: roles.length > 0 && roles.length !== tokens.length,
  };
}

function parseProjectCsvText(text) {
  const rows = parseDelimitedText(text, ';');
  if (!rows.length) {
    return { rows: [], total: 0, invalid: 0, investigatorsLinked: 0, odsLinked: 0, warnings: [] };
  }

  const [headerRow, ...dataRows] = rows;
  const headers = headerRow.map((header) => normalizeCsvHeaderKey(header));
  const recognizedColumns = headers.filter((header) => PROYECTO_CSV_COLUMN_MAP[header]);
  if (!recognizedColumns.length) {
    throw new Error('La plantilla de proyectos no tiene columnas reconocibles. Descargue la plantilla oficial e inténtelo de nuevo.');
  }

  const parsedRows = [];
  let invalid = 0;
  let investigatorsLinked = 0;
  let odsLinked = 0;
  const warnings = [];

  dataRows.forEach((row, rowIndex) => {
    const source = {};
    headers.forEach((header, columnIndex) => {
      const internalKey = PROYECTO_CSV_COLUMN_MAP[header];
      if (!internalKey) return;
      const rawValue = row[columnIndex] ?? '';
      const cleanedValue = sanitizeFreeTextPreserveSpacing(rawValue).trim();
      if (cleanedValue) {
        source[internalKey] = cleanedValue;
      }
    });

    const hasCode = !!String(source.codigo || '').trim();
    const hasTitle = !!String(source.nombre || source.titulo || '').trim();
    const hasMeaningfulContent = Object.keys(source).length > 0;
    if (!hasMeaningfulContent) return;
    if (!hasCode && !hasTitle) {
      invalid += 1;
      return;
    }

    const normalizedRow = {
      ...source,
      codigo: source.codigo || '',
      nombre: source.nombre || source.titulo || '',
      titulo: source.titulo || source.nombre || '',
      linea_investigacion: source.linea_investigacion || '',
      lineas: parseStringListValue(source.lineas || source.linea_investigacion || ''),
      orden_importacion: rowIndex + 1,
    };
    normalizedRow.linea_investigacion = normalizedRow.lineas[0] || '';

    if (normalizedRow.ods) {
      const parsedOds = parseProjectNumberList(normalizedRow.ods, { allowedSet: VALID_ODS_NUMBERS });
      normalizedRow.ods = parsedOds.valid;
      if (parsedOds.invalid.length) {
        warnings.push(`Fila ${rowIndex + 2}: ODS omitidos por valor invalido (${parsedOds.invalid.join(', ')}).`);
      }
    }

    if (normalizedRow.objetivos_ods) {
      const parsedObjetivos = parseProjectNumberList(normalizedRow.objetivos_ods, { allowedSet: VALID_ODS_NUMBERS });
      normalizedRow.objetivos_ods = parsedObjetivos.valid;
      if (parsedObjetivos.invalid.length) {
        warnings.push(`Fila ${rowIndex + 2}: objetivos ODS omitidos por valor invalido (${parsedObjetivos.invalid.join(', ')}).`);
      }
    }

    if (normalizedRow.campos_cine || normalizedRow.campo_cine) {
      const rawCampos = normalizedRow.campos_cine || normalizedRow.campo_cine;
      const parsedCampos = parseStringListValue(rawCampos);
      const validCampos = [];
      const invalidCampos = [];
      parsedCampos.forEach((token) => {
        if (VALID_CINE_FIELD_IDS.has(String(token))) {
          validCampos.push(Number.parseInt(String(token), 10));
        } else {
          invalidCampos.push(token);
        }
      });
      normalizedRow.campos_cine = validCampos;
      if (invalidCampos.length) {
        warnings.push(`Fila ${rowIndex + 2}: campos CINE omitidos por valor invalido (${invalidCampos.join(', ')}). Use valores del 1 al 10.`);
      }
    }

    const investigatorsValue = normalizedRow.investigadores || normalizedRow.investigadores_ids;
    if (investigatorsValue) {
      const parsedInvestigadores = parseProjectInvestigadores(
        investigatorsValue,
        normalizedRow.investigadores_roles
      );
      normalizedRow.investigadores = parsedInvestigadores.valid;
      if (parsedInvestigadores.invalid.length) {
        warnings.push(`Fila ${rowIndex + 2}: investigadores omitidos por formato invalido (${parsedInvestigadores.invalid.join(', ')}).`);
      }
      if (parsedInvestigadores.roleCountMismatch) {
        warnings.push(`Fila ${rowIndex + 2}: la cantidad de INVESTIGADORES_ROLES no coincide con INVESTIGADORES_IDS.`);
      }
    }
    delete normalizedRow.investigadores_ids;
    delete normalizedRow.investigadores_roles;

    const odsValues = Array.isArray(normalizedRow.objetivos_ods) && normalizedRow.objetivos_ods.length
      ? normalizedRow.objetivos_ods
      : Array.isArray(normalizedRow.ods)
        ? normalizedRow.ods
        : [];
    investigatorsLinked += Array.isArray(normalizedRow.investigadores) ? normalizedRow.investigadores.length : 0;
    odsLinked += odsValues.length;

    parsedRows.push(normalizedRow);
  });

  return {
    rows: parsedRows,
    total: parsedRows.length,
    invalid,
    investigatorsLinked,
    odsLinked,
    warnings,
  };
}

function normalizeCongresoAlcance(value) {
  const normalized = normalizeCsvHeaderKey(value);
  if (normalized.includes('NACION')) return 'nacional';
  if (normalized.includes('INTERNACION')) return 'internacional';
  return '';
}

function summarizeCongresosToProduccion(rows) {
  const next = {
    p33_congresos_nacionales: {},
    p33_congresos_internacionales: {},
  };
  (rows || []).forEach((row) => {
    const year = String(row?.anio || '').trim();
    const campo = String(row?.campo_cine || '').trim();
    const alcance = normalizeCongresoAlcance(row?.alcance);
    if (!year || !campo || !VALID_CINE_FIELD_IDS.has(campo) || !alcance) return;
    const key = alcance === 'nacional' ? 'p33_congresos_nacionales' : 'p33_congresos_internacionales';
    next[key][campo] = {
      ...(next[key][campo] || {}),
      [year]: String((Number.parseInt(next[key][campo]?.[year] || '0', 10) || 0) + 1),
    };
  });
  return next;
}

function mergeYearCategoryMatrices(...matrices) {
  const merged = {};
  matrices.forEach((matrix) => {
    if (!matrix || typeof matrix !== 'object' || Array.isArray(matrix)) return;
    Object.entries(matrix).forEach(([categoryKey, yearMap]) => {
      if (!yearMap || typeof yearMap !== 'object' || Array.isArray(yearMap)) return;
      const currentCategory = merged[categoryKey] || {};
      Object.entries(yearMap).forEach(([yearKey, rawValue]) => {
        const nextValue = (Number.parseInt(currentCategory[yearKey] || '0', 10) || 0)
          + (Number.parseInt(rawValue || '0', 10) || 0);
        currentCategory[yearKey] = String(nextValue);
      });
      merged[categoryKey] = currentCategory;
    });
  });
  return merged;
}

function summarizeLibrosToProduccion(rows) {
  const next = { p34_libros: {} };
  (rows || []).forEach((row) => {
    const year = String(row?.anio || '').trim();
    const campo = String(row?.campo_cine || '').trim();
    if (!year || !campo || !VALID_CINE_FIELD_IDS.has(campo)) return;
    next.p34_libros[campo] = {
      ...(next.p34_libros[campo] || {}),
      [year]: String((Number.parseInt(next.p34_libros[campo]?.[year] || '0', 10) || 0) + 1),
    };
  });
  return next;
}

function parseCongresosCsvText(text) {
  const rows = parseDelimitedText(text, ';');
  if (!rows.length) {
    return { rows: [], total: 0, invalid: 0, nacionales: 0, internacionales: 0, warnings: [] };
  }

  const [headerRow, ...dataRows] = rows;
  const headers = headerRow.map((header) => normalizeCsvHeaderKey(header));
  const recognizedColumns = headers.filter((header) => PRODUCCION_CONGRESO_CSV_COLUMN_MAP[header]);
  if (!recognizedColumns.length) {
    throw new Error('La plantilla de congresos no tiene columnas reconocibles. Descargue la plantilla oficial e inténtelo de nuevo.');
  }

  const parsedRows = [];
  let invalid = 0;
  let nacionales = 0;
  let internacionales = 0;
  const warnings = [];

  dataRows.forEach((row, rowIndex) => {
    const source = {};
    headers.forEach((header, columnIndex) => {
      const internalKey = PRODUCCION_CONGRESO_CSV_COLUMN_MAP[header];
      if (!internalKey) return;
      const rawValue = row[columnIndex] ?? '';
      const cleanedValue = sanitizeFreeTextPreserveSpacing(rawValue).trim();
      if (cleanedValue) {
        source[internalKey] = cleanedValue;
      }
    });

    if (!Object.keys(source).length) return;
    const alcance = normalizeCongresoAlcance(source.alcance);
    const campo = String(source.campo_cine || '').trim();
    const anio = sanitizeIntegerInput(source.anio, { max: YEAR_MAX, maxDigits: 4 });
    if (!anio || !campo || !VALID_CINE_FIELD_IDS.has(campo) || !alcance || !String(source.nombre_congreso_o_evento || '').trim()) {
      invalid += 1;
      return;
    }

    const docentes = sanitizeIntegerInput(source.docentes_asistentes || '');
    if ((source.docentes_asistentes || '') && !docentes) {
      warnings.push(`Fila ${rowIndex + 2}: DOCENTES_ASISTENTES no es válido y se guardó vacío.`);
    }

    if (alcance === 'nacional') nacionales += 1;
    else internacionales += 1;

    parsedRows.push({
      anio,
      campo_cine: campo,
      nombre_congreso_o_evento: String(source.nombre_congreso_o_evento || '').trim(),
      alcance,
      docentes_asistentes: docentes,
      observaciones: String(source.observaciones || '').trim(),
      orden_importacion: rowIndex + 1,
    });
  });

  return { rows: parsedRows, total: parsedRows.length, invalid, nacionales, internacionales, warnings };
}

function parseLibrosCsvText(text) {
  const rows = parseDelimitedText(text, ';');
  if (!rows.length) {
    return { rows: [], total: 0, invalid: 0, warnings: [] };
  }

  const [headerRow, ...dataRows] = rows;
  const headers = headerRow.map((header) => normalizeCsvHeaderKey(header));
  const recognizedColumns = headers.filter((header) => PRODUCCION_LIBRO_CSV_COLUMN_MAP[header]);
  if (!recognizedColumns.length) {
    throw new Error('La plantilla de libros no tiene columnas reconocibles. Descargue la plantilla oficial e inténtelo de nuevo.');
  }

  const parsedRows = [];
  let invalid = 0;
  const warnings = [];

  dataRows.forEach((row, rowIndex) => {
    const source = {};
    headers.forEach((header, columnIndex) => {
      const internalKey = PRODUCCION_LIBRO_CSV_COLUMN_MAP[header];
      if (!internalKey) return;
      const rawValue = row[columnIndex] ?? '';
      const cleanedValue = sanitizeFreeTextPreserveSpacing(rawValue).trim();
      if (cleanedValue) {
        source[internalKey] = cleanedValue;
      }
    });

    if (!Object.keys(source).length) return;
    const campo = String(source.campo_cine || '').trim();
    const anio = sanitizeIntegerInput(source.anio, { max: YEAR_MAX, maxDigits: 4 });
    if (!anio || !campo || !VALID_CINE_FIELD_IDS.has(campo) || !String(source.titulo_obra || '').trim() || !String(source.tipo_obra || '').trim()) {
      invalid += 1;
      return;
    }

    parsedRows.push({
      anio,
      campo_cine: campo,
      titulo_obra: String(source.titulo_obra || '').trim(),
      tipo_obra: String(source.tipo_obra || '').trim(),
      autores: String(source.autores || '').trim(),
      editorial: String(source.editorial || '').trim(),
      observaciones: String(source.observaciones || '').trim(),
      orden_importacion: rowIndex + 1,
    });
  });

  return { rows: parsedRows, total: parsedRows.length, invalid, warnings };
}

function blockInvalidNumberKeys(event) {
  if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
    event.preventDefault();
  }
}

function sanitizeIntegerInput(value, { max = MAX_FORM_NUMBER, maxDigits = String(max).length } = {}) {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, maxDigits);
  if (!digits) return '';
  const parsed = Number.parseInt(digits, 10);
  if (Number.isNaN(parsed)) return '';
  return String(Math.min(max, parsed));
}

function sanitizePhoneInput(value) {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, MAX_PHONE_LENGTH);
  if (!digits) return '';
  if (digits[0] !== '0') return '';
  if (digits.length === 1) return '0';
  if (digits[1] !== '9') return '0';
  return digits;
}

function isValidEcuadorMobilePhone(value) {
  return PHONE_REGEX.test(String(value || '').trim());
}

function isValidNumericValue(value, { max = MAX_FORM_NUMBER, min = 0 } = {}) {
  if (value === '' || value == null) return true;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= min && value <= max;
  }
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return false;
  const parsed = Number.parseInt(text, 10);
  return !Number.isNaN(parsed) && parsed >= min && parsed <= max;
}

function hasInvalidNumericCollection(node, opts = {}) {
  if (node == null || node === '') return false;
  if (Array.isArray(node)) {
    return node.some((item) => hasInvalidNumericCollection(item, opts));
  }
  if (typeof node === 'object') {
    return Object.values(node).some((item) => hasInvalidNumericCollection(item, opts));
  }
  return !isValidNumericValue(node, opts);
}

function hasInvalidNumericDraftMatrix(node, opts = {}) {
  if (node == null || node === '') return false;
  if (Array.isArray(node)) {
    return node.some((item) => hasInvalidNumericDraftMatrix(item, opts));
  }
  if (typeof node === 'object') {
    return Object.values(node).some((item) => hasInvalidNumericDraftMatrix(item, opts));
  }
  if (typeof node === 'string') {
    const text = node.trim();
    if (!text) return false;
    if (text.startsWith('{') || text.startsWith('[')) {
      try {
        return hasInvalidNumericDraftMatrix(JSON.parse(text), opts);
      } catch {
        return false;
      }
    }
    if (!/^\d+$/.test(text)) return false;
  }
  if (typeof node !== 'string' && typeof node !== 'number') return false;
  return !isValidNumericValue(node, opts);
}

function numericInputProps(max = MAX_FORM_NUMBER) {
  return {
    inputMode: 'numeric',
    pattern: '[0-9]*',
    maxLength: String(max).length,
    onKeyDown: blockInvalidNumberKeys,
  };
}

function getP52OtherNames(values = []) {
  return values
    .filter((value) => String(value).startsWith(P52_OTHER_PREFIX))
    .map((value) => String(value).slice(P52_OTHER_PREFIX.length));
}

function getP52BaseValues(values = []) {
  return values.filter(
    (value) =>
      value !== P52_OTHER_LABEL &&
      !String(value).startsWith(P52_OTHER_PREFIX) &&
      value !== P52_NO_APLICA_LABEL
  );
}

function buildEmptyP64Payload() {
  return {
    valores: Object.fromEntries(VINCULACION_RESULTADOS_OPCIONES.map((option) => [option.key, ''])),
    otros_aplica: '',
    otros: '',
    ninguno: false,
  };
}

function parseSiNoLikert(value) {
  if (!value) return { aplica: '', nivel: '' };
  try {
    const parsed = JSON.parse(value);
    const aplica = parsed?.aplica === 'si' ? 'si' : parsed?.aplica === 'no' ? 'no' : '';
    const nivel = aplica === 'si' && ['1', '2', '3', '4', '5'].includes(String(parsed?.nivel || ''))
      ? String(parsed.nivel)
      : '';
    return { aplica, nivel };
  } catch {
    return { aplica: '', nivel: '' };
  }
}

function stringifySiNoLikert(payload) {
  if (payload?.aplica === 'no') return JSON.stringify({ aplica: 'no' });
  return JSON.stringify({ aplica: 'si', nivel: payload?.nivel || '1' });
}

function parsePlanVt(value) {
  if (!value) return { aplica: '', alcance: '' };
  try {
    const parsed = JSON.parse(value);
    const aplica = parsed?.aplica === 'si' ? 'si' : parsed?.aplica === 'no' ? 'no' : '';
    const alcance = aplica === 'si' && ['institucional', 'departamentos'].includes(String(parsed?.alcance || ''))
      ? String(parsed.alcance)
      : '';
    return { aplica, alcance };
  } catch {
    return { aplica: '', alcance: '' };
  }
}

function stringifyPlanVt(payload) {
  if (payload?.aplica === 'no') return JSON.stringify({ aplica: 'no' });
  return JSON.stringify({ aplica: 'si', alcance: payload?.alcance || 'institucional' });
}

function parseP64Payload(value) {
  const empty = buildEmptyP64Payload();
  if (!value) return empty;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    const rawValues = parsed?.valores && typeof parsed.valores === 'object' && !Array.isArray(parsed.valores)
      ? parsed.valores
      : {};
    const legacyValues = {};
    VINCULACION_OPCIONES.forEach((label, index) => {
      const key = VINCULACION_RESULTADOS_OPCIONES[index]?.key;
      if (key && rawValues[label] && !rawValues[key]) legacyValues[key] = rawValues[label];
    });
    const rawOtrosAplica = parsed?.otros_aplica === 'si' || parsed?.otros_aplica === 'no'
      ? parsed.otros_aplica
      : String(parsed?.otros || '').trim() || String(rawValues.otros || '').trim()
        ? 'si'
        : 'no';
    return {
      valores: Object.fromEntries(
        VINCULACION_RESULTADOS_OPCIONES.map((option) => {
          const raw = String(rawValues[option.key] ?? legacyValues[option.key] ?? '');
          const safe = ['1', '2', '3', '4', '5'].includes(raw) ? raw : '';
          return [option.key, parsed?.ninguno || (option.key === 'otros' && rawOtrosAplica !== 'si') ? '' : safe];
        })
      ),
      otros_aplica: parsed?.ninguno ? 'no' : rawOtrosAplica,
      otros: parsed?.ninguno ? '' : String(parsed?.otros || ''),
      ninguno: !!parsed?.ninguno,
    };
  } catch {
    return empty;
  }
}

function stringifyP64Payload(payload) {
  return JSON.stringify({
    valores: {
      ...buildEmptyP64Payload().valores,
      ...(payload?.valores || {}),
      otros: payload?.otros_aplica === 'si' ? payload?.valores?.otros || '' : '',
    },
    otros_aplica: payload?.otros_aplica || '',
    otros: payload?.otros_aplica === 'si' ? sanitizeFreeTextPreserveSpacing(payload?.otros || '') : '',
    ninguno: !!payload?.ninguno,
  });
}

function fillEmptyProjectSectionWithZeros(payload, anio) {
  const years = matrixYearKeys(anio);
  const next = {
    ...payload,
    num_estudiantes_pregrado_inv_hombres:
      payload?.num_estudiantes_pregrado_inv_hombres === '' || payload?.num_estudiantes_pregrado_inv_hombres == null
        ? '0'
        : payload.num_estudiantes_pregrado_inv_hombres,
    num_estudiantes_pregrado_inv_mujeres:
      payload?.num_estudiantes_pregrado_inv_mujeres === '' || payload?.num_estudiantes_pregrado_inv_mujeres == null
        ? '0'
        : payload.num_estudiantes_pregrado_inv_mujeres,
    num_estudiantes_posgrado_inv_hombres:
      payload?.num_estudiantes_posgrado_inv_hombres === '' || payload?.num_estudiantes_posgrado_inv_hombres == null
        ? '0'
        : payload.num_estudiantes_posgrado_inv_hombres,
    num_estudiantes_posgrado_inv_mujeres:
      payload?.num_estudiantes_posgrado_inv_mujeres === '' || payload?.num_estudiantes_posgrado_inv_mujeres == null
        ? '0'
        : payload.num_estudiantes_posgrado_inv_mujeres,
  };

  const matrices = ensureMatricesShape(next?.formulario_extra?.matrices_5_anios, anio);
  const cineFieldIds = Array.from({ length: 10 }, (_, i) => String(i + 1));
  const hmKeys = ['docentes_tc_titulares_inv', 'docentes_tc_no_titulares_inv'];
  const cineKeys = [
    'proyectos_interno_cine',
    'proyectos_sin_fin_cine',
    'proyectos_externo_cine',
    'proyectos_redes_cine',
  ];

  hmKeys.forEach((key) => {
    years.forEach((year) => {
      const current = matrices?.[key]?.[year] || {};
      matrices[key][year] = {
        h: current.h === '' || current.h == null ? '0' : String(current.h),
        m: current.m === '' || current.m == null ? '0' : String(current.m),
      };
    });
  });

  cineKeys.forEach((key) => {
    years.forEach((year) => {
      const current = matrices?.[key]?.[year] || {};
      matrices[key][year] = Object.fromEntries(
        cineFieldIds.map((fieldId) => {
          const raw = current[fieldId];
          return [fieldId, raw === '' || raw == null ? '0' : String(raw)];
        })
      );
    });
  });

  next.formulario_extra = {
    ...(next.formulario_extra || {}),
    matrices_5_anios: matrices,
  };

  return next;
}

function normalizeExtraForSigc(extra) {
  const next = mergeExtra(emptyExtra(), extra || {});
  if (
    next.produccion?.p33_congresos
    && !Object.keys(next.produccion?.p33_congresos_nacionales || {}).length
    && !Object.keys(next.produccion?.p33_congresos_internacionales || {}).length
  ) {
    next.produccion.p33_congresos_internacionales = next.produccion.p33_congresos;
  }
  next.produccion = {
    ...(next.produccion || {}),
    p33_congresos: mergeYearCategoryMatrices(
      next.produccion?.p33_congresos_nacionales,
      next.produccion?.p33_congresos_internacionales,
      !Object.keys(next.produccion?.p33_congresos_nacionales || {}).length
        && !Object.keys(next.produccion?.p33_congresos_internacionales || {}).length
        ? next.produccion?.p33_congresos
        : null
    ),
  };
  if (!next.p55_importancia_vinculacion_pnd && next.pnd_integracion_2025 != null) {
    next.p55_importancia_vinculacion_pnd = String(next.pnd_integracion_2025);
  }
  if (!next.p56_grado_alineacion_pnd && next.pnd_integracion_anterior != null) {
    next.p56_grado_alineacion_pnd = String(next.pnd_integracion_anterior);
  }
  if ((!next.p57_objetivos_pnd || next.p57_objetivos_pnd.length === 0) && Array.isArray(next.pnd_objetivos_seleccion)) {
    next.p57_objetivos_pnd = [...next.pnd_objetivos_seleccion];
  }
  if (!next.p58_nivel_participacion_consejos && next.politicas_publicas_uso != null) {
    next.p58_nivel_participacion_consejos = String(next.politicas_publicas_uso);
  }
  if ((!next.p63_objetivos_ods || next.p63_objetivos_ods.length === 0) && Array.isArray(next.ods_priorizados)) {
    next.p63_objetivos_ods = next.ods_priorizados.map((v) => String(v));
  }
  if (next.p28_aplica === null || next.p28_aplica === undefined) {
    next.p28_aplica = false;
  }
  if (!['si', 'no'].includes(String(next.p28_tiene_desglose || ''))) {
    next.p28_tiene_desglose = '';
  }
  if (next.p29_aplica === null || next.p29_aplica === undefined) {
    next.p29_aplica = false;
  }
  if (!['si', 'no'].includes(String(next.p29_tiene_desglose || ''))) {
    next.p29_tiene_desglose = '';
  }
  if (next.p21_aplica === null || next.p21_aplica === undefined) {
    next.p21_aplica = false;
  }
  if (next.p22_aplica === null || next.p22_aplica === undefined) {
    next.p22_aplica = false;
  }
  return next;
}

function toStringArray(value) {
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item)).filter(Boolean);
    } catch { }
    return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function normalizeGrupoDetalleRow(row) {
  const source = row && typeof row === 'object' ? row : {};
  return {
    nombre: source.nombre || source.grupo || '',
    campos_cine: toStringArray(source.campos_cine || source.campo_cine || source.campo),
    lineas: toStringArray(source.lineas),
    hombres: source.hombres ?? '',
    mujeres: source.mujeres ?? '',
    antiguedad_anios: source.antiguedad_anios ?? source.antiguedad ?? '',
    produccion_cientifica: source.produccion_cientifica ?? '',
  };
}

function normalizeCentroDetalleRow(row) {
  const source = row && typeof row === 'object' ? row : {};
  return {
    nombre: source.nombre || source.centro || '',
    tipo: source.tipo || '',
    campos_cine: toStringArray(source.campos_cine || source.campo_cine || source.campo),
    lineas: toStringArray(source.lineas),
    hombres: source.hombres ?? '',
    mujeres: source.mujeres ?? '',
    personal_apoyo: source.personal_apoyo ?? '',
    fondos_internos_usd: source.fondos_internos_usd ?? '',
    fondos_externos_usd: source.fondos_externos_usd ?? source.fondos ?? '',
  };
}

/* ─── UI primitives ─────────────────────────────────────────────────────── */

function PBadge({ code, display = null }) {
  const rawShown = String(display || code);
  const shown = rawShown.startsWith('P') ? rawShown : `P${rawShown}`;
  const title = display && display !== code ? `Pregunta ${shown} · Código SIGC ${code}` : `Código SIGC ${code}`;
  return (
    <span
      title={title}
      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-[#2D5BA6] dark:text-blue-400 font-bold text-[10px] flex-shrink-0 leading-none"
    >
      {shown}
    </span>
  );
}

function SmallPBadge({ code, display = null, className = '' }) {
  const rawShown = String(display || code);
  const shown = rawShown.startsWith('P') ? rawShown : `P${rawShown}`;
  const title = display && display !== code ? `Pregunta ${shown} · Código SIGC ${code}` : `Código SIGC ${code}`;
  return (
    <span title={title} className={`${smallBadgeCls} ${className}`.trim()}>
      {shown}
    </span>
  );
}

function SCard({ children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

const inputCls =
  'w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition-colors focus:border-[#2D5BA6] focus:ring-1 focus:ring-blue-100 dark:focus:ring-blue-900/30 bg-white dark:bg-slate-700';
const inputDisabledCls =
  'w-full px-3 py-2.5 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-500 dark:text-slate-400 cursor-default outline-none';
const textareaCls =
  'w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition-colors focus:border-[#2D5BA6] focus:ring-1 focus:ring-blue-100 dark:focus:ring-blue-900/30 bg-white dark:bg-slate-700 resize-none';
const actionLinkBaseCls =
  'inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-colors';
const actionLinkSecondaryCls = `${actionLinkBaseCls} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700`;
const actionLinkPrimaryCls = `${actionLinkBaseCls} bg-[#2D5BA6] text-white hover:bg-[#244d8f]`;

/* ─── VT field ──────────────────────────────────────────────────────────── */

function VtQuestionField({ pregunta, value, onChange }) {
  const code = pregunta.codigo;
  const tipo = pregunta.tipo_respuesta;

  if (code === 'VT01') {
    return (
      <div className="space-y-2">
        {VT01_OPTIONS.map((o) => {
          const sel = Number(value) === o.v;
          return (
            <label
              key={o.v}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all select-none ${sel ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
            >
              <input type="radio" name={code} className="sr-only" checked={sel} onChange={() => onChange(o.v)} />
              <span className={`w-4 h-4 rounded-full flex-shrink-0 border-2 flex items-center justify-center transition-colors ${sel ? 'border-[#2D5BA6]' : 'border-slate-300 dark:border-slate-500'}`}>
                {sel && <span className="w-2 h-2 rounded-full bg-[#2D5BA6]" />}
              </span>
              <span className={`text-sm font-medium ${sel ? 'text-[#2D5BA6] dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}>{o.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (tipo === 'LIKERT_1_5') {
    const madurezLabels = ['', 'Inicial / Inexistente', 'En desarrollo', 'Definido', 'Gestionado', 'Optimizado'];
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <label
            key={n}
            className={`flex-1 flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-sm font-medium cursor-pointer transition-all ${Number(value) === n ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20 text-[#2D5BA6] dark:text-blue-400' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-600 dark:text-slate-400'
              }`}
          >
            <input type="radio" className="sr-only" name={code} checked={Number(value) === n} onChange={() => onChange(n)} />
            <span className="text-xl font-bold leading-none">{n}</span>
            <span className="text-[10px] leading-tight text-center opacity-75">{madurezLabels[n]}</span>
          </label>
        ))}
      </div>
    );
  }

  if (tipo === 'TEXTO_LIBRE') {
    return (
      <textarea
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className={textareaCls}
        placeholder="Describa según su institución…"
      />
    );
  }

  if (tipo === 'JSON_MULTI') {
    const opts =
      pregunta.opciones_json && Array.isArray(pregunta.opciones_json)
        ? pregunta.opciones_json
        : ['boletines', 'informes', 'alertas', 'estados_del_arte', 'otros'];
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {opts.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label
              key={opt}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all select-none ${checked ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) onChange([...selected, opt]);
                  else onChange(selected.filter((x) => x !== opt));
                }}
              />
              <span className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${checked ? 'bg-[#2D5BA6] border-[#2D5BA6]' : 'border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700'}`}>
                {checked && (
                  <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2.5">
                    <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (tipo === 'SI_NO') {
    return (
      <div className="flex gap-3">
        {[{ v: true, l: 'Sí' }, { v: false, l: 'No' }].map(({ v, l }) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(v)}
            className={`px-5 py-2 rounded-lg text-sm font-medium border transition-all ${value === v ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
              }`}
          >
            {l}
          </button>
        ))}
      </div>
    );
  }

  return (
    <input
      type="text"
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    />
  );
}

/* ─── DynamicTableField ─────────────────────────────────────────────────── */

const smallBadgeCls = 'inline-flex items-center justify-center w-6 h-6 rounded bg-blue-100 dark:bg-blue-900/30 text-[#2D5BA6] dark:text-blue-400 font-bold text-[10px] flex-shrink-0';

function DynamicTableField({ code, label, subtitle, rowCount, columns, value, onChange, itemLabel = 'Fila' }) {
  const rows = useMemo(() => {
    if (rowCount <= 0) return [];
    try {
      const parsed = value ? JSON.parse(value) : [];
      const arr = Array.isArray(parsed) ? parsed : [];
      const out = arr.map((r) => (typeof r === 'string' ? { nombre: r } : { ...r }));
      while (out.length < rowCount) out.push({});
      return out.slice(0, rowCount);
    } catch {
      return Array.from({ length: rowCount }, () => ({}));
    }
  }, [value, rowCount]);

  const update = (rowIdx, colKey, val) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [colKey]: val } : r));
    onChange(JSON.stringify(next));
  };

  if (rowCount <= 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 flex items-center gap-2">
        <span className={smallBadgeCls}>{code}</span>
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</p>
          {subtitle && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700">
              <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase w-8">#</th>
              {columns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b border-slate-50 dark:border-slate-700/50 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors">
                <td className="px-3 py-2 text-xs font-mono text-slate-400 dark:text-slate-500">{idx + 1}</td>
                {columns.map((col) => (
                  <td key={col.key} className="px-2 py-1.5">
                    <input
                      type={col.inputType || 'text'}
                      min={col.inputType === 'number' ? (col.min ?? 0) : undefined}
                      value={row[col.key] ?? ''}
                      onChange={(e) =>
                        update(
                          idx,
                          col.key,
                          col.inputType === 'number'
                            ? sanitizeIntegerInput(e.target.value, { max: col.max ?? MAX_FORM_NUMBER })
                            : e.target.value
                        )
                      }
                      placeholder={col.placeholder || ''}
                      {...(col.inputType === 'number' ? numericInputProps(col.max ?? MAX_FORM_NUMBER) : {})}
                      className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-xs outline-none focus:border-[#2D5BA6] focus:ring-1 focus:ring-blue-100 dark:focus:ring-blue-900/30 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── YearCategoryGrid ──────────────────────────────────────────────────── */

function YearCategoryGrid({ code, label, years, categories, value, onChange }) {
  const rows = useMemo(() => {
    try { return value ? JSON.parse(value) : {}; }
    catch { return {}; }
  }, [value]);

  const update = (catKey, year, val) => {
    const next = {
      ...rows,
      [catKey]: { ...(rows[catKey] || {}), [year]: sanitizeIntegerInput(val) },
    };
    onChange(JSON.stringify(next));
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 flex items-center gap-2">
        {code ? <span className={smallBadgeCls}>{code}</span> : null}
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase min-w-[160px]">Tipo</th>
              {years.map((y) => (
                <th key={y} className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase w-16 border-l border-slate-100 dark:border-slate-700">{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.key} className="border-b border-slate-50 dark:border-slate-700/50 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-700/30">
                <td className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 font-medium">{cat.label}</td>
                {years.map((y) => (
                  <td key={y} className="px-2 py-1.5 border-l border-slate-50 dark:border-slate-700/50">
                    <input
                      type="number" min={0}
                      value={rows[cat.key]?.[y] ?? ''}
                      onChange={(e) => update(cat.key, y, e.target.value)}
                      {...numericInputProps()}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-xs outline-none focus:border-[#2D5BA6] dark:focus:ring-blue-900/30 text-center"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── YearNumGrid ───────────────────────────────────────────────────────── */

function YearNumGrid({ code, label, hint, years, value = {}, onChange, suffix = '', max }) {
  const update = (year, val) => onChange({ ...value, [year]: sanitizeIntegerInput(val, { max: max ?? MAX_FORM_NUMBER }) });

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 flex items-center gap-2">
        <span className={smallBadgeCls}>{code}</span>
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</p>
          {hint && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
              {years.map((y) => (
                <th key={y} className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase border-r last:border-r-0 border-slate-100 dark:border-slate-700">
                  {y}{suffix && <span className="ml-0.5 font-normal text-slate-400 dark:text-slate-500">{suffix}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {years.map((y) => (
                <td key={y} className="p-2 border-r last:border-r-0 border-slate-50 dark:border-slate-700/50">
                  <input
                    type="number"
                    min={0}
                    max={max}
                    value={value[y] ?? ''}
                    onChange={(e) => update(y, e.target.value)}
                    {...numericInputProps(max ?? MAX_FORM_NUMBER)}
                    className="w-full px-2 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-xs outline-none focus:border-[#2D5BA6] dark:focus:ring-blue-900/30 text-center"
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────── */

function AiFillSteps({ current, loading, resumen, advertencias }) {
  const items = [
    { title: 'Recopilar publicaciones', description: 'Filtrar por institución y rango de años' },
    { title: 'Aplicar heurística', description: 'Clasificar topics en campos CINE' },
    { title: 'Aplicar resultados', description: 'Llenar P30-P38 e indicadores' },
  ];
  return (
    <div className="rounded-2xl border border-blue-100 dark:border-blue-900/50 bg-blue-50/60 dark:bg-blue-950/20 p-4">
      <div className="grid gap-3 md:grid-cols-3">
        {items.map((item, index) => {
          const done = current > index || (!loading && resumen && current >= index);
          const active = current === index && loading;
          return (
            <div
              key={item.title}
              className={`rounded-xl border p-3 transition-colors ${active
                  ? 'border-[#2D5BA6] bg-white dark:bg-slate-800 shadow-sm'
                  : done
                    ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20'
                    : 'border-slate-200 bg-white/70 dark:border-slate-700 dark:bg-slate-800/70'
                }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done
                    ? 'bg-emerald-500 text-white'
                    : active
                      ? 'bg-[#2D5BA6] text-white animate-pulse'
                      : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                  {done ? '✓' : index + 1}
                </span>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{item.title}</p>
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
            </div>
          );
        })}
      </div>
      {resumen && (
        <p className="mt-3 text-xs text-blue-800 dark:text-blue-200">
          Se analizaron <strong>{resumen.publicaciones_analizadas ?? 0}</strong> publicaciones del rango <strong>{resumen.rango}</strong>.
        </p>
      )}
      {Array.isArray(advertencias) && advertencias.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          {advertencias.map((warning) => (
            <p key={warning}>• {warning}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function buildDraftPayloadFromParts({
  anio,
  nums,
  tienePersonalInvestigacion,
  tienePersonalApoyo,
  redesList,
  rankingsSeleccionados,
  rankingsOtrasN,
  rankingsOtras,
  acreditacionesSeleccionadas,
  acreditacionesOtrasN,
  acreditacionesOtras,
  extra,
  vtResp,
}) {
  const numPayload = {};
  Object.entries(nums || {}).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    const n = parseInt(String(v), 10);
    if (!Number.isNaN(n)) numPayload[k] = n;
  });
  const normalizedExtra = normalizeExtraForSigc(extra);
  normalizedExtra.responsable_nombre = sanitizeInlineText(normalizedExtra.responsable_nombre || '');
  normalizedExtra.responsable_cargo = sanitizeInlineText(normalizedExtra.responsable_cargo || '');
  normalizedExtra.responsable_correo = String(normalizedExtra.responsable_correo || '').trim();
  normalizedExtra.responsable_telefono = sanitizePhoneInput(normalizedExtra.responsable_telefono || '');
  normalizedExtra.pnd_integracion_2025 = normalizedExtra.p55_importancia_vinculacion_pnd || normalizedExtra.pnd_integracion_2025;
  normalizedExtra.pnd_integracion_anterior = normalizedExtra.p56_grado_alineacion_pnd || normalizedExtra.pnd_integracion_anterior;
  normalizedExtra.pnd_objetivos_seleccion = normalizedExtra.p57_objetivos_pnd || normalizedExtra.pnd_objetivos_seleccion;
  normalizedExtra.politicas_publicas_uso = normalizedExtra.p58_nivel_participacion_consejos || normalizedExtra.politicas_publicas_uso;
  normalizedExtra.ods_priorizados = (normalizedExtra.p63_objetivos_ods || []).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
  normalizedExtra.ods_seguimiento_nivel = normalizedExtra.p62_medicion_impacto_ods || normalizedExtra.ods_seguimiento_nivel;
  return {
    anio: Number(anio),
    estado: 'BORRADOR',
    ...numPayload,
    redes_academicas: (redesList || []).filter(Boolean),
    rankings: [...(rankingsSeleccionados || []), ...ensureListLen(rankingsOtras || [], rankingsOtrasN || 0).filter(Boolean)],
    acreditaciones: [...(acreditacionesSeleccionadas || []), ...ensureListLen(acreditacionesOtras || [], acreditacionesOtrasN || 0).filter(Boolean)],
    formulario_extra: {
      ...normalizedExtra,
      tiene_personal_investigacion: tienePersonalInvestigacion,
      tiene_personal_apoyo: tienePersonalApoyo,
    },
    vigilancia_respuestas: vtResp || {},
  };
}

function buildDraftComparableSignature(payload) {
  if (!payload) return '';
  const {
    paso_actual,
    estado,
    ...rest
  } = payload;
  return JSON.stringify(rest);
}

export default function FormularioIES() {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const canEdit = user && ROLES_CRUD.includes(user.rol);
  const isSuperAdmin = user?.rol === 'SuperAdmin' || user?.role === 'SuperAdmin';

  const [universidades, setUniversidades] = useState([]);
  const [universidadId, setUniversidadId] = useState('');
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [estado, setEstado] = useState(null);
  const [formulariosRegistrados, setFormulariosRegistrados] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [newFormUniversidadId, setNewFormUniversidadId] = useState('');
  const [newFormYear, setNewFormYear] = useState(new Date().getFullYear());
  const [newFormMode, setNewFormMode] = useState('BLANCO');
  const [copySourceYear, setCopySourceYear] = useState('');
  const [step, setStep] = useState(0);
  const [institutionAccess, setInstitutionAccess] = useState({ loading: true, allAccess: true, universidad: null, detail: '' });
  const [aiFill, setAiFill] = useState({ loading: false, current: -1, resumen: null, advertencias: [] });
  const [draftStatus, setDraftStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSavedSignature, setLastSavedSignature] = useState('');
  const [projectImportSummary, setProjectImportSummary] = useState({ total: 0, invalid: 0, investigatorsLinked: 0, odsLinked: 0, warnings: [] });
  const [congresosImportSummary, setCongresosImportSummary] = useState({ total: 0, invalid: 0, nacionales: 0, internacionales: 0, warnings: [] });
  const [librosImportSummary, setLibrosImportSummary] = useState({ total: 0, invalid: 0, warnings: [] });
  const isUiLocked = aiFill.loading;
  const hydratingRef = useRef(false);
  const proyectosFileInputRef = useRef(null);
  const congresosFileInputRef = useRef(null);
  const librosFileInputRef = useRef(null);

  const [nums, setNums] = useState({});
  const [tienePersonalInvestigacion, setTienePersonalInvestigacion] = useState(null);
  const [tienePersonalApoyo, setTienePersonalApoyo] = useState(null);
  const [numRedes, setNumRedes] = useState(0);
  const [redesList, setRedesList] = useState([]);
  const [rankingsSeleccionados, setRankingsSeleccionados] = useState([]);
  const [rankingsOtrasN, setRankingsOtrasN] = useState(0);
  const [rankingsOtras, setRankingsOtras] = useState([]);
  const [acreditacionesSeleccionadas, setAcreditacionesSeleccionadas] = useState([]);
  const [acreditacionesOtrasN, setAcreditacionesOtrasN] = useState(0);
  const [acreditacionesOtras, setAcreditacionesOtras] = useState([]);
  const [extra, setExtra] = useState(emptyExtra());
  const [vtResp, setVtResp] = useState({});
  const [cineCampos, setCineCampos] = useState([]);
  const [vtPreguntas, setVtPreguntas] = useState([]);
  const [pndCatalog, setPndCatalog] = useState({
    periodo_actual: '2025-2029',
    periodo_previo: '2024-2025',
    objetivos: [],
    warning: null,
  });

  const anioNum = Number(anio);
  const matrixYears = useMemo(() => matrixYearKeys(anioNum), [anioNum]);
  const referenceYear = useMemo(() => [String(anioNum || new Date().getFullYear())], [anioNum]);
  const matricesView = useMemo(() => ensureMatricesShape(extra.matrices_5_anios, anioNum), [extra.matrices_5_anios, anioNum]);
  const cineCategories = useMemo(() => {
    const fields = (cineCampos.length
      ? cineCampos.filter((c) => Number(c.campo_id) !== 0)
      : Array.from({ length: 10 }, (_, i) => ({ campo_id: i + 1, campo_amplio: `Campo ${i + 1}` })));
    return fields.map((c) => ({ key: String(c.campo_id), label: `${c.campo_id} · ${c.campo_amplio}` }));
  }, [cineCampos]);
  const universidadesDisponibles = useMemo(() => {
    if (!institutionAccess.allAccess && institutionAccess.universidad) {
      return [institutionAccess.universidad];
    }
    return universidades;
  }, [institutionAccess, universidades]);
  const formulariosRegistradosByUniversidad = useMemo(() => {
    return formulariosRegistrados.reduce((acc, item) => {
      const key = String(item.universidad_id);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [formulariosRegistrados]);
  const formulariosEnviados = useMemo(
    () => formulariosRegistrados.filter((item) => item.estado === 'ENVIADO'),
    [formulariosRegistrados]
  );
  const formulariosBorrador = useMemo(
    () => formulariosRegistrados.filter((item) => item.estado !== 'ENVIADO'),
    [formulariosRegistrados]
  );

  const patchMatrixHm = useCallback(
    (rowKey, year, field, value) => {
      setExtra((ex) => {
        const mat = { ...ensureMatricesShape(ex.matrices_5_anios, anioNum) };
        const row = { ...mat[rowKey] };
        row[year] = {
          ...(row[year] || { h: '', m: '' }),
          [field]: sanitizeIntegerInput(value),
        };
        mat[rowKey] = row;
        return { ...ex, matrices_5_anios: mat };
      });
    },
    [anioNum]
  );

  const patchMatrixCine = useCallback(
    (matrixKey, year, campoId, value) => {
      setExtra((ex) => {
        const mat = { ...ensureMatricesShape(ex.matrices_5_anios, anioNum) };
        const row = { ...mat[matrixKey] };
        const yr = { ...(row[year] || {}) };
        yr[String(campoId)] = sanitizeIntegerInput(value);
        row[year] = yr;
        mat[matrixKey] = row;
        return { ...ex, matrices_5_anios: mat };
      });
    },
    [anioNum]
  );

  const loadUniversidades = useCallback(async () => {
    try {
      const [res, accessRes] = await Promise.all([
        api.get('/api/universities'),
        canEdit ? api.get('/api/ies-formulario/mi-universidad') : Promise.resolve({ data: { all_access: true } }),
      ]);
      const list = Array.isArray(res.data) ? res.data : [];
      const access = accessRes.data || { all_access: true };
      setUniversidades(list);
      setInstitutionAccess({
        loading: false,
        allAccess: Boolean(access.all_access),
        universidad: access.universidad || null,
        detail: access.detail || '',
      });
      if (!access.all_access && access.universidad?.id) {
        setUniversidadId(String(access.universidad.id));
      } else if (!universidadId && list.length === 1) {
        setUniversidadId(String(list[0].id));
      }
    } catch (e) {
      console.error(e);
      setInstitutionAccess({ loading: false, allAccess: false, universidad: null, detail: 'No se pudo validar la institución autorizada.' });
    }
  }, [canEdit, universidadId]);

  useEffect(() => {
    loadUniversidades();
  }, [loadUniversidades]);

  useEffect(() => {
    if (!newFormUniversidadId) {
      if (!institutionAccess.allAccess && institutionAccess.universidad?.id) {
        setNewFormUniversidadId(String(institutionAccess.universidad.id));
      } else if (universidadesDisponibles.length === 1) {
        setNewFormUniversidadId(String(universidadesDisponibles[0].id));
      }
    }
  }, [institutionAccess, newFormUniversidadId, universidadesDisponibles]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const [cineRes, vtRes] = await Promise.all([
          api.get('/api/ies-formulario/catalogo/campo-cine'),
          api.get('/api/ies-formulario/catalogo/vigilancia-preguntas'),
        ]);
        if (!cancelled) {
          if (Array.isArray(cineRes.data)) setCineCampos(cineRes.data);
          if (Array.isArray(vtRes.data)) setVtPreguntas(vtRes.data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [canEdit]);

  useEffect(() => {
    if (!canEdit || !Number.isFinite(anioNum)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/ies-formulario/catalogo/pnd-objetivos', {
          params: { anio: anioNum },
        });
        if (!cancelled) {
          setPndCatalog({
            periodo_actual: res.data?.periodo_actual || (anioNum >= 2025 ? '2025-2029' : anioNum === 2024 ? '2024-2025' : '2021-2025'),
            periodo_previo: res.data?.periodo_previo || (anioNum >= 2025 ? '2024-2025' : anioNum === 2024 ? '2021-2025' : '2017-2020'),
            objetivos: Array.isArray(res.data?.objetivos) ? res.data.objetivos : [],
            warning: null,
          });
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPndCatalog((prev) => ({
            ...prev,
            periodo_actual: anioNum >= 2025 ? '2025-2029' : anioNum === 2024 ? '2024-2025' : '2021-2025',
            periodo_previo: anioNum >= 2025 ? '2024-2025' : anioNum === 2024 ? '2021-2025' : '2017-2020',
            warning: 'No se pudo cargar el catálogo dinámico del PND. Revise la conexión del formulario.',
          }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [anioNum, canEdit]);

  const hydrateFromServer = useCallback((payload) => {
    const d = payload.datos;
    if (!d) return;
    hydratingRef.current = true;
    setNums({
      num_personal_academico_hombres: d.num_personal_academico_hombres ?? '',
      num_personal_academico_mujeres: d.num_personal_academico_mujeres ?? '',
      num_personal_phd_hombres: d.num_personal_phd_hombres ?? '',
      num_personal_phd_mujeres: d.num_personal_phd_mujeres ?? '',
      num_personal_investigacion_hombres: d.num_personal_investigacion_hombres ?? '',
      num_personal_investigacion_mujeres: d.num_personal_investigacion_mujeres ?? '',
      num_personal_apoyo_hombres: d.num_personal_apoyo_hombres ?? '',
      num_personal_apoyo_mujeres: d.num_personal_apoyo_mujeres ?? '',
      num_estudiantes_pregrado_inv_hombres: d.num_estudiantes_pregrado_inv_hombres ?? '',
      num_estudiantes_pregrado_inv_mujeres: d.num_estudiantes_pregrado_inv_mujeres ?? '',
      num_estudiantes_posgrado_inv_hombres: d.num_estudiantes_posgrado_inv_hombres ?? '',
      num_estudiantes_posgrado_inv_mujeres: d.num_estudiantes_posgrado_inv_mujeres ?? '',
      num_academico_inv_hombres: d.num_academico_inv_hombres ?? '',
      num_academico_inv_mujeres: d.num_academico_inv_mujeres ?? '',
    });
    const redesArr = Array.isArray(d.redes_academicas) ? d.redes_academicas : [];
    setNumRedes(redesArr.length);
    setRedesList(redesArr);

    const RANK_VALS = RANKINGS_CONOCIDOS.map((r) => r.v);
    const rankArr = Array.isArray(d.rankings) ? d.rankings : [];
    setRankingsSeleccionados(rankArr.filter((r) => RANK_VALS.includes(r)));
    const rankOtras = rankArr.filter((r) => !RANK_VALS.includes(r));
    setRankingsOtrasN(rankOtras.length);
    setRankingsOtras(rankOtras);

    const ACRED_VALS = ACREDITACIONES_CONOCIDAS.map((a) => a.v);
    const acredArr = Array.isArray(d.acreditaciones) ? d.acreditaciones : [];
    setAcreditacionesSeleccionadas(acredArr.filter((a) => ACRED_VALS.includes(a)));
    const acredOtras = acredArr.filter((a) => !ACRED_VALS.includes(a));
    setAcreditacionesOtrasN(acredOtras.length);
    setAcreditacionesOtras(acredOtras);

    const ex = normalizeExtraForSigc(d.formulario_extra);
    ex.matrices_5_anios = ensureMatricesShape(ex.matrices_5_anios, d.anio);

    // Compat: grupos_detalle may be string[] from older saves → upgrade to object[]
    if (Array.isArray(ex.grupos_detalle)) {
      ex.grupos_detalle = ex.grupos_detalle.map((g) => normalizeGrupoDetalleRow(g));
    }
    if (Array.isArray(ex.centros_detalle)) {
      ex.centros_detalle = ex.centros_detalle.map((c) => normalizeCentroDetalleRow(c));
    }
    // Infer tiene_centros gate from existing data
    if (ex.tiene_centros === null || ex.tiene_centros === undefined) {
      ex.tiene_centros = ex.num_centros_declarado > 0 ? true : null;
    }
    if (ex.p28_aplica === false && ((d.num_estudiantes_pregrado_inv_hombres || 0) > 0 || (d.num_estudiantes_pregrado_inv_mujeres || 0) > 0)) {
      ex.p28_aplica = true;
    }
    if (!ex.p28_tiene_desglose && ((d.num_estudiantes_pregrado_inv_hombres || 0) > 0 || (d.num_estudiantes_pregrado_inv_mujeres || 0) > 0)) {
      ex.p28_tiene_desglose = 'si';
    }
    if (ex.p29_aplica === false && ((d.num_estudiantes_posgrado_inv_hombres || 0) > 0 || (d.num_estudiantes_posgrado_inv_mujeres || 0) > 0)) {
      ex.p29_aplica = true;
    }
    if (!ex.p29_tiene_desglose && ((d.num_estudiantes_posgrado_inv_hombres || 0) > 0 || (d.num_estudiantes_posgrado_inv_mujeres || 0) > 0)) {
      ex.p29_tiene_desglose = 'si';
    }
    if (ex.p21_aplica === false && referenceYear.some((yy) => (Number(ex.matrices_5_anios?.docentes_tc_titulares_inv?.[yy]?.h) || 0) > 0 || (Number(ex.matrices_5_anios?.docentes_tc_titulares_inv?.[yy]?.m) || 0) > 0)) {
      ex.p21_aplica = true;
    }
    if (ex.p22_aplica === false && referenceYear.some((yy) => (Number(ex.matrices_5_anios?.docentes_tc_no_titulares_inv?.[yy]?.h) || 0) > 0 || (Number(ex.matrices_5_anios?.docentes_tc_no_titulares_inv?.[yy]?.m) || 0) > 0)) {
      ex.p22_aplica = true;
    }

    setExtra(ex);
    setProjectImportSummary((prev) => ({ ...prev, total: Array.isArray(ex.proyectos_detalle) ? ex.proyectos_detalle.length : 0 }));
    setCongresosImportSummary({
      total: Array.isArray(ex.congresos_detalle) ? ex.congresos_detalle.length : 0,
      invalid: 0,
      nacionales: Array.isArray(ex.congresos_detalle) ? ex.congresos_detalle.filter((row) => normalizeCongresoAlcance(row?.alcance) === 'nacional').length : 0,
      internacionales: Array.isArray(ex.congresos_detalle) ? ex.congresos_detalle.filter((row) => normalizeCongresoAlcance(row?.alcance) === 'internacional').length : 0,
      warnings: [],
    });
    setLibrosImportSummary({
      total: Array.isArray(ex.libros_detalle) ? ex.libros_detalle.length : 0,
      invalid: 0,
      warnings: [],
    });

    if (ex.tiene_personal_investigacion !== null && ex.tiene_personal_investigacion !== undefined) {
      setTienePersonalInvestigacion(ex.tiene_personal_investigacion);
    } else {
      const h = d.num_personal_investigacion_hombres;
      const m = d.num_personal_investigacion_mujeres;
      setTienePersonalInvestigacion(h > 0 || m > 0 ? true : null);
    }

    if (ex.tiene_personal_apoyo !== null && ex.tiene_personal_apoyo !== undefined) {
      setTienePersonalApoyo(ex.tiene_personal_apoyo);
    } else {
      const apoyo_h = d.num_personal_apoyo_hombres;
      const apoyo_m = d.num_personal_apoyo_mujeres;
      setTienePersonalApoyo(apoyo_h > 0 || apoyo_m > 0 ? true : null);
    }

    setVtResp({ ...(payload.vigilancia_respuestas || {}) });
    setStep(Math.min(Math.max((d.paso_actual || 1) - 1, 0), STEPS.length - 1));
    const syncedPayload = buildDraftPayloadFromParts({
      anio: d.anio,
      nums: {
        num_personal_academico_hombres: d.num_personal_academico_hombres ?? '',
        num_personal_academico_mujeres: d.num_personal_academico_mujeres ?? '',
        num_personal_phd_hombres: d.num_personal_phd_hombres ?? '',
        num_personal_phd_mujeres: d.num_personal_phd_mujeres ?? '',
        num_personal_investigacion_hombres: d.num_personal_investigacion_hombres ?? '',
        num_personal_investigacion_mujeres: d.num_personal_investigacion_mujeres ?? '',
        num_personal_apoyo_hombres: d.num_personal_apoyo_hombres ?? '',
        num_personal_apoyo_mujeres: d.num_personal_apoyo_mujeres ?? '',
        num_estudiantes_pregrado_inv_hombres: d.num_estudiantes_pregrado_inv_hombres ?? '',
        num_estudiantes_pregrado_inv_mujeres: d.num_estudiantes_pregrado_inv_mujeres ?? '',
        num_estudiantes_posgrado_inv_hombres: d.num_estudiantes_posgrado_inv_hombres ?? '',
        num_estudiantes_posgrado_inv_mujeres: d.num_estudiantes_posgrado_inv_mujeres ?? '',
        num_academico_inv_hombres: d.num_academico_inv_hombres ?? '',
        num_academico_inv_mujeres: d.num_academico_inv_mujeres ?? '',
      },
      tienePersonalInvestigacion: ex.tiene_personal_investigacion !== null && ex.tiene_personal_investigacion !== undefined
        ? ex.tiene_personal_investigacion
        : ((d.num_personal_investigacion_hombres > 0 || d.num_personal_investigacion_mujeres > 0) ? true : null),
      tienePersonalApoyo: ex.tiene_personal_apoyo !== null && ex.tiene_personal_apoyo !== undefined
        ? ex.tiene_personal_apoyo
        : ((d.num_personal_apoyo_hombres > 0 || d.num_personal_apoyo_mujeres > 0) ? true : null),
      redesList: redesArr,
      rankingsSeleccionados: rankArr.filter((r) => RANK_VALS.includes(r)),
      rankingsOtrasN: rankOtras.length,
      rankingsOtras: rankOtras,
      acreditacionesSeleccionadas: acredArr.filter((a) => ACRED_VALS.includes(a)),
      acreditacionesOtrasN: acredOtras.length,
      acreditacionesOtras: acredOtras,
      extra: ex,
      vtResp: { ...(payload.vigilancia_respuestas || {}) },
    });
    setLastSavedSignature(buildDraftComparableSignature(syncedPayload));
    setDraftStatus('saved');
    setLastSavedAt(d.updated_at || new Date().toISOString());
    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, []);

  const fetchEstado = useCallback(async () => {
    if (!universidadId || viewMode !== 'form') return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/ies-formulario/${universidadId}`, { params: { anio } });
      setEstado(res.data);
      hydrateFromServer(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Error al cargar el formulario');
    } finally {
      setLoading(false);
    }
  }, [universidadId, anio, hydrateFromServer, viewMode]);

  const fetchFormulariosRegistrados = useCallback(async () => {
    if (!canEdit) return;
    try {
      const res = await api.get('/api/ies-formulario/registros');
      setFormulariosRegistrados(Array.isArray(res.data) ? res.data : []);
    } catch {
      setFormulariosRegistrados([]);
    }
  }, [canEdit]);

  const abrirFormularioEnviado = useCallback((item) => {
    setUniversidadId(String(item.universidad_id));
    setAnio(item.anio);
    setStep(0);
    setViewMode('form');
  }, []);

  const añosEnviadosParaNuevaUniversidad = useMemo(() => {
    return (formulariosRegistradosByUniversidad[String(newFormUniversidadId)] || [])
      .filter((item) => item.estado === 'ENVIADO')
      .map((item) => Number(item.anio));
  }, [formulariosRegistradosByUniversidad, newFormUniversidadId]);

  const añosBaseDisponibles = useMemo(() => {
    return [...(formulariosRegistradosByUniversidad[String(newFormUniversidadId)] || [])]
      .filter((item) => item.estado === 'ENVIADO')
      .sort((a, b) => Number(b.anio) - Number(a.anio));
  }, [formulariosRegistradosByUniversidad, newFormUniversidadId]);

  const crearNuevoFormulario = useCallback(async () => {
    if (!newFormUniversidadId || !newFormYear) return;
    if (añosEnviadosParaNuevaUniversidad.includes(Number(newFormYear))) {
      setError('Ese año ya fue enviado para la institución seleccionada.');
      return;
    }
    if (newFormMode === 'COPIAR' && !copySourceYear) {
      setError('Seleccione el año base que desea copiar.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`/api/ies-formulario/${newFormUniversidadId}/inicializar`, {
        anio: Number(newFormYear),
        modo: newFormMode,
        source_anio: newFormMode === 'COPIAR' ? Number(copySourceYear) : null,
      });
      setUniversidadId(String(newFormUniversidadId));
      setAnio(Number(newFormYear));
      setEstado(res.data);
      hydrateFromServer(res.data);
      setViewMode('form');
      setShowNewFormModal(false);
      setCopySourceYear('');
      setNewFormMode('BLANCO');
      await fetchFormulariosRegistrados();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'No se pudo crear el formulario.');
    } finally {
      setLoading(false);
    }
  }, [añosEnviadosParaNuevaUniversidad, copySourceYear, fetchFormulariosRegistrados, hydrateFromServer, newFormMode, newFormUniversidadId, newFormYear]);

  useEffect(() => {
    if (universidadId && viewMode === 'form') fetchEstado();
  }, [universidadId, anio, fetchEstado]);

  useEffect(() => {
    fetchFormulariosRegistrados();
  }, [fetchFormulariosRegistrados]);

  useEffect(() => {
    if (newFormMode === 'COPIAR' && !copySourceYear && añosBaseDisponibles.length > 0) {
      setCopySourceYear(String(añosBaseDisponibles[0].anio));
    }
    if (newFormMode === 'BLANCO' && copySourceYear) {
      setCopySourceYear('');
    }
  }, [añosBaseDisponibles, copySourceYear, newFormMode]);

  const applySugerencias = () => {
    if (!estado?.sugerencias) return;
    const s = estado.sugerencias;
    setNums((prev) => ({
      ...prev,
      num_personal_academico_hombres: s.num_investigadores_hombres || prev.num_personal_academico_hombres,
      num_personal_academico_mujeres: s.num_investigadores_mujeres || prev.num_personal_academico_mujeres,
    }));
    setExtra((ex) =>
      mergeExtra(ex, {
        num_lineas_declarado: s.num_lineas_investigacion_activas || ex.num_lineas_declarado,
        lineas_detalle: ensureListLen(
          s.nombres_lineas?.length ? s.nombres_lineas : ex.lineas_detalle,
          Math.max(s.num_lineas_investigacion_activas || 0, ex.lineas_detalle?.length || 0)
        ),
        num_grupos_declarado: s.num_grupos_investigacion || ex.num_grupos_declarado,
        num_centros_declarado: s.num_centros_investigacion || ex.num_centros_declarado,
        q1_5_anios: s.q1_5_anios ?? ex.q1_5_anios,
        q2_5_anios: s.q2_5_anios ?? ex.q2_5_anios,
        q3_5_anios: s.q3_5_anios ?? ex.q3_5_anios,
        q4_5_anios: s.q4_5_anios ?? ex.q4_5_anios,
      })
    );
  };

  const draftPayload = useMemo(() => buildDraftPayloadFromParts({
    anio,
    nums,
    tienePersonalInvestigacion,
    tienePersonalApoyo,
    redesList,
    rankingsSeleccionados,
    rankingsOtrasN,
    rankingsOtras,
    acreditacionesSeleccionadas,
    acreditacionesOtrasN,
    acreditacionesOtras,
    extra,
    vtResp,
  }), [anio, nums, tienePersonalInvestigacion, tienePersonalApoyo, redesList, rankingsSeleccionados, rankingsOtrasN, rankingsOtras, acreditacionesSeleccionadas, acreditacionesOtrasN, acreditacionesOtras, extra, vtResp]);

  const draftSignature = useMemo(() => buildDraftComparableSignature(draftPayload), [draftPayload]);
  const buildPayload = useCallback(() => ({ ...draftPayload }), [draftPayload]);

  const applySavedDraftLocally = useCallback((body, updatedAt, nextEstado = 'BORRADOR') => {
    setEstado((prev) => {
      const prevDatos = prev?.datos || {};
      return {
        ...(prev || {}),
        universidad_id: Number(universidadId) || prev?.universidad_id,
        universidad_nombre: prev?.universidad_nombre || '',
        anio: Number(body.anio),
        datos: {
          ...prevDatos,
          ...body,
          estado: nextEstado,
          paso_actual: body.paso_actual ?? prevDatos.paso_actual ?? step + 1,
          formulario_extra: body.formulario_extra ?? prevDatos.formulario_extra ?? {},
          updated_at: updatedAt,
        },
        vigilancia_respuestas: body.vigilancia_respuestas ?? prev?.vigilancia_respuestas ?? {},
      };
    });
  }, [step, universidadId]);

  const applyNormalizedProjectSectionLocally = useCallback((body) => {
    if (!body?.formulario_extra?.matrices_5_anios) return;
    setExtra((prev) => ({
      ...prev,
      matrices_5_anios: body.formulario_extra.matrices_5_anios,
    }));
  }, []);

  useEffect(() => {
    if (hydratingRef.current || !universidadId) return;
    if (!lastSavedSignature) {
      setDraftStatus('idle');
      return;
    }
    if (saving) return;
    setDraftStatus(draftSignature === lastSavedSignature ? 'saved' : 'dirty');
  }, [draftSignature, lastSavedSignature, saving, universidadId]);

  const saveDraft = async (navigateToStep) => {
    if (!universidadId || !canEdit || editLocked) return false;
    const validationError = validateFormInputs();
    if (validationError) {
      setDraftStatus('error');
      setError(validationError);
      return false;
    }
    setSaving(true);
    setDraftStatus('saving');
    setError(null);
    try {
      let body = buildPayload();
      const idx = typeof navigateToStep === 'number' ? navigateToStep : step;
      if (step === 4 && typeof navigateToStep === 'number' && navigateToStep > 4) {
        body = fillEmptyProjectSectionWithZeros(body, anio);
        applyNormalizedProjectSectionLocally(body);
      }
      body.paso_actual = idx + 1;
      await api.put(`/api/ies-formulario/${universidadId}`, body);
      const savedAt = new Date().toISOString();
      applySavedDraftLocally(body, savedAt, body.estado || 'BORRADOR');
      await fetchFormulariosRegistrados();
      setLastSavedSignature(buildDraftComparableSignature(body));
      setLastSavedAt(savedAt);
      setDraftStatus('saved');
      if (typeof navigateToStep === 'number') setStep(navigateToStep);
      return true;
    } catch (e) {
      setDraftStatus('error');
      setError(e.response?.data?.detail || e.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (!universidadId || !canEdit || editLocked) return;
    const validationError = validateFormInputs();
    if (validationError) {
      setDraftStatus('error');
      setError(validationError);
      return;
    }
    const accepted = await confirm({
      title: 'Enviar formulario',
      message: '¿Marcar este formulario como enviado?',
      details: 'Después del envío, el registro quedará como enviado y tendrá restricciones de edición según el rol.',
      confirmText: 'Sí, enviar',
      cancelText: 'Cancelar',
      variation: 'warning',
      badge: 'Envío final',
      icon: 'send',
    });
    if (!accepted) return;
    setSaving(true);
    setDraftStatus('saving');
    try {
      const body = buildPayload();
      body.estado = 'ENVIADO';
      body.paso_actual = STEPS.length;
      await api.put(`/api/ies-formulario/${universidadId}`, body);
      const savedAt = new Date().toISOString();
      applySavedDraftLocally(body, savedAt, 'ENVIADO');
      await fetchFormulariosRegistrados();
      setLastSavedSignature(buildDraftComparableSignature(body));
      setLastSavedAt(savedAt);
      setDraftStatus('saved');
    } catch (e) {
      setDraftStatus('error');
      setError(e.response?.data?.detail || e.message || 'Error al finalizar');
    } finally {
      setSaving(false);
    }
  };

  const saveAndExit = async () => {
    if (editLocked) return;
    const ok = await saveDraft();
    if (!ok) return;
    await logout();
    navigate('/login');
  };

  const autofillProduccionConIa = async () => {
    if (!universidadId || !canEdit || aiFill.loading || editLocked) return;
    setError(null);
    setAiFill({ loading: true, current: 0, resumen: null, advertencias: [] });
    try {
      setAiFill((prev) => ({ ...prev, current: 1 }));
      const res = await api.post(`/api/ies-formulario/${universidadId}/produccion/ai-autofill`, {
        anio: Number(anio),
      });
      setAiFill((prev) => ({ ...prev, current: 2 }));
      const patch = res.data?.formulario_extra || {};
      setExtra((ex) => {
        const merged = mergeExtra(ex, patch);
        if (patch?.produccion) {
          merged.produccion = patch.produccion;
        }
        if (patch?.produccion_ext) {
          merged.produccion_ext = patch.produccion_ext;
        }
        return merged;
      });
      setAiFill({
        loading: false,
        current: 2,
        resumen: res.data?.resumen || {},
        advertencias: res.data?.advertencias || [],
      });
    } catch (e) {
      setAiFill((prev) => ({ ...prev, loading: false }));
      setError(e.response?.data?.detail || e.message || 'Error al autollenar producción científica');
    }
  };

  const sugerencias = estado?.sugerencias;
  const lineasN = Math.max(0, parseInt(extra.num_lineas_declarado, 10) || 0);
  const gruposN = Math.max(0, parseInt(extra.num_grupos_declarado, 10) || 0);
  const centrosN = Math.max(0, parseInt(extra.num_centros_declarado, 10) || 0);
  const lineasList = useMemo(() => ensureListLen(extra.lineas_detalle, lineasN), [extra.lineas_detalle, lineasN]);
  const proyectosDetalle = useMemo(
    () => (Array.isArray(extra.proyectos_detalle) ? extra.proyectos_detalle.filter((row) => row && typeof row === 'object') : []),
    [extra.proyectos_detalle]
  );
  const proyectosPreview = useMemo(() => proyectosDetalle.slice(0, 8), [proyectosDetalle]);
  const congresosDetalle = useMemo(
    () => (Array.isArray(extra.congresos_detalle) ? extra.congresos_detalle.filter((row) => row && typeof row === 'object') : []),
    [extra.congresos_detalle]
  );
  const librosDetalle = useMemo(
    () => (Array.isArray(extra.libros_detalle) ? extra.libros_detalle.filter((row) => row && typeof row === 'object') : []),
    [extra.libros_detalle]
  );
  const congresosPreview = useMemo(() => congresosDetalle.slice(0, 8), [congresosDetalle]);
  const librosPreview = useMemo(() => librosDetalle.slice(0, 8), [librosDetalle]);
  /* ── Render helpers ── */

  const hm = (prefix) => ({
    h: Number(nums[`${prefix}_hombres`]) || 0,
    m: Number(nums[`${prefix}_mujeres`]) || 0,
  });

  const setNumField = useCallback((field, rawValue, max = MAX_FORM_NUMBER) => {
    setNums((prev) => ({ ...prev, [field]: sanitizeIntegerInput(rawValue, { max }) }));
  }, []);

  const validateFormInputs = useCallback(() => {
    const phone = String(extra.responsable_telefono || '').trim();
    if (phone && !isValidEcuadorMobilePhone(phone)) {
      return 'El teléfono del responsable debe tener 10 dígitos y comenzar con 09.';
    }

    if (hasInvalidNumericCollection(nums)) {
      return 'Revise los campos numéricos del formulario: solo se permiten números enteros positivos y dentro del límite permitido.';
    }

    if (!isValidNumericValue(extra.num_lineas_declarado)
      || !isValidNumericValue(extra.num_grupos_declarado)
      || !isValidNumericValue(extra.num_centros_declarado)) {
      return 'Las cantidades declaradas de líneas, grupos y centros deben ser números válidos y razonables.';
    }

    const vtNumerics = extra.vt_operativo || {};
    if (!isValidNumericValue(vtNumerics.personas_tc)
      || !isValidNumericValue(vtNumerics.personas_mt)
      || !isValidNumericValue(vtNumerics.horas_mensuales)
      || !isValidNumericValue(vtNumerics.presupuesto_usd)) {
      return 'Revise los valores numéricos de vigilancia tecnológica: solo se permiten números positivos dentro del rango permitido.';
    }

    if (hasInvalidNumericDraftMatrix(extra.matrices_5_anios)
      || hasInvalidNumericDraftMatrix(extra.produccion)
      || hasInvalidNumericDraftMatrix(extra.produccion_ext)) {
      return 'Revise las matrices del formulario: contienen valores numéricos inválidos o demasiado grandes.';
    }

    const invalidGrupo = (extra.grupos_detalle || []).some((row) =>
      !isValidNumericValue(row?.hombres)
      || !isValidNumericValue(row?.mujeres)
      || !isValidNumericValue(row?.antiguedad_anios)
      || !isValidNumericValue(row?.produccion_cientifica)
    );
    if (invalidGrupo) {
      return 'Revise los datos numéricos de grupos de investigación: no se permiten letras, signos negativos ni valores demasiado grandes.';
    }

    const invalidCentro = (extra.centros_detalle || []).some((row) =>
      !isValidNumericValue(row?.hombres)
      || !isValidNumericValue(row?.mujeres)
      || !isValidNumericValue(row?.personal_apoyo)
      || !isValidNumericValue(row?.fondos_internos_usd)
      || !isValidNumericValue(row?.fondos_externos_usd)
    );
    if (invalidCentro) {
      return 'Revise los datos numéricos de centros de investigación: no se permiten letras, signos negativos ni valores demasiado grandes.';
    }

    const propiedadRegistros = Array.isArray(extra.p27_patentes?.registros)
      ? extra.p27_patentes.registros
      : Object.values(extra.p27_patentes || {});
    const invalidPropiedadIntelectual = propiedadRegistros.some((record) =>
      !isValidNumericValue(record?.anio, { min: YEAR_MIN, max: YEAR_MAX })
    );
    if (invalidPropiedadIntelectual) {
      return 'Revise el año de registro en propiedad intelectual: debe ser un año válido.';
    }

    const transferenciaRegistros = Array.isArray(extra.p37_transferencia?.registros)
      ? extra.p37_transferencia.registros
      : Object.values(extra.p37_transferencia || {});
    const invalidTransferencia = transferenciaRegistros.some((record) =>
      !isValidNumericValue(record?.anio, { min: YEAR_MIN, max: YEAR_MAX })
    );
    if (invalidTransferencia) {
      return 'Revise el año en transferencia tecnológica: debe ser un año válido.';
    }

    const invalidCongresos = (extra.congresos_detalle || []).some((row) =>
      !isValidNumericValue(row?.anio, { min: YEAR_MIN, max: YEAR_MAX })
      || !VALID_CINE_FIELD_IDS.has(String(row?.campo_cine || '').trim())
      || !['nacional', 'internacional'].includes(String(row?.alcance || '').trim().toLowerCase())
      || !isValidNumericValue(row?.docentes_asistentes)
    );
    if (invalidCongresos) {
      return 'Revise la carga de congresos: año, campo CINE, alcance y docentes asistentes deben ser válidos.';
    }

    const invalidLibros = (extra.libros_detalle || []).some((row) =>
      !isValidNumericValue(row?.anio, { min: YEAR_MIN, max: YEAR_MAX })
      || !VALID_CINE_FIELD_IDS.has(String(row?.campo_cine || '').trim())
    );
    if (invalidLibros) {
      return 'Revise la carga de libros: año y campo CINE deben ser válidos.';
    }

    return null;
  }, [extra, nums]);

  const PersonalField = ({ code, label, prefix, displayCode = null }) => {
    const { h, m } = hm(prefix);
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40 space-y-3">
        <div className="flex items-center gap-2">
          <SmallPBadge code={code} display={displayCode} />
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres</label>
            <input type="number" min={0} value={nums[`${prefix}_hombres`] ?? ''} onChange={(e) => setNumField(`${prefix}_hombres`, e.target.value)} {...numericInputProps()} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres</label>
            <input type="number" min={0} value={nums[`${prefix}_mujeres`] ?? ''} onChange={(e) => setNumField(`${prefix}_mujeres`, e.target.value)} {...numericInputProps()} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Total <span className="text-slate-400 dark:text-slate-500 font-normal">(auto)</span></label>
            <input type="number" readOnly value={h + m || ''} className={inputDisabledCls} tabIndex={-1} />
          </div>
        </div>
      </div>
    );
  };

  const ConditionalPersonalField = ({ code, label, gate, onGateYes, onGateNo, prefix, noAplicaText, displayCode = null }) => {
    const { h, m } = hm(prefix);
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-2">
            <SmallPBadge code={code} display={displayCode} />
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onGateYes}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}>
              Sí
            </button>
            <button type="button" onClick={onGateNo}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}>
              No
            </button>
          </div>
        </div>
        {gate === true && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres</label>
              <input type="number" min={0} value={nums[`${prefix}_hombres`] ?? ''} onChange={(e) => setNumField(`${prefix}_hombres`, e.target.value)} {...numericInputProps()} className={inputCls} placeholder="0" />
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres</label>
              <input type="number" min={0} value={nums[`${prefix}_mujeres`] ?? ''} onChange={(e) => setNumField(`${prefix}_mujeres`, e.target.value)} {...numericInputProps()} className={inputCls} placeholder="0" />
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Total</label>
              <input type="number" readOnly value={h + m || ''} className={inputDisabledCls} tabIndex={-1} />
            </div>
          </div>
        )}
        {gate === false && noAplicaText && (
          <p className="text-sm text-slate-500 italic">{noAplicaText}</p>
        )}
      </div>
    );
  };

  const LIKERT_SCALE = ['Muy bajo', 'Bajo', 'Medio', 'Alto', 'Muy alto'];
  const LikertButtons = ({ value, onChange, scaleLabels }) => {
    const lbls = scaleLabels || LIKERT_SCALE;
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`flex-1 flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-sm font-medium transition-all ${value === n
                ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20 text-[#2D5BA6] dark:text-blue-400'
                : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
          >
            <span className="text-xl font-bold leading-none">{n}</span>
            <span className="text-[10px] leading-tight text-center opacity-75">{lbls[n - 1]}</span>
          </button>
        ))}
      </div>
    );
  };

  const CheckboxCard = ({ checked, onChange, label }) => (
    <label
      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all select-none ${checked ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
    >
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
      <span className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${checked ? 'bg-[#2D5BA6] border-[#2D5BA6]' : 'border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700'}`}>
        {checked && (
          <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2.5">
            <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
    </label>
  );

  const InlineMultiSelect = ({ options, selected, onToggle, placeholder = 'Sin selección', addLabel = 'Agregar' }) => {
    const availableOptions = options.filter((option) => !selected.includes(option.value));
    const [pendingValue, setPendingValue] = React.useState('');

    useEffect(() => {
      if (pendingValue && selected.includes(pendingValue)) {
        setPendingValue('');
      }
    }, [pendingValue, selected]);

    return (
      <div className="space-y-3">
        <div className="min-h-[42px] rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
          {(selected || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selected.map((value) => {
                const label = options.find((option) => option.value === value)?.label || value;
                return (
                  <span
                    key={value}
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-[#2D5BA6] dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    <span>{label}</span>
                    <button
                      type="button"
                      onClick={() => onToggle(value)}
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/80 text-[#2D5BA6] dark:bg-slate-800"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">{placeholder}</span>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={pendingValue}
            onChange={(e) => setPendingValue(e.target.value)}
            className={`${inputCls} flex-1`}
          >
            <option value="">Seleccione una opción</option>
            {availableOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!pendingValue}
            onClick={() => {
              if (!pendingValue) return;
              onToggle(pendingValue);
              setPendingValue('');
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#2D5BA6] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#244d8f] disabled:opacity-40"
          >
            <span className="material-icons-round text-sm">add</span>
            {addLabel}
          </button>
        </div>
      </div>
    );
  };

  const updateGrupoRow = (idx, updater) => {
    setExtra((ex) => {
      const rows = Array.isArray(ex.grupos_detalle) ? ex.grupos_detalle.map((row) => normalizeGrupoDetalleRow(row)) : [];
      const next = Array.from({ length: gruposN }, (_, i) => normalizeGrupoDetalleRow(rows[i]));
      next[idx] = updater(next[idx]);
      return { ...ex, grupos_detalle: next };
    });
  };

  const updateCentroRow = (idx, updater) => {
    setExtra((ex) => {
      const rows = Array.isArray(ex.centros_detalle) ? ex.centros_detalle.map((row) => normalizeCentroDetalleRow(row)) : [];
      const next = Array.from({ length: centrosN }, (_, i) => normalizeCentroDetalleRow(rows[i]));
      next[idx] = updater(next[idx]);
      return { ...ex, centros_detalle: next };
    });
  };

  const addGrupoRow = () => {
    setExtra((ex) => ({
      ...ex,
      num_grupos_declarado: (Number(ex.num_grupos_declarado) || 0) + 1,
      grupos_detalle: [...(Array.isArray(ex.grupos_detalle) ? ex.grupos_detalle.map((row) => normalizeGrupoDetalleRow(row)) : []), normalizeGrupoDetalleRow({})],
    }));
  };

  const addLineaRow = () => {
    setExtra((ex) => {
      const current = ensureListLen(ex.lineas_detalle, Number(ex.num_lineas_declarado) || 0);
      const next = [...current, ''];
      return {
        ...ex,
        num_lineas_declarado: next.length,
        lineas_detalle: next,
      };
    });
  };

  const removeLineaRow = (idx) => {
    setExtra((ex) => {
      const current = ensureListLen(ex.lineas_detalle, Number(ex.num_lineas_declarado) || 0);
      const next = current.filter((_, index) => index !== idx);
      return {
        ...ex,
        num_lineas_declarado: next.length,
        lineas_detalle: next,
      };
    });
  };

  const removeGrupoRow = (idx) => {
    setExtra((ex) => {
      const rows = Array.isArray(ex.grupos_detalle) ? ex.grupos_detalle.map((row) => normalizeGrupoDetalleRow(row)) : [];
      const nextRows = rows.filter((_, index) => index !== idx);
      return {
        ...ex,
        num_grupos_declarado: Math.max(0, nextRows.length),
        grupos_detalle: nextRows,
      };
    });
  };

  const addCentroRow = () => {
    setExtra((ex) => ({
      ...ex,
      tiene_centros: true,
      num_centros_declarado: (Number(ex.num_centros_declarado) || 0) + 1,
      centros_detalle: [...(Array.isArray(ex.centros_detalle) ? ex.centros_detalle.map((row) => normalizeCentroDetalleRow(row)) : []), normalizeCentroDetalleRow({})],
    }));
  };

  const removeCentroRow = (idx) => {
    setExtra((ex) => {
      const rows = Array.isArray(ex.centros_detalle) ? ex.centros_detalle.map((row) => normalizeCentroDetalleRow(row)) : [];
      const nextRows = rows.filter((_, index) => index !== idx);
      return {
        ...ex,
        num_centros_declarado: Math.max(0, nextRows.length),
        centros_detalle: nextRows,
        tiene_centros: nextRows.length > 0 ? true : ex.tiene_centros,
      };
    });
  };

  const clearProyectoImport = useCallback(() => {
    setExtra((ex) => ({ ...ex, proyectos_detalle: [] }));
    setProjectImportSummary({ total: 0, invalid: 0, investigatorsLinked: 0, odsLinked: 0, warnings: [] });
    if (proyectosFileInputRef.current) {
      proyectosFileInputRef.current.value = '';
    }
  }, []);

  const handleProyectoCsvSelected = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseProjectCsvText(text);
      setExtra((ex) => ({ ...ex, proyectos_detalle: parsed.rows }));
      setProjectImportSummary({
        total: parsed.total,
        invalid: parsed.invalid,
        investigatorsLinked: parsed.investigatorsLinked,
        odsLinked: parsed.odsLinked,
        warnings: parsed.warnings || [],
      });
      if (parsed.total === 0) {
        setError('La plantilla no contiene proyectos válidos para importar.');
        return;
      }
      if (parsed.invalid > 0) {
        setError(`Se importaron ${parsed.total} proyectos. ${parsed.invalid} fila(s) se omitieron porque no tenían código ni nombre.`);
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err?.message || 'No se pudo leer la plantilla de proyectos.');
      event.target.value = '';
    }
  }, []);

  const clearCongresosImport = useCallback(() => {
    setExtra((ex) => ({
      ...ex,
      congresos_detalle: [],
      produccion: {
        ...(ex.produccion || {}),
        p33_congresos: {},
        p33_congresos_nacionales: {},
        p33_congresos_internacionales: {},
      },
    }));
    setCongresosImportSummary({ total: 0, invalid: 0, nacionales: 0, internacionales: 0, warnings: [] });
    if (congresosFileInputRef.current) {
      congresosFileInputRef.current.value = '';
    }
  }, []);

  const handleCongresosCsvSelected = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCongresosCsvText(text);
      const summarized = summarizeCongresosToProduccion(parsed.rows);
      setExtra((ex) => ({
        ...ex,
        congresos_detalle: parsed.rows,
        produccion: {
          ...(ex.produccion || {}),
          p33_congresos: mergeYearCategoryMatrices(
            summarized.p33_congresos_nacionales,
            summarized.p33_congresos_internacionales
          ),
          ...summarized,
        },
      }));
      setCongresosImportSummary({
        total: parsed.total,
        invalid: parsed.invalid,
        nacionales: parsed.nacionales,
        internacionales: parsed.internacionales,
        warnings: parsed.warnings || [],
      });
      if (parsed.total === 0) {
        setError('La plantilla no contiene registros válidos de congresos para importar.');
        return;
      }
      if (parsed.invalid > 0) {
        setError(`Se importaron ${parsed.total} registros de congresos. ${parsed.invalid} fila(s) se omitieron por datos incompletos o inválidos.`);
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err?.message || 'No se pudo leer la plantilla de congresos.');
      event.target.value = '';
    }
  }, []);

  const clearLibrosImport = useCallback(() => {
    setExtra((ex) => ({
      ...ex,
      libros_detalle: [],
      produccion: {
        ...(ex.produccion || {}),
        p34_libros: {},
      },
    }));
    setLibrosImportSummary({ total: 0, invalid: 0, warnings: [] });
    if (librosFileInputRef.current) {
      librosFileInputRef.current.value = '';
    }
  }, []);

  const handleLibrosCsvSelected = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseLibrosCsvText(text);
      const summarized = summarizeLibrosToProduccion(parsed.rows);
      setExtra((ex) => ({
        ...ex,
        libros_detalle: parsed.rows,
        produccion: {
          ...(ex.produccion || {}),
          ...summarized,
        },
      }));
      setLibrosImportSummary({
        total: parsed.total,
        invalid: parsed.invalid,
        warnings: parsed.warnings || [],
      });
      if (parsed.total === 0) {
        setError('La plantilla no contiene registros válidos de libros para importar.');
        return;
      }
      if (parsed.invalid > 0) {
        setError(`Se importaron ${parsed.total} registros de libros. ${parsed.invalid} fila(s) se omitieron por datos incompletos o inválidos.`);
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err?.message || 'No se pudo leer la plantilla de libros.');
      event.target.value = '';
    }
  }, []);

  const lastSavedLabel = useMemo(() => {
    if (!lastSavedAt) return 'Aún no se ha guardado';
    const date = new Date(lastSavedAt);
    if (Number.isNaN(date.getTime())) return 'Aún no se ha guardado';
    return new Intl.DateTimeFormat('es-EC', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  }, [lastSavedAt]);

  const draftStatusUi = {
    idle: {
      tone: 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400',
      badge: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      label: 'Sin guardar',
      icon: 'schedule',
      detail: 'Todavía no hay un guardado en esta sesión.',
    },
    dirty: {
      tone: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
      badge: 'bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
      label: 'Cambios sin guardar',
      icon: 'edit_note',
      detail: 'Hay cambios pendientes en esta fase.',
    },
    saving: {
      tone: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300',
      badge: 'bg-blue-200 text-blue-800 dark:bg-blue-900/70 dark:text-blue-200',
      label: 'Guardando...',
      icon: 'sync',
      detail: 'Se está guardando el borrador del formulario.',
    },
    saved: {
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
      badge: 'bg-emerald-200 text-emerald-800 dark:bg-emerald-900/70 dark:text-emerald-200',
      label: 'Guardado',
      icon: 'task_alt',
      detail: `Último guardado: ${lastSavedLabel}`,
    },
    error: {
      tone: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300',
      badge: 'bg-red-200 text-red-800 dark:bg-red-900/70 dark:text-red-200',
      label: 'Error al guardar',
      icon: 'error',
      detail: 'El último intento de guardado falló.',
    },
  }[draftStatus] || {
    tone: 'border-slate-200 bg-slate-50 text-slate-500',
    badge: 'bg-slate-200 text-slate-600',
    label: 'Sin guardar',
    icon: 'schedule',
    detail: 'Todavía no hay un guardado en esta sesión.',
  };
  const isSubmitted = estado?.datos?.estado === 'ENVIADO';
  const isReadOnlySubmitted = Boolean(estado?.solo_lectura) || (isSubmitted && !isSuperAdmin);
  const interactionLocked = isUiLocked;
  const editLocked = isUiLocked || isReadOnlySubmitted;
  const savedStep = estado?.datos?.paso_actual ? estado.datos.paso_actual - 1 : -1;

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-slate-600">
        Necesita rol de administrador para usar el formulario anual IES.
      </div>
    );
  }

  /* ── Sidebar stepper ── */
  const Stepper = () => {
    const progress = ((step + 1) / STEPS.length) * 100;
    return (
      <SCard className="p-5 overflow-hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Progreso</p>
        <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mb-5 overflow-hidden">
          <div className="h-full bg-[#2D5BA6] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <nav className="space-y-1">
          {STEPS.map((s, i) => {
            const isActive = i === step;
            const isDone = i < step || i <= savedStep;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (interactionLocked) return;
                  if (i === step) return;
                  if (isReadOnlySubmitted) {
                    setStep(i);
                    return;
                  }
                  saveDraft(i);
                }}
                disabled={interactionLocked}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${isActive ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2D5BA6]' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
              >
                <span className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold transition-all ${isDone && !isActive
                    ? 'bg-emerald-500 text-white'
                    : isActive
                      ? 'bg-[#2D5BA6] text-white'
                      : 'border-2 border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500'
                  }`}>
                  {isDone && !isActive ? (
                    <svg viewBox="0 0 10 8" fill="none" className="w-3 h-3">
                      <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </span>
                <span className={`text-sm font-medium flex-1 leading-tight ${isActive ? 'text-[#2D5BA6] dark:text-blue-400' : isDone ? 'text-slate-700 dark:text-slate-300' : 'text-slate-500 dark:text-slate-500'}`}>
                  {s.title}
                </span>
                {isDone && !isActive && (
                  <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full flex-shrink-0">
                    Listo
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </SCard>
    );
  };

  /* ── Navigation footer ── */
  const NavFooter = () => (
    <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-slate-100 dark:border-slate-700">
      <button
        type="button"
        disabled={step === 0 || saving || editLocked}
        onClick={() => saveDraft(step - 1)}
        className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <span className="material-icons-round text-base">arrow_back</span>
        Guardar y retroceder
      </button>
      <button
        type="button"
        disabled={saving || !universidadId || editLocked}
        onClick={() => saveDraft()}
        className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40 ${draftStatus === 'saved'
            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50'
            : draftStatus === 'dirty'
              ? 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
      >
        {draftStatus === 'saving' ? 'Guardando...' : draftStatus === 'saved' ? 'Guardado' : 'Guardar borrador'}
      </button>
      <button
        type="button"
        disabled={saving || !universidadId || editLocked}
        onClick={saveAndExit}
        className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 disabled:opacity-40"
      >
        Guardar y salir
      </button>
      {step < STEPS.length - 1 ? (
        <button
          type="button"
          disabled={saving || !universidadId || editLocked}
          onClick={() => saveDraft(step + 1)}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#2D5BA6] text-white text-sm font-semibold ml-auto hover:bg-[#244d8f] transition-colors disabled:opacity-40"
        >
          Siguiente
          <span className="material-icons-round text-base">arrow_forward</span>
        </button>
      ) : (
        <button
          type="button"
          disabled={saving || !universidadId || editLocked}
          onClick={finalize}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold ml-auto hover:bg-emerald-700 transition-colors disabled:opacity-40"
        >
          <span className="material-icons-round text-base">send</span>
          Finalizar envío
        </button>
      )}
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-8 px-4">
      {isUiLocked && (
        <div className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-[2px] flex items-center justify-center px-4">
          <div className="w-full max-w-3xl rounded-3xl border border-blue-200 bg-white p-6 shadow-2xl dark:border-blue-900/60 dark:bg-slate-900 sm:p-8">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-11 w-11 rounded-2xl bg-[#2D5BA6] text-white flex items-center justify-center">
                <span className="material-icons-round animate-pulse">auto_awesome</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Analizando publicaciones del año {anio}</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  El formulario está bloqueado temporalmente para evitar cambios mientras se clasifica la producción científica.
                </p>
              </div>
            </div>
            <div className="mt-6">
              <AiFillSteps
                current={aiFill.current}
                loading={aiFill.loading}
                resumen={aiFill.resumen}
                advertencias={[]}
              />
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2D5BA6] flex items-center justify-center flex-shrink-0">
              <span className="material-icons-round text-white text-xl">assignment</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 leading-tight">Formulario anual IES</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Instrumento P1–P64 · borrador por institución y año</p>
            </div>
          </div>
        </motion.div>

        {/* Error banner */}
        {error && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm flex items-start gap-2">
            <span className="material-icons-round text-base flex-shrink-0 mt-0.5">error_outline</span>
            <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
          </div>
        )}

        {showNewFormModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-[2px] flex items-center justify-center px-4">
            <div className="w-full max-w-4xl rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:p-8">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Nuevo formulario</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Elija institución, año y si desea iniciar en blanco o copiar la información del año anterior.</p>
                </div>
                <button type="button" onClick={() => setShowNewFormModal(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800">
                  Cerrar
                </button>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Institución</label>
                  <select value={newFormUniversidadId} onChange={(e) => setNewFormUniversidadId(e.target.value)} disabled={!institutionAccess.allAccess} className={inputCls}>
                    <option value="">Seleccione una institución…</option>
                    {universidadesDisponibles.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}{u.short_name ? ` (${u.short_name})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Año nuevo</label>
                  <select value={newFormYear} onChange={(e) => setNewFormYear(Number(e.target.value))} className={inputCls}>
                    {Array.from({ length: 9 }, (_, idx) => new Date().getFullYear() - 2 + idx).map((yearOption) => {
                      const disabled = añosEnviadosParaNuevaUniversidad.includes(yearOption);
                      return <option key={yearOption} value={yearOption} disabled={disabled}>{yearOption}{disabled ? ' · ya enviado' : ''}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <button type="button" onClick={() => setNewFormMode('BLANCO')} className={`rounded-2xl border p-4 text-left transition-all ${newFormMode === 'BLANCO' ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Empezar en blanco</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Crea el formulario vacío para ese año.</p>
                </button>
                <button type="button" onClick={() => setNewFormMode('COPIAR')} className={`rounded-2xl border p-4 text-left transition-all ${newFormMode === 'COPIAR' ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Rellenar con datos anteriores</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Toma un formulario previo enviado y lo deja como borrador para el nuevo año.</p>
                </button>
              </div>
              {newFormMode === 'COPIAR' && (
                <div className="mt-5">
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Año base para copiar</label>
                  <select value={copySourceYear} onChange={(e) => setCopySourceYear(e.target.value)} className={inputCls}>
                    <option value="">Seleccione un año base…</option>
                    {añosBaseDisponibles.map((item) => (
                      <option key={`${item.universidad_id}-${item.anio}`} value={item.anio}>{item.anio} · {item.universidad_nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowNewFormModal(false)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800">
                  Cancelar
                </button>
                <button type="button" onClick={crearNuevoFormulario} disabled={loading || !newFormUniversidadId || (newFormMode === 'COPIAR' && !copySourceYear)} className="px-5 py-2.5 rounded-xl bg-[#2D5BA6] text-white text-sm font-semibold hover:bg-[#244d8f] disabled:opacity-40">
                  Crear formulario
                </button>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'list' ? (
          <SCard className="p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">{isSuperAdmin ? 'Formularios enviados del sistema' : 'Formularios enviados de la institución'}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Revise formularios enviados y cree un nuevo formulario evitando repetir años ya reportados.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setNewFormMode('BLANCO');
                  setCopySourceYear('');
                  setNewFormYear(new Date().getFullYear());
                  setShowNewFormModal(true);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-[#2D5BA6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#244d8f]"
              >
                <span className="material-icons-round text-base">add</span>
                Nuevo formulario
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Formularios en proceso</h3>
                  <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {formulariosBorrador.length}
                  </span>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                        <th className="px-3 py-3 text-left font-semibold">Institución</th>
                        <th className="px-3 py-3 text-left font-semibold">Año</th>
                        <th className="px-3 py-3 text-left font-semibold">Estado</th>
                        <th className="px-3 py-3 text-left font-semibold">Paso</th>
                        <th className="px-3 py-3 text-left font-semibold">Última actualización</th>
                        <th className="px-3 py-3 text-left font-semibold">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formulariosBorrador.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-slate-500 dark:text-slate-400">No hay formularios en proceso.</td>
                        </tr>
                      ) : formulariosBorrador.map((item) => (
                        <tr key={`${item.universidad_id}-${item.anio}-${item.datos_id}`} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                          <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{item.universidad_nombre}</td>
                          <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{item.anio}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">{item.estado}</span>
                          </td>
                          <td className="px-3 py-3 text-slate-500 dark:text-slate-400">{item.paso_actual || 1}</td>
                          <td className="px-3 py-3 text-slate-500 dark:text-slate-400">
                            {item.updated_at ? new Intl.DateTimeFormat('es-EC', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(item.updated_at)) : '—'}
                          </td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => abrirFormularioEnviado(item)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
                            >
                              <span className="material-icons-round text-sm">edit_note</span>
                              Continuar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Formularios enviados</h3>
                  <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    {formulariosEnviados.length}
                  </span>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                        <th className="px-3 py-3 text-left font-semibold">Institución</th>
                        <th className="px-3 py-3 text-left font-semibold">Año</th>
                        <th className="px-3 py-3 text-left font-semibold">Estado</th>
                        <th className="px-3 py-3 text-left font-semibold">Última actualización</th>
                        <th className="px-3 py-3 text-left font-semibold">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formulariosEnviados.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-8 text-center text-slate-500 dark:text-slate-400">Aún no hay formularios enviados para mostrar.</td>
                        </tr>
                      ) : formulariosEnviados.map((item) => (
                        <tr key={`${item.universidad_id}-${item.anio}-${item.datos_id}`} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                          <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{item.universidad_nombre}</td>
                          <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{item.anio}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">{item.estado}</span>
                          </td>
                          <td className="px-3 py-3 text-slate-500 dark:text-slate-400">
                            {item.updated_at ? new Intl.DateTimeFormat('es-EC', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(item.updated_at)) : '—'}
                          </td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => abrirFormularioEnviado(item)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
                            >
                              <span className="material-icons-round text-sm">visibility</span>
                              Ver enviado
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </SCard>
        ) : (
          <>
            {isReadOnlySubmitted && (
              <div className="mb-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-sm flex items-start gap-2">
                <span className="material-icons-round text-base flex-shrink-0 mt-0.5">lock</span>
                <span>Este formulario ya fue enviado para el año {anio}. Puede revisarlo, pero solo un SuperAdmin puede modificarlo.</span>
              </div>
            )}

            <div className="mb-4 flex justify-between">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <span className="material-icons-round text-base">arrow_back</span>
                Volver al panel
              </button>
            </div>

            {/* Two-column layout */}
            <div className="grid lg:grid-cols-[260px,1fr] gap-5 items-start">

              {/* Sidebar */}
              <div className="sticky top-6">
                <Stepper />
                <div className={`mt-3 rounded-xl border px-4 py-3 text-xs ${draftStatusUi.tone}`}>
                  <div className="flex items-center gap-2">
                    <span className={`material-icons-round text-sm ${draftStatus === 'saving' ? 'animate-spin' : ''}`}>{draftStatusUi.icon}</span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${draftStatusUi.badge}`}>
                      {draftStatusUi.label}
                    </span>
                  </div>
                  <p className="mt-2 leading-relaxed">{draftStatusUi.detail}</p>
                </div>
                {estado?.datos && (
                  <div className="mt-3 px-4 py-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
                    Estado: <strong className="text-slate-700 dark:text-slate-200">{estado.datos.estado}</strong>
                    {' · '}paso guardado: <strong className="text-slate-700 dark:text-slate-200">{estado.datos.paso_actual}</strong>
                  </div>
                )}
              </div>

              {/* Main content */}
              <div>
                {loading && (
                  <SCard className="p-8 text-center">
                    <span className="text-slate-400 text-sm animate-pulse">Cargando formulario…</span>
                  </SCard>
                )}

                {!loading && (
                  <>
                    {/* ── Step 0: Contexto ────────────────────────────────────── */}
                    {step === 0 && (
                      <div className="space-y-4">

                        {/* Año de referencia */}
                        <SCard className="p-5">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="material-icons-round text-[#2D5BA6] text-lg">info</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Año de referencia del informe</span>
                            <input
                              type="number"
                              min={2000}
                              max={2100}
                              value={anio}
                              disabled
                              onChange={(e) => setAnio(e.target.value)}
                              className={`${inputCls} w-32`}
                            />
                            <p className="text-xs text-slate-400 dark:text-slate-500">El año se define desde el panel de formularios y luego queda bloqueado.</p>
                          </div>
                        </SCard>

                        {/* P1 — IES */}
                        <SCard className="p-6">
                          <div className="flex items-center gap-3 mb-5">
                            <PBadge code="P1" display="1" />
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              Universidad o Institución de Educación Superior
                            </h3>
                            <span className="text-red-400 text-sm">*</span>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Seleccione la universidad</label>
                            <select
                              value={universidadId}
                              onChange={(e) => setUniversidadId(e.target.value)}
                              disabled
                              className={inputCls}
                            >
                              <option value="">Seleccione una universidad…</option>
                              {universidadesDisponibles.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name}{u.short_name ? ` (${u.short_name})` : ''}
                                </option>
                              ))}
                            </select>
                            {!institutionAccess.allAccess && institutionAccess.universidad && (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">
                                Institución bloqueada por tu correo institucional: {institutionAccess.universidad.siglas || institutionAccess.universidad.short_name}.
                              </p>
                            )}
                            {!institutionAccess.allAccess && !institutionAccess.universidad && (
                              <p className="text-xs text-red-500 dark:text-red-400 mt-1.5">
                                {institutionAccess.detail || 'No se pudo detectar tu universidad autorizada.'}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5">La institución se selecciona desde el panel de formularios y aquí queda solo de consulta.</p>
                          </div>
                        </SCard>

                        {/* P2 — Ciudad */}
                        <SCard className="p-6">
                          <div className="flex items-center gap-3 mb-5">
                            <PBadge code="P2" display="2" />
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ciudad sede principal</h3>
                            {universidadId && (
                              <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                                Oficial
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="relative flex-1 max-w-xs">
                              <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-lg">location_on</span>
                              <input
                                type="text"
                                readOnly
                                value={sugerencias?.ciudad || ''}
                                placeholder={universidadId ? 'Cargando…' : 'Seleccione primero una institución'}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-sm text-slate-600 dark:text-slate-300 outline-none cursor-default"
                              />
                            </div>
                            {sugerencias?.provincia && (
                              <span className="text-xs text-slate-400 dark:text-slate-500">{sugerencias.provincia}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">Ciudad auto-poblada desde el registro de la IES en SIGC.</p>
                        </SCard>

                        <SCard className="p-6">
                          <div className="flex items-center gap-3 mb-5">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-[#2D5BA6] dark:text-blue-400 font-bold text-[10px] flex-shrink-0 leading-none">
                              RP
                            </span>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Persona responsable de proporcionar la información</h3>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Complete los datos de contacto de la persona responsable del formulario institucional.</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Nombre completo</label>
                              <input
                                type="text"
                                value={extra.responsable_nombre || ''}
                                onChange={(e) => setExtra((ex) => ({ ...ex, responsable_nombre: e.target.value }))}
                                className={inputCls}
                                placeholder="Nombre del responsable"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Cargo</label>
                              <input
                                type="text"
                                value={extra.responsable_cargo || ''}
                                onChange={(e) => setExtra((ex) => ({ ...ex, responsable_cargo: e.target.value }))}
                                className={inputCls}
                                placeholder="Cargo institucional"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Correo</label>
                              <input
                                type="email"
                                value={extra.responsable_correo || ''}
                                onChange={(e) => setExtra((ex) => ({ ...ex, responsable_correo: e.target.value }))}
                                className={inputCls}
                                placeholder="correo@universidad.edu.ec"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Teléfono</label>
                              <input
                                type="text"
                                value={extra.responsable_telefono || ''}
                                onChange={(e) => setExtra((ex) => ({ ...ex, responsable_telefono: sanitizePhoneInput(e.target.value) }))}
                                inputMode="numeric"
                                pattern="09[0-9]{8}"
                                maxLength={MAX_PHONE_LENGTH}
                                className={`${inputCls} ${extra.responsable_telefono && !isValidEcuadorMobilePhone(extra.responsable_telefono) ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-100 dark:border-amber-500/70' : ''}`}
                                placeholder="Ej: 0999999999"
                              />
                              {extra.responsable_telefono && !isValidEcuadorMobilePhone(extra.responsable_telefono) && (
                                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                  Debe tener 10 dígitos y empezar con 09.
                                </p>
                              )}
                            </div>
                          </div>
                        </SCard>

                        {/* Datos de referencia SIGC */}
                        {sugerencias && (
                          <SCard className="p-5">
                            <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 dark:border-slate-700 sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300">
                                  <span className="material-icons-round text-lg">insights</span>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[#2D5BA6]">Datos de referencia en SIGC</p>
                                  <p className="mt-1 max-w-2xl text-xs leading-6 text-slate-500 dark:text-slate-400">
                                    Estas referencias usan la información ya disponible en SIGC para ayudarte a validar y completar el formulario con más contexto.
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={applySugerencias}
                                className="flex items-center gap-1.5 text-xs font-medium text-[#2D5BA6] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors"
                              >
                                <span className="material-icons-round text-sm">auto_fix_high</span>
                                Aplicar sugerencias
                              </button>
                            </div>
                            <div className="mt-5 grid gap-4">
                              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                {[
                                  ['Líneas activas', sugerencias.num_lineas_investigacion_activas, 'route'],
                                  ['Grupos de investigación', sugerencias.num_grupos_investigacion, 'groups'],
                                  ['Centros e institutos', sugerencias.num_centros_investigacion, 'business'],
                                  ['Proyectos activos', sugerencias.num_proyectos_investigacion, 'schema'],
                                ].map(([label, val, icon]) => (
                                  <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4 dark:border-slate-700 dark:bg-slate-800/60">
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{label}</p>
                                        <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">{val ?? '—'}</p>
                                      </div>
                                      <span className="material-icons-round text-[#2D5BA6] dark:text-blue-300">{icon}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {/* P5 y P6 */}
                              {false && ([
                                ['Líneas activas', sugerencias.num_lineas_investigacion_activas],
                                ['Grupos', sugerencias.num_grupos_investigacion],
                                ['Centros', sugerencias.num_centros_investigacion],
                                ['Proyectos', sugerencias.num_proyectos_investigacion],
                                ['Investigadores', sugerencias.num_investigadores_afiliados],
                                ['Publicaciones IES', sugerencias.num_publicaciones_distintas_ies],
                                [`Investigadores H`, sugerencias.num_investigadores_hombres],
                                [`Investigadores M`, sugerencias.num_investigadores_mujeres],
                              ].map(([label, val]) => (
                                <div key={label} className="rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-700 px-3 py-2">
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
                                  <p className="text-base font-bold text-slate-800 dark:text-slate-100 mt-0.5">{val ?? '—'}</p>
                                </div>
                              )))}
                              <ConditionalPersonalField
                                code="P5"
                                displayCode="5"
                                label="¿Cuenta la IES con personal académico contratado exclusivamente para investigación?"
                                gate={tienePersonalInvestigacion}
                                onGateYes={() => setTienePersonalInvestigacion(true)}
                                onGateNo={() => {
                                  setTienePersonalInvestigacion(false);
                                  setNums((p) => ({ ...p, num_personal_investigacion_hombres: '', num_personal_investigacion_mujeres: '' }));
                                }}
                                prefix="num_personal_investigacion"
                                noAplicaText="No aplica para la institución."
                              />
                              <ConditionalPersonalField
                                code="P6"
                                displayCode="6"
                                label="¿Cuenta la IES con personal de apoyo académico, técnico docente o técnico de investigación?"
                                gate={tienePersonalApoyo}
                                onGateYes={() => setTienePersonalApoyo(true)}
                                onGateNo={() => {
                                  setTienePersonalApoyo(false);
                                  setNums((p) => ({ ...p, num_personal_apoyo_hombres: '', num_personal_apoyo_mujeres: '' }));
                                }}
                                prefix="num_personal_apoyo"
                                noAplicaText="No aplica para la institución."
                              />
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-2">
                                Publicaciones {matrixYears[0]}–{matrixYears[4]}
                              </p>
                              </div>
                              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {['q1', 'q2', 'q3', 'q4'].map((q) => (
                                  <div key={q} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-4 text-center dark:border-slate-700 dark:bg-slate-900/50">
                                    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{q.toUpperCase()}</p>
                                    <p className="mt-2 text-xl font-bold text-slate-800 dark:text-slate-100">{sugerencias[`${q}_5_anios`] || 0}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </SCard>
                        )}
                      </div>
                    )}

                    {/* ── Step 1: Personal académico ───────────────────────── */}
                    {step === 1 && (() => {
                      return (
                        <div className="space-y-4">

                          {/* Card principal Personal Académico */}
                          <SCard className="p-6">
                            <div className="mb-5">
                              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                <span className="material-icons-round text-[#2D5BA6] text-lg">people</span>
                                Personal Académico
                              </h3>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Información sobre el recurso humano académico. Los totales se calculan automáticamente.</p>
                            </div>
                            <div className="space-y-4">
                              <PersonalField code="P3" displayCode="3" label="Total personal académico" prefix="num_personal_academico" />
                              <PersonalField code="P4" displayCode="4" label="Personal académico con Doctorado (PhD)" prefix="num_personal_phd" />
                              <ConditionalPersonalField
                                code="P5"
                                displayCode="5"
                                label="¿Cuenta la IES con personal académico contratado exclusivamente para investigación?"
                                gate={tienePersonalInvestigacion}
                                onGateYes={() => setTienePersonalInvestigacion(true)}
                                onGateNo={() => {
                                  setTienePersonalInvestigacion(false);
                                  setNums((p) => ({ ...p, num_personal_investigacion_hombres: '', num_personal_investigacion_mujeres: '' }));
                                }}
                                prefix="num_personal_investigacion"
                                noAplicaText="No aplica para la institución."
                              />
                              <ConditionalPersonalField
                                code="P6"
                                displayCode="6"
                                label="¿Cuenta la IES con personal de apoyo académico, técnico docente o técnico de investigación?"
                                gate={tienePersonalApoyo}
                                onGateYes={() => setTienePersonalApoyo(true)}
                                onGateNo={() => {
                                  setTienePersonalApoyo(false);
                                  setNums((p) => ({ ...p, num_personal_apoyo_hombres: '', num_personal_apoyo_mujeres: '' }));
                                }}
                                prefix="num_personal_apoyo"
                                noAplicaText="No aplica para la institución."
                              />
                              {false && ([
                                [
                                  'P5',
                                  '5',
                                  '¿Cuenta la IES con personal académico contratado exclusivamente para investigación?',
                                  tienePersonalInvestigacion,
                                  () => setTienePersonalInvestigacion(true),
                                  () => {
                                    setTienePersonalInvestigacion(false);
                                    setNums((p) => ({ ...p, num_personal_investigacion_hombres: '', num_personal_investigacion_mujeres: '' }));
                                  },
                                ],
                                [
                                  'P6',
                                  '6',
                                  '¿Cuenta la IES con personal de apoyo académico, técnico docente o técnico de investigación?',
                                  tienePersonalApoyo,
                                  () => setTienePersonalApoyo(true),
                                  () => {
                                    setTienePersonalApoyo(false);
                                    setNums((p) => ({ ...p, num_personal_apoyo_hombres: '', num_personal_apoyo_mujeres: '' }));
                                  },
                                ],
                              ].map(([code, displayCode, label, gate, onYes, onNo]) => (
                                <div key={code} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <SmallPBadge code={code} display={displayCode} />
                                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={onYes}
                                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                    >
                                      Sí
                                    </button>
                                    <button
                                      type="button"
                                      onClick={onNo}
                                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                    >
                                      No
                                    </button>
                                  </div>
                                </div>
                              )))}
                            </div>
                          </SCard>

                        </div>
                      );
                    })()}

                    {/* ── Step 2: Presupuesto y vínculos ──────────────────── */}
                    {step === 2 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-1">
                          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">{STEPS[2].title}</h2>
                          <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">P7</span>
                        </div>

                        <SCard className="p-6">
                          <div className="mb-6">
                            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">hub</span>
                              Redes Académicas
                            </h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Indique las redes académicas y de investigación con las que colabora la institución.</p>
                          </div>

                          <div className="space-y-4">
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <PBadge code="P10" display="7" />
                                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                  Indique las redes académicas y de investigación con las que colabora la IES
                                </h4>
                                <span className="text-red-400 text-sm">*</span>
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Cantidad de redes</label>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="number" min={0} max={50} value={numRedes}
                                    onChange={(e) => {
                                      const sanitized = sanitizeIntegerInput(e.target.value, { max: 50, maxDigits: 2 });
                                      const n = Number.parseInt(sanitized || '0', 10) || 0;
                                      setNumRedes(n);
                                      setRedesList(ensureListLen(redesList, n));
                                    }}
                                    {...numericInputProps(50)}
                                    className={`${inputCls} w-28`}
                                  />
                                  <span className="text-xs text-slate-400 dark:text-slate-500">Ingrese el número de redes académicas</span>
                                </div>
                              </div>
                              {numRedes > 0 && (
                                <div className="space-y-2">
                                  <label className="text-xs text-slate-500 dark:text-slate-400 font-medium block">Nombres de las redes académicas</label>
                                  {ensureListLen(redesList, numRedes).map((val, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-[#2D5BA6] dark:text-blue-400 w-24 flex-shrink-0">Nombre Red {idx + 1}</span>
                                      <input type="text" placeholder="Ej: CEDIA, REDU, etc." value={val}
                                        onChange={(e) => { const next = [...ensureListLen(redesList, numRedes)]; next[idx] = e.target.value; setRedesList(next); }}
                                        className={inputCls}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </SCard>
                      </div>
                    )}

                    {/* ── Step 3: Estructura investigativa ────────────────── */}
                    {step === 3 && (() => {
                      const gruposRows = Array.from({ length: gruposN }, (_, idx) => normalizeGrupoDetalleRow((extra.grupos_detalle || [])[idx]));
                      const centrosRowsNormalized = Array.from({ length: centrosN }, (_, idx) => normalizeCentroDetalleRow((extra.centros_detalle || [])[idx]));
                      const lineasOptions = lineasList
                        .map((linea) => String(linea || '').trim())
                        .filter(Boolean)
                        .map((linea) => ({ value: linea, label: linea }));
                      const cineOptions = ((cineCampos.length
                        ? cineCampos.filter((campo) => Number(campo.campo_id) !== 0)
                        : Array.from({ length: 10 }, (_, i) => ({ campo_id: i + 1, campo_amplio: `Campo ${i + 1}` }))))
                        .map((campo) => ({ value: String(campo.campo_id), label: `${campo.campo_id} · ${campo.campo_amplio}` }));

                      return (
                        <div className="space-y-4">
                          {/* Card Capacidades Institucionales */}
                          <SCard className="p-6">
                            <div className="mb-5">
                              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                <span className="material-icons-round text-[#2D5BA6] text-lg">account_tree</span>
                                Capacidades Institucionales
                              </h3>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Estructura organizativa de investigación.</p>
                            </div>

                            <div className="space-y-6 mb-6">
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <SmallPBadge code="P13" display="8" />
                                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Número de líneas de investigación activas</span>
                                    </div>
                                    <p className="text-2xl font-bold text-[#2D5BA6] dark:text-blue-400">{lineasN}</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500">líneas registradas</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={addLineaRow}
                                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#244d8f]"
                                  >
                                    <span className="material-icons-round text-sm">add</span>
                                    Añadir
                                  </button>
                                </div>
                              </div>

                              {/* P14 — Lista líneas */}
                              {lineasN > 0 && (
                                <div className="mb-6">
                                  <div className="flex items-center gap-2 mb-3">
                                    <SmallPBadge code="P14" display="9" />
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Describa las líneas de investigación activas</span>
                                  </div>
                                  <div className="space-y-2">
                                    {lineasList.map((val, idx) => (
                                      <div key={idx} className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-[#2D5BA6] dark:text-blue-400 w-14 flex-shrink-0">Línea {idx + 1}</span>
                                        <input type="text" placeholder="Nombre o descripción de la línea..."
                                          value={val}
                                          onChange={(e) => { const next = [...lineasList]; next[idx] = e.target.value; setExtra((ex) => ({ ...ex, lineas_detalle: next })); }}
                                          className={inputCls}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => removeLineaRow(idx)}
                                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] font-semibold text-red-600 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300"
                                        >
                                          <span className="material-icons-round text-sm">delete</span>
                                          Quitar
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <SmallPBadge code="P15" display="10" />
                                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Número de grupos de investigación</span>
                                    </div>
                                    <p className="text-2xl font-bold text-[#2D5BA6] dark:text-blue-400">{gruposN}</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500">grupos registrados</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={addGrupoRow}
                                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#244d8f]"
                                  >
                                    <span className="material-icons-round text-sm">add</span>
                                    Añadir
                                  </button>
                                </div>
                              </div>

                              {gruposN > 0 && (
                                <div className="mb-6">
                                  <SCard className="p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                      <SmallPBadge code="P16" display="11" />
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Detalle de grupos de investigación del año de referencia</p>
                                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Seleccione uno o más campos CINE y una o más líneas de investigación declaradas.</p>
                                      </div>
                                    </div>
                                    <div className="space-y-4">
                                      {gruposRows.map((row, idx) => (
                                        <details key={idx} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/40 group" open>
                                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
                                            <div className="min-w-0">
                                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Grupo {idx + 1}</p>
                                              <p className="truncate text-xs text-slate-400 dark:text-slate-500">{row.nombre || 'Sin nombre todavía'}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeGrupoRow(idx); }}
                                                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300"
                                              >
                                                <span className="material-icons-round text-sm">delete</span>
                                                Quitar
                                              </button>
                                              <span className="material-icons-round text-slate-400 transition-transform group-open:rotate-180">expand_more</span>
                                            </div>
                                          </summary>
                                          <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs font-mono text-[#2D5BA6] w-16">Nombre</span>
                                              <input
                                                type="text"
                                                value={row.nombre}
                                                onChange={(e) => updateGrupoRow(idx, (prev) => ({ ...prev, nombre: e.target.value }))}
                                                className={inputCls}
                                                placeholder="Nombre del grupo"
                                              />
                                            </div>
                                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block font-medium">Campos CINE</label>
                                                <InlineMultiSelect
                                                  options={cineOptions}
                                                  selected={row.campos_cine}
                                                  onToggle={(value) => updateGrupoRow(idx, (prev) => ({
                                                    ...prev,
                                                    campos_cine: prev.campos_cine.includes(value)
                                                      ? prev.campos_cine.filter((item) => item !== value)
                                                      : [...prev.campos_cine, value],
                                                  }))}
                                                  addLabel="Añadir campo"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block font-medium">Líneas de investigación</label>
                                                <InlineMultiSelect
                                                  options={lineasOptions}
                                                  selected={row.lineas}
                                                  onToggle={(value) => updateGrupoRow(idx, (prev) => ({
                                                    ...prev,
                                                    lineas: prev.lineas.includes(value)
                                                      ? prev.lineas.filter((item) => item !== value)
                                                      : [...prev.lineas, value],
                                                  }))}
                                                  placeholder={lineasOptions.length ? 'Sin selección' : 'Primero complete P14'}
                                                  addLabel="Añadir línea"
                                                />
                                              </div>
                                            </div>
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres personal académico</label>
                                                <input type="number" min={0} value={row.hombres} onChange={(e) => updateGrupoRow(idx, (prev) => ({ ...prev, hombres: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres personal académico</label>
                                                <input type="number" min={0} value={row.mujeres} onChange={(e) => updateGrupoRow(idx, (prev) => ({ ...prev, mujeres: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Antigüedad</label>
                                                <input type="number" min={0} value={row.antiguedad_anios} onChange={(e) => updateGrupoRow(idx, (prev) => ({ ...prev, antiguedad_anios: sanitizeIntegerInput(e.target.value, { max: 120, maxDigits: 3 }) }))} {...numericInputProps(120)} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Producción científica</label>
                                                <input type="number" min={0} value={row.produccion_cientifica} onChange={(e) => updateGrupoRow(idx, (prev) => ({ ...prev, produccion_cientifica: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                            </div>
                                          </div>
                                        </details>
                                      ))}
                                    </div>
                                  </SCard>
                                </div>
                              )}

                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-2">
                                    <SmallPBadge code="P17" display="12" className="mt-0.5" />
                                    <div>
                                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿Tiene centros o institutos de investigación?</p>
                                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Si responde sí, indique cuántos y complete el detalle siguiente.</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button type="button" onClick={() => setExtra((ex) => ({ ...ex, tiene_centros: true }))}
                                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${extra.tiene_centros === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}>
                                      Sí
                                    </button>
                                    <button type="button" onClick={() => setExtra((ex) => ({ ...ex, tiene_centros: false, num_centros_declarado: 0, centros_detalle: [] }))}
                                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${extra.tiene_centros === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}>
                                      No
                                    </button>
                                  </div>
                                  {extra.tiene_centros === true && (
                                    <div className="flex items-center justify-between gap-3">
                                      <div>
                                        <p className="text-2xl font-bold text-[#2D5BA6] dark:text-blue-400">{centrosN}</p>
                                        <p className="text-xs text-slate-400 dark:text-slate-500">centros/institutos registrados</p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={addCentroRow}
                                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#244d8f]"
                                      >
                                        <span className="material-icons-round text-sm">add</span>
                                        Añadir
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {extra.tiene_centros === true && centrosN > 0 && (
                                <div>
                                  <SCard className="p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                      <SmallPBadge code="P18" display="13" />
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Detalle de centros e institutos de investigación</p>
                                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Seleccione los campos CINE y las líneas asociadas.</p>
                                      </div>
                                    </div>
                                    <div className="space-y-4">
                                      {centrosRowsNormalized.map((row, idx) => (
                                        <details key={idx} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/40 group" open>
                                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
                                            <div className="min-w-0">
                                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Centro / Instituto {idx + 1}</p>
                                              <p className="truncate text-xs text-slate-400 dark:text-slate-500">{row.nombre || 'Sin nombre todavía'}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeCentroRow(idx); }}
                                                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300"
                                              >
                                                <span className="material-icons-round text-sm">delete</span>
                                                Quitar
                                              </button>
                                              <span className="material-icons-round text-slate-400 transition-transform group-open:rotate-180">expand_more</span>
                                            </div>
                                          </summary>
                                          <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4">
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                              <div className="lg:col-span-2">
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Nombre</label>
                                                <input type="text" value={row.nombre} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, nombre: e.target.value }))} className={inputCls} placeholder="Nombre del centro o instituto" />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Tipo</label>
                                                <select value={row.tipo} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, tipo: e.target.value }))} className={inputCls}>
                                                  <option value="">Seleccione</option>
                                                  <option value="centro">Centro</option>
                                                  <option value="instituto">Instituto</option>
                                                </select>
                                              </div>
                                            </div>
                                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block font-medium">Campos CINE</label>
                                                <InlineMultiSelect
                                                  options={cineOptions}
                                                  selected={row.campos_cine}
                                                  onToggle={(value) => updateCentroRow(idx, (prev) => ({
                                                    ...prev,
                                                    campos_cine: prev.campos_cine.includes(value)
                                                      ? prev.campos_cine.filter((item) => item !== value)
                                                      : [...prev.campos_cine, value],
                                                  }))}
                                                  addLabel="Añadir campo"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block font-medium">Líneas asociadas</label>
                                                <InlineMultiSelect
                                                  options={lineasOptions}
                                                  selected={row.lineas}
                                                  onToggle={(value) => updateCentroRow(idx, (prev) => ({
                                                    ...prev,
                                                    lineas: prev.lineas.includes(value)
                                                      ? prev.lineas.filter((item) => item !== value)
                                                      : [...prev.lineas, value],
                                                  }))}
                                                  placeholder={lineasOptions.length ? 'Sin selección' : 'Primero complete P14'}
                                                  addLabel="Añadir línea"
                                                />
                                              </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres personal académico</label>
                                                <input type="number" min={0} value={row.hombres} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, hombres: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres personal académico</label>
                                                <input type="number" min={0} value={row.mujeres} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, mujeres: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Personal apoyo</label>
                                                <input type="number" min={0} value={row.personal_apoyo} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, personal_apoyo: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Fondos internos USD</label>
                                                <input type="number" min={0} value={row.fondos_internos_usd} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, fondos_internos_usd: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Fondos externos USD</label>
                                                <input type="number" min={0} value={row.fondos_externos_usd} onChange={(e) => updateCentroRow(idx, (prev) => ({ ...prev, fondos_externos_usd: sanitizeIntegerInput(e.target.value) }))} {...numericInputProps()} className={inputCls} />
                                              </div>
                                            </div>
                                          </div>
                                        </details>
                                      ))}
                                    </div>
                                  </SCard>
                                </div>
                              )}

                              {extra.tiene_centros === false && (
                                <p className="text-sm text-slate-400 dark:text-slate-500 italic">No se registrarán centros o institutos de investigación.</p>
                              )}
                            </div>
                          </SCard>

                        </div>
                      );
                    })()}

                    {/* ── Step 4: Matrices CINE ────────────────────────────── */}
                    {step === 4 && (
                      <div className="space-y-5">
                        <div className="flex items-center gap-2 mb-1">
                          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">{STEPS[4].title}</h2>
                          <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">P14 – P21</span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2 mb-1">
                          Proyectos por campo amplio CINE para los últimos 5 años ({matrixYears[0]}–{matrixYears[4]}) y personal en proyectos del año de referencia {referenceYear[0]}.
                        </p>

                        {/* P21 / P22 — Docentes de tiempo completo (TC) en investigación */}
                        {false && (
                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-4">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">people_outline</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Docentes de tiempo completo (TC) en investigación (P21 / P22)</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Primero indique si aplica; si aplica, registre hombres, mujeres y total del año de referencia.</p>
                              </div>
                            </div>
                            <div className="space-y-4">
                              {[
                                ['P21', 'P18', 'Docentes tiempo completo titulares que participan en proyectos de investigación', 'docentes_tc_titulares_inv', 'p21_aplica'],
                                ['P22', 'P19', 'Docentes tiempo completo no titulares que participan en proyectos de investigación', 'docentes_tc_no_titulares_inv', 'p22_aplica'],
                              ].map(([code, displayCode, label, key, gateField]) => {
                                const yy = referenceYear[0];
                                const h = Number(matricesView[key]?.[yy]?.h) || 0;
                                const m = Number(matricesView[key]?.[yy]?.m) || 0;
                                const gate = extra[gateField];
                                return (
                                  <div key={key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <SmallPBadge code={code} display={displayCode} />
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setExtra((ex) => ({ ...ex, [gateField]: true }))}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          Sí
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setExtra((ex) => ({ ...ex, [gateField]: false }));
                                            patchMatrixHm(key, yy, 'h', '');
                                            patchMatrixHm(key, yy, 'm', '');
                                          }}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          No
                                        </button>
                                      </div>
                                    </div>
                                    {gate === true && (
                                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                        <div className="sm:col-span-1 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-4 py-3 bg-white/70 dark:bg-slate-800/50">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Año de referencia</p>
                                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{yy}</p>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Hombres</label>
                                          <input type="number" min={0} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm focus:border-[#2D5BA6] outline-none" value={matricesView[key]?.[yy]?.h ?? ''} onChange={(e) => patchMatrixHm(key, yy, 'h', e.target.value)} {...numericInputProps()} />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Mujeres</label>
                                          <input type="number" min={0} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm focus:border-[#2D5BA6] outline-none" value={matricesView[key]?.[yy]?.m ?? ''} onChange={(e) => patchMatrixHm(key, yy, 'm', e.target.value)} {...numericInputProps()} />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Total</label>
                                          <input type="number" readOnly tabIndex={-1} className="w-full px-3 py-2.5 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm outline-none cursor-default" value={(h + m) || ''} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </SCard>
                        )}

                        {/* P23–P26 Proyectos por campo CINE */}
                        {[
                          ['P23', 'P14', 'Número de proyectos con financiamiento interno por campo amplio (CINE)', 'proyectos_interno_cine'],
                          ['P24', 'P15', 'Número de proyectos sin financiamiento por campo amplio (CINE)', 'proyectos_sin_fin_cine'],
                          ['P25', 'P16', 'Número de proyectos con financiamiento externo por campo amplio (CINE)', 'proyectos_externo_cine'],
                          ['P26', 'P17', 'Número de proyectos en colaboración con redes por campo amplio (CINE)', 'proyectos_redes_cine'],
                        ].map(([code, displayCode, label, mk]) => (
                          <SCard key={mk} className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <SmallPBadge code={code} display={displayCode} />
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</p>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 max-h-72 overflow-y-auto">
                              <table className="min-w-full text-xs">
                                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-700 z-10">
                                  <tr>
                                    <th className="text-left p-2.5 font-semibold text-slate-600 dark:text-slate-300 min-w-[140px]">Campo CINE</th>
                                    {matrixYears.map((yy) => (
                                      <th key={yy} className="p-2.5 font-semibold text-center border-l border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 w-16">{yy}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {((cineCampos.length
                                    ? cineCampos.filter((c) => Number(c.campo_id) !== 0)
                                    : Array.from({ length: 10 }, (_, i) => ({ campo_id: i + 1, campo_amplio: `Campo ${i + 1}` })))).map((c) => (
                                    <tr key={c.campo_id} className="border-t border-slate-100 dark:border-slate-700">
                                      <td className="p-2 text-slate-600 dark:text-slate-300">
                                        <span className="font-mono text-slate-400 dark:text-slate-500 mr-1">{c.campo_id}</span>
                                        {c.campo_amplio}
                                      </td>
                                      {matrixYears.map((yy) => (
                                        <td key={yy} className="p-1 border-l border-slate-100 dark:border-slate-700">
                                          <input type="number" min={0} className="w-full px-1.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-xs focus:border-[#2D5BA6] outline-none" value={matricesView[mk]?.[yy]?.[String(c.campo_id)] ?? ''} onChange={(e) => patchMatrixCine(mk, yy, c.campo_id, e.target.value)} {...numericInputProps()} />
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </SCard>
                        ))}

                        {/* Complementarios */}
                        {false && (
                          <SCard className="p-5">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4">Participación estudiantil en proyectos</p>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {[
                                ['P28', 'P20', 'Estudiantes de pregrado que participan en proyectos de investigación', 'num_estudiantes_pregrado_inv', 'p28_aplica'],
                                ['P29', 'P21', 'Estudiantes de posgrado que participan en proyectos de investigación', 'num_estudiantes_posgrado_inv', 'p29_aplica'],
                              ].map(([code, displayCode, label, prefix, gateField]) => {
                                const h = Number(nums[`${prefix}_hombres`]) || 0;
                                const m = Number(nums[`${prefix}_mujeres`]) || 0;
                                const gate = extra[gateField];
                                return (
                                  <div key={code} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <SmallPBadge code={code} display={displayCode} />
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setExtra((ex) => ({ ...ex, [gateField]: true }))}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          Sí
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setExtra((ex) => ({ ...ex, [gateField]: false }));
                                            setNums((prev) => ({ ...prev, [`${prefix}_hombres`]: '', [`${prefix}_mujeres`]: '' }));
                                          }}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          No
                                        </button>
                                      </div>
                                    </div>
                                    {gate === true ? (
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres</label>
                                          <input type="number" min={0} value={nums[`${prefix}_hombres`] ?? ''} onChange={(e) => setNumField(`${prefix}_hombres`, e.target.value)} {...numericInputProps()} className={inputCls} />
                                        </div>
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres</label>
                                          <input type="number" min={0} value={nums[`${prefix}_mujeres`] ?? ''} onChange={(e) => setNumField(`${prefix}_mujeres`, e.target.value)} {...numericInputProps()} className={inputCls} />
                                        </div>
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Total</label>
                                          <input type="number" readOnly tabIndex={-1} value={(h + m) || ''} className={inputDisabledCls} />
                                        </div>
                                      </div>
                                    ) : gate === false ? (
                                      <p className="text-sm text-slate-500 italic">No aplica para el año de referencia.</p>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </SCard>
                        )}

                        
                        <SCard className="p-5">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="material-icons-round text-[#2D5BA6] text-lg">upload_file</span>
                            <div>
                              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Carga estructurada de proyectos</h3>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Importe la plantilla CSV para guardar el detalle de proyectos dentro del sistema. Dropbox queda disponible para respaldos.</p>
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/40 p-4 space-y-4">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Importacion desde plantilla oficial</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">La carga quedara guardada en el borrador y luego se sincronizara con la base al guardar o enviar el formulario. Formatos opcionales: `ODS` como `3|4|9` e `INVESTIGADORES_IDS` como `12|35|44`, con `INVESTIGADORES_ROLES` en el mismo orden.</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a
                                  href={PROYECTOS_TEMPLATE_URL}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={actionLinkSecondaryCls}
                                >
                                  <span className="material-icons-round text-sm">download</span>
                                  Descargar plantilla
                                </a>
                                <button
                                  type="button"
                                  onClick={() => proyectosFileInputRef.current?.click()}
                                  className={actionLinkPrimaryCls}
                                >
                                  <span className="material-icons-round text-sm">upload_file</span>
                                  Cargar CSV
                                </button>
                                {proyectosDetalle.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={clearProyectoImport}
                                    className={actionLinkSecondaryCls}
                                  >
                                    <span className="material-icons-round text-sm">delete</span>
                                    Limpiar carga
                                  </button>
                                )}
                                <a
                                  href={PROYECTOS_DROPBOX_URL}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={actionLinkSecondaryCls}
                                >
                                  <span className="material-icons-round text-sm">cloud_upload</span>
                                  Subir respaldo a Dropbox
                                </a>
                              </div>
                              <input
                                ref={proyectosFileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={handleProyectoCsvSelected}
                                className="hidden"
                              />
                            </div>
                            <div className="grid gap-3 md:grid-cols-4">
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Proyectos importados</p>
                                <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{proyectosDetalle.length}</p>
                              </div>
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Persistencia</p>
                                <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">Se vincula al formulario del ano activo</p>
                              </div>
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Vista rapida</p>
                                <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">Codigo, titulo, tipo, estado, linea y red</p>
                              </div>
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Enlaces extra</p>
                                <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">
                                  {projectImportSummary.investigatorsLinked} investigadores y {projectImportSummary.odsLinked} ODS
                                </p>
                              </div>
                            </div>
                            {projectImportSummary.warnings.length > 0 && (
                              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                <p className="font-semibold">Advertencias de importacion</p>
                                <div className="mt-2 space-y-1">
                                  {projectImportSummary.warnings.slice(0, 8).map((warning, index) => (
                                    <p key={`${warning}-${index}`}>- {warning}</p>
                                  ))}
                                  {projectImportSummary.warnings.length > 8 && (
                                    <p>Hay {projectImportSummary.warnings.length - 8} advertencia(s) adicionales en la carga.</p>
                                  )}
                                </div>
                              </div>
                            )}
                            {proyectosDetalle.length > 0 ? (
                              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                                <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Vista previa de proyectos cargados</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Se muestran los primeros {proyectosPreview.length} registros.</p>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-xs">
                                    <thead className="bg-slate-50 dark:bg-slate-700/60">
                                      <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Codigo</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Titulo</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Tipo</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Estado</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Linea</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Red</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Inv.</th>
                                        <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">ODS</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {proyectosPreview.map((row, idx) => (
                                        <tr key={`${row.codigo || row.nombre || 'proyecto'}-${idx}`} className="border-t border-slate-100 dark:border-slate-700">
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.codigo || '-'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300 min-w-[260px]">{row.nombre || row.titulo || 'Sin titulo'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.tipo_proyecto || '-'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.estado || '-'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.linea_investigacion || row.lineas || '-'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.red || '-'}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{Array.isArray(row.investigadores) ? row.investigadores.length : 0}</td>
                                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                            {Array.isArray(row.objetivos_ods) && row.objetivos_ods.length
                                              ? row.objetivos_ods.join(', ')
                                              : Array.isArray(row.ods) && row.ods.length
                                                ? row.ods.join(', ')
                                                : '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {proyectosDetalle.length > proyectosPreview.length && (
                                  <div className="border-t border-slate-100 dark:border-slate-700 px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                    Hay {proyectosDetalle.length - proyectosPreview.length} registro(s) adicionales que tambien quedaran guardados.
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-800/60 px-4 py-5 text-sm text-slate-500 dark:text-slate-400">
                                Aun no se ha cargado la plantilla de proyectos. Cuando la importe aqui, el detalle quedara vinculado al formulario del ano seleccionado.
                              </div>
                            )}
                          </div>
                        </SCard>

                        

                        <SCard className="p-5">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="material-icons-round text-[#2D5BA6] text-lg">people_outline</span>
                            <div>
                              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Personal vinculado a proyectos de investigación</h3>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Las preguntas condicionales del bloque se registran al final, siguiendo el orden visible P18 a P21.</p>
                            </div>
                          </div>
                          <div className="space-y-4">
                            {[
                              ['P21', 'P18', 'Docentes tiempo completo titulares que participan en proyectos de investigación', 'docentes_tc_titulares_inv', 'p21_aplica'],
                              ['P22', 'P19', 'Docentes tiempo completo no titulares que participan en proyectos de investigación', 'docentes_tc_no_titulares_inv', 'p22_aplica'],
                            ].map(([code, displayCode, label, key, gateField]) => {
                              const yy = referenceYear[0];
                              const h = Number(matricesView[key]?.[yy]?.h) || 0;
                              const m = Number(matricesView[key]?.[yy]?.m) || 0;
                              const gate = extra[gateField];
                              return (
                                <div key={key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <SmallPBadge code={code} display={displayCode} />
                                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setExtra((ex) => ({ ...ex, [gateField]: true }))}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                      >
                                        Sí
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExtra((ex) => ({ ...ex, [gateField]: false }));
                                          patchMatrixHm(key, yy, 'h', '');
                                          patchMatrixHm(key, yy, 'm', '');
                                        }}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                      >
                                        No
                                      </button>
                                    </div>
                                  </div>
                                  {gate === true && (
                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                      <div className="sm:col-span-1 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-4 py-3 bg-white/70 dark:bg-slate-800/50">
                                        <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Año de referencia</p>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{yy}</p>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Hombres</label>
                                        <input type="number" min={0} className={inputCls} value={matricesView[key]?.[yy]?.h ?? ''} onChange={(e) => patchMatrixHm(key, yy, 'h', e.target.value)} {...numericInputProps()} />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Mujeres</label>
                                        <input type="number" min={0} className={inputCls} value={matricesView[key]?.[yy]?.m ?? ''} onChange={(e) => patchMatrixHm(key, yy, 'm', e.target.value)} {...numericInputProps()} />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Total</label>
                                        <input type="number" readOnly tabIndex={-1} className={inputDisabledCls} value={(h + m) || ''} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
                              {[
                                ['P28', 'P20', 'Estudiantes de pregrado que participan en proyectos de investigación', 'num_estudiantes_pregrado_inv', 'p28_aplica'],
                                ['P29', 'P21', 'Estudiantes de posgrado que participan en proyectos de investigación', 'num_estudiantes_posgrado_inv', 'p29_aplica'],
                              ].map(([code, displayCode, label, prefix, gateField]) => {
                                const h = Number(nums[`${prefix}_hombres`]) || 0;
                                const m = Number(nums[`${prefix}_mujeres`]) || 0;
                                const gate = extra[gateField];
                                return (
                                  <div key={code} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-700/40">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <SmallPBadge code={code} display={displayCode} />
                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setExtra((ex) => ({ ...ex, [gateField]: true }))}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === true ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          Sí
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setExtra((ex) => ({ ...ex, [gateField]: false }));
                                            setNums((prev) => ({ ...prev, [`${prefix}_hombres`]: '', [`${prefix}_mujeres`]: '' }));
                                          }}
                                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${gate === false ? 'bg-slate-600 text-white border-slate-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                        >
                                          No
                                        </button>
                                      </div>
                                    </div>
                                    {gate === true ? (
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Hombres</label>
                                          <input type="number" min={0} value={nums[`${prefix}_hombres`] ?? ''} onChange={(e) => setNumField(`${prefix}_hombres`, e.target.value)} {...numericInputProps()} className={inputCls} />
                                        </div>
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Mujeres</label>
                                          <input type="number" min={0} value={nums[`${prefix}_mujeres`] ?? ''} onChange={(e) => setNumField(`${prefix}_mujeres`, e.target.value)} {...numericInputProps()} className={inputCls} />
                                        </div>
                                        <div>
                                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Total</label>
                                          <input type="number" readOnly tabIndex={-1} value={(h + m) || ''} className={inputDisabledCls} />
                                        </div>
                                      </div>
                                    ) : gate === false ? (
                                      <p className="text-sm text-slate-500 italic">No aplica para el año de referencia.</p>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </SCard>
                      </div>
                    )}

                    {/* ── Step 5: Producción científica ────────────────────── */}
                    {step === 5 && (() => {
                      const prod = extra.produccion || {};
                      const patchProd = (key, v) => {
                        try { setExtra((ex) => ({ ...ex, produccion: { ...(ex.produccion || {}), [key]: JSON.parse(v) } })); } catch { }
                      };
                      const patchCongresosUnified = (v) => {
                        try {
                          const parsed = JSON.parse(v);
                          setExtra((ex) => ({
                            ...ex,
                            produccion: {
                              ...(ex.produccion || {}),
                              p33_congresos: parsed,
                              p33_congresos_nacionales: {},
                              p33_congresos_internacionales: {},
                            },
                          }));
                        } catch { }
                      };
                      const currentYear = referenceYear[0] || String(new Date().getFullYear());

                      const propiedadPayload = (() => {
                        const raw = extra.p27_patentes;
                        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
                          return { aplica: '', registros: [] };
                        }
                        if (Array.isArray(raw.registros)) {
                          return {
                            aplica: raw.aplica === 'si' || raw.aplica === 'no' ? raw.aplica : '',
                            registros: raw.registros.length > 0
                              ? raw.registros.map((row) => ({
                                categoria: String(row?.categoria || ''),
                                anio: String(row?.anio || ''),
                                codigo_unico: String(row?.codigo_unico || ''),
                                nombre: String(row?.nombre || ''),
                                descripcion: String(row?.descripcion || ''),
                                estado: String(row?.estado || ''),
                              }))
                              : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }],
                          };
                        }
                        const legacyTipos = Array.isArray(raw.tipos) ? raw.tipos : [];
                        const legacyDetalles = raw.detalles && typeof raw.detalles === 'object' && !Array.isArray(raw.detalles) ? raw.detalles : {};
                        const registros = legacyTipos.flatMap((tipo) => {
                          const rows = Array.isArray(legacyDetalles[tipo]) ? legacyDetalles[tipo] : [];
                          return rows.map((row) => ({
                            categoria: tipo,
                            anio: String(row?.anio || ''),
                            codigo_unico: String(row?.codigo_unico || ''),
                            nombre: String(row?.nombre || ''),
                            descripcion: String(row?.descripcion || ''),
                            estado: String(row?.estado || ''),
                          }));
                        });
                        return {
                          aplica: raw.aplica === 'si' || raw.aplica === 'no' ? raw.aplica : '',
                          registros: registros.length > 0 ? registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }],
                        };
                      })();

                      const transferenciaPayload = (() => {
                        const raw = extra.p37_transferencia;
                        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
                          return { aplica: '', registros: [] };
                        }
                        return {
                          aplica: raw.aplica === 'si' || raw.aplica === 'no' ? raw.aplica : '',
                          registros: Array.isArray(raw.registros) && raw.registros.length > 0
                            ? raw.registros.map((row) => ({
                              anio: String(row?.anio || ''),
                              nombre_resultado: String(row?.nombre_resultado || ''),
                              tipos: Array.isArray(row?.tipos) ? row.tipos.map(String) : [],
                            }))
                            : [{ anio: '', nombre_resultado: '', tipos: [] }],
                        };
                      })();

                      const setPropiedadPayload = (next) => setExtra((ex) => ({ ...ex, p27_patentes: next }));
                      const setTransferenciaPayload = (next) => setExtra((ex) => ({ ...ex, p37_transferencia: next }));

                      const CineSection = ({ pCode, label, dataKey }) => (
                        <YearCategoryGrid
                          code={pCode}
                          label={label}
                          years={matrixYears}
                          categories={cineCategories}
                          value={JSON.stringify(prod[dataKey] || {})}
                          onChange={(v) => patchProd(dataKey, v)}
                        />
                      );

                      return (
                        <div className="space-y-5">
                          <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">{STEPS[5].title}</h2>
                            <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">P30 – P38</span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3">
                            Años: {matrixYears.join(', ')} · Cada fila corresponde a un campo amplio de la Clasificación Internacional Normalizada de la Educación (CINE).
                          </p>

                          {false && (
                            <SCard className="p-5">
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <SmallPBadge code="P27" display="39" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Propiedad intelectual registrada</p>
                                </div>
                                <p className="text-xs text-slate-400 dark:text-slate-500">Si aplica, registre cada pieza de propiedad intelectual con su categoría, año de registro y datos.</p>
                              </div>
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-3">
                                  {['si', 'no'].map((option) => {
                                    const checked = propiedadPayload.aplica === option;
                                    return (
                                      <label
                                        key={option}
                                        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${checked
                                            ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20'
                                            : 'border-slate-200 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                                          }`}
                                      >
                                        <input
                                          type="radio"
                                          className="sr-only"
                                          checked={checked}
                                          onChange={() =>
                                            setPropiedadPayload(
                                              option === 'si'
                                                ? { aplica: 'si', registros: propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }] }
                                                : { aplica: 'no', registros: [] }
                                            )
                                          }
                                        />
                                        <span>{option === 'si' ? 'Sí' : 'No'}</span>
                                      </label>
                                    );
                                  })}
                                </div>

                                {propiedadPayload.aplica === 'si' && (
                                  <div className="space-y-4">
                                    {(propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }]).map((row, index, arr) => (
                                      <div key={`propiedad-${index}`} className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/70">
                                          <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Registro #{index + 1}</p>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const nextRows = arr.filter((_, rowIndex) => rowIndex !== index);
                                              setPropiedadPayload({
                                                aplica: 'si',
                                                registros: nextRows.length > 0 ? nextRows : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }],
                                              });
                                            }}
                                            className="rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-900/20"
                                          >
                                            Quitar
                                          </button>
                                        </div>
                                        <div className="grid gap-3 p-4 lg:grid-cols-2 xl:grid-cols-3">
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Categoría</label>
                                            <select
                                              value={row.categoria || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, categoria: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                            >
                                              <option value="">Seleccione categoría</option>
                                              {PROPIEDAD_INTELECTUAL_CATEGORIAS.map((category) => (
                                                <option key={category.key} value={category.key}>{category.label}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Año de registro</label>
                                            <select
                                              value={row.anio || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, anio: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                            >
                                              <option value="">Seleccione año</option>
                                              {matrixYears.map((year) => (
                                                <option key={year} value={year}>{year}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Código único</label>
                                            <input
                                              type="text"
                                              value={row.codigo_unico || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, codigo_unico: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                              placeholder="Ej: SENADI-2025-00123"
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Nombre</label>
                                            <input
                                              type="text"
                                              value={row.nombre || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, nombre: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                              placeholder="Nombre del registro"
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Descripción</label>
                                            <input
                                              type="text"
                                              value={row.descripcion || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, descripcion: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                              placeholder="Descripción breve"
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Estado</label>
                                            <select
                                              value={row.estado || ''}
                                              onChange={(e) => {
                                                const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, estado: e.target.value } : item);
                                                setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                              }}
                                              className={inputCls}
                                            >
                                              {PROPIEDAD_INTELECTUAL_ESTADOS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => setPropiedadPayload({
                                        aplica: 'si',
                                        registros: [
                                          ...(propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }]),
                                          { categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' },
                                        ],
                                      })}
                                      className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white hover:bg-[#244d8f]"
                                    >
                                      <span className="material-icons-round text-sm">add</span>
                                      Agregar registro
                                    </button>
                                  </div>
                                )}
                              </div>
                            </SCard>
                          )}

                          <SCard className="p-5 border-blue-100 dark:border-blue-900/50">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="material-icons-round text-[#2D5BA6] text-lg">auto_awesome</span>
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Autollenar producción científica con heurística</h3>
                                </div>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-2xl">
                                  Usa las publicaciones de la institución entre {matrixYears[0]} y {matrixYears[4]}, analiza topics, conceptos y títulos, y propone los campos CINE para P30-P38.
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled={!universidadId || aiFill.loading || saving}
                                onClick={autofillProduccionConIa}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2D5BA6] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#244d8f] disabled:opacity-40"
                              >
                                <span className="material-icons-round text-base">{aiFill.loading ? 'hourglass_top' : 'auto_fix_high'}</span>
                                {aiFill.loading ? 'Analizando...' : 'Autollenar con heurística'}
                              </button>
                            </div>
                            {(aiFill.loading || aiFill.resumen || aiFill.advertencias.length > 0) && (
                              <div className="mt-4">
                                <AiFillSteps
                                  current={aiFill.current}
                                  loading={aiFill.loading}
                                  resumen={aiFill.resumen}
                                  advertencias={aiFill.advertencias}
                                />
                              </div>
                            )}
                          </SCard>

                          {/* P30 – P32: Artículos */}
                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-5">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">article</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Artículos científicos</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Por campo amplio de la Clasificación Internacional Normalizada de la Educación (CINE) · {matrixYears[0]}–{matrixYears[4]}</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <CineSection pCode="P30" label="Artículos en revistas indexadas Scopus / WOS" dataKey="p30_scopus_wos" />
                              <CineSection pCode="P31" label="Artículos indexados en Latindex u otras bases regionales" dataKey="p31_latindex" />
                              <CineSection pCode="P32" label="Artículos en revistas sin indexación reconocida" dataKey="p32_sin_index" />
                            </div>
                          </SCard>

                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-5">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">campaign</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Producción científica — Ponencias en congresos</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Por campo amplio CINE para los últimos 5 años ({matrixYears[0]}–{matrixYears[4]}).</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60 space-y-4">
                                <div className="flex items-start gap-3">
                                  <SmallPBadge code="P22" display="22" className="mt-0.5" />
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ponencias en congresos nacionales e internacionales</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Registre el total de ponencias por campo amplio CINE y año.</p>
                                  </div>
                                </div>
                                <YearCategoryGrid
                                  code={null}
                                  label="Ponencias en congresos nacionales e internacionales"
                                  years={matrixYears}
                                  categories={cineCategories}
                                  value={JSON.stringify(
                                    mergeYearCategoryMatrices(
                                      prod.p33_congresos,
                                      prod.p33_congresos_nacionales,
                                      prod.p33_congresos_internacionales
                                    )
                                  )}
                                  onChange={patchCongresosUnified}
                                />
                              </div>
                              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70 space-y-4">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Detalle estructurado de congresos</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Importe el CSV oficial para guardar el detalle en la base y alimentar automáticamente los totales nacionales e internacionales.</p>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <a href={PRODUCCION_CONGRESOS_TEMPLATE_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">download</span>
                                      Plantilla XLSX
                                    </a>
                                    <a href={PRODUCCION_CONGRESOS_CSV_TEMPLATE_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">description</span>
                                      Plantilla CSV
                                    </a>
                                    <button type="button" onClick={() => congresosFileInputRef.current?.click()} className={actionLinkPrimaryCls}>
                                      <span className="material-icons-round text-sm">upload_file</span>
                                      Cargar CSV
                                    </button>
                                    {congresosDetalle.length > 0 && (
                                      <button type="button" onClick={clearCongresosImport} className={actionLinkSecondaryCls}>
                                        <span className="material-icons-round text-sm">delete</span>
                                        Limpiar carga
                                      </button>
                                    )}
                                    <a href={PRODUCCION_DROPBOX_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">cloud_upload</span>
                                      Subir respaldo
                                    </a>
                                  </div>
                                  <input ref={congresosFileInputRef} type="file" accept=".csv,text/csv" onChange={handleCongresosCsvSelected} className="hidden" />
                                </div>
                                <div className="grid gap-3 md:grid-cols-4">
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Registros</p>
                                    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{congresosImportSummary.total}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Nacionales</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{congresosImportSummary.nacionales}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Internacionales</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{congresosImportSummary.internacionales}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Persistencia</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">Tabla `produccion_congreso_ies`</p>
                                  </div>
                                </div>
                                {congresosImportSummary.warnings.length > 0 && (
                                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                    {congresosImportSummary.warnings.slice(0, 8).map((warning, index) => (
                                      <p key={`${warning}-${index}`}>- {warning}</p>
                                    ))}
                                  </div>
                                )}
                                {congresosDetalle.length > 0 && (
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                                    <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
                                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Vista previa de congresos importados</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-700/60">
                                          <tr>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Año</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Campo CINE</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Evento</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Alcance</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Docentes</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {congresosPreview.map((row, idx) => (
                                            <tr key={`${row.nombre_congreso_o_evento}-${idx}`} className="border-t border-slate-100 dark:border-slate-700">
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.anio}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.campo_cine}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300 min-w-[260px]">{row.nombre_congreso_o_evento}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300 capitalize">{row.alcance}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.docentes_asistentes || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </SCard>

                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-5">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">menu_book</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Producción científica — Libros y/o capítulos de libros</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Por campo amplio CINE para los últimos 5 años ({matrixYears[0]}–{matrixYears[4]}).</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <CineSection pCode="P34" label="Libros y/o capítulos de libros" dataKey="p34_libros" />
                              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70 space-y-4">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Detalle estructurado de libros y capítulos</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Importe el CSV oficial para guardar el detalle en la base y alimentar automáticamente el agregado de P34.</p>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <a href={PRODUCCION_LIBROS_TEMPLATE_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">download</span>
                                      Plantilla XLSX
                                    </a>
                                    <a href={PRODUCCION_LIBROS_CSV_TEMPLATE_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">description</span>
                                      Plantilla CSV
                                    </a>
                                    <button type="button" onClick={() => librosFileInputRef.current?.click()} className={actionLinkPrimaryCls}>
                                      <span className="material-icons-round text-sm">upload_file</span>
                                      Cargar CSV
                                    </button>
                                    {librosDetalle.length > 0 && (
                                      <button type="button" onClick={clearLibrosImport} className={actionLinkSecondaryCls}>
                                        <span className="material-icons-round text-sm">delete</span>
                                        Limpiar carga
                                      </button>
                                    )}
                                    <a href={PRODUCCION_DROPBOX_URL} target="_blank" rel="noreferrer" className={actionLinkSecondaryCls}>
                                      <span className="material-icons-round text-sm">cloud_upload</span>
                                      Subir respaldo
                                    </a>
                                  </div>
                                  <input ref={librosFileInputRef} type="file" accept=".csv,text/csv" onChange={handleLibrosCsvSelected} className="hidden" />
                                </div>
                                <div className="grid gap-3 md:grid-cols-3">
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Registros</p>
                                    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{librosImportSummary.total}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Persistencia</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">Tabla `produccion_libro_ies`</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Agregado</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">Actualiza P34 automáticamente</p>
                                  </div>
                                </div>
                                {librosImportSummary.warnings.length > 0 && (
                                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                    {librosImportSummary.warnings.slice(0, 8).map((warning, index) => (
                                      <p key={`${warning}-${index}`}>- {warning}</p>
                                    ))}
                                  </div>
                                )}
                                {librosDetalle.length > 0 && (
                                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                                    <div className="border-b border-slate-100 dark:border-slate-700 px-4 py-3">
                                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Vista previa de libros importados</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-700/60">
                                          <tr>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Año</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Campo CINE</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Título</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Tipo</th>
                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Editorial</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {librosPreview.map((row, idx) => (
                                            <tr key={`${row.titulo_obra}-${idx}`} className="border-t border-slate-100 dark:border-slate-700">
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.anio}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.campo_cine}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300 min-w-[260px]">{row.titulo_obra}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.tipo_obra}</td>
                                              <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{row.editorial || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </SCard>

                          {/* P35 – P38: Por cuartil Scimago */}
                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-5">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">bar_chart</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Publicaciones por cuartil Scimago</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Por campo amplio de la Clasificación Internacional Normalizada de la Educación (CINE) · {matrixYears[0]}–{matrixYears[4]}</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <CineSection pCode="P35" label="Publicaciones en Q1 (primer cuartil)" dataKey="p35_q1" />
                              <CineSection pCode="P36" label="Publicaciones en Q2 (segundo cuartil)" dataKey="p36_q2" />
                              <CineSection pCode="P37" label="Publicaciones en Q3 (tercer cuartil)" dataKey="p37_q3" />
                              <CineSection pCode="P38" label="Publicaciones en Q4 (cuarto cuartil)" dataKey="p38_q4" />
                            </div>
                          </SCard>

                          <SCard className="p-5">
                            <div className="flex items-center gap-2 mb-5">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">hub</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Otras métricas de producción científica</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Propiedad intelectual y transferencia tecnológica del año de referencia.</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                                <div className="flex items-center gap-2 mb-3">
                                  <SmallPBadge code="P27" display="39" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Propiedad intelectual registrada</p>
                                </div>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">Si aplica, registre cada pieza de propiedad intelectual con su categoría, año de registro y datos.</p>
                                <div className="space-y-4">
                                  <div className="flex flex-wrap gap-3">
                                    {['si', 'no'].map((option) => {
                                      const checked = propiedadPayload.aplica === option;
                                      return (
                                        <label
                                          key={option}
                                          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${checked
                                              ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20'
                                              : 'border-slate-200 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                                            }`}
                                        >
                                          <input
                                            type="radio"
                                            className="sr-only"
                                            checked={checked}
                                            onChange={() =>
                                              setPropiedadPayload(
                                                option === 'si'
                                                  ? { aplica: 'si', registros: propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }] }
                                                  : { aplica: 'no', registros: [] }
                                              )
                                            }
                                          />
                                          <span>{option === 'si' ? 'Sí' : 'No'}</span>
                                        </label>
                                      );
                                    })}
                                  </div>

                                  {propiedadPayload.aplica === 'si' && (
                                    <div className="space-y-4">
                                      {(propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }]).map((row, index, arr) => (
                                        <div key={`propiedad-metricas-${index}`} className="rounded-2xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                                          <div className="mb-3 flex items-center justify-between">
                                            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Registro #{index + 1}</p>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const nextRows = arr.filter((_, rowIndex) => rowIndex !== index);
                                                setPropiedadPayload({
                                                  aplica: 'si',
                                                  registros: nextRows.length > 0 ? nextRows : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }],
                                                });
                                              }}
                                              className="rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-900/20"
                                            >
                                              Quitar
                                            </button>
                                          </div>
                                          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Categoría</label>
                                              <select
                                                value={row.categoria || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, categoria: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                              >
                                                <option value="">Seleccione categoría</option>
                                                {PROPIEDAD_INTELECTUAL_CATEGORIAS.map((category) => (
                                                  <option key={category.key} value={category.key}>{category.label}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Año de registro</label>
                                              <select
                                                value={row.anio || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, anio: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                              >
                                                <option value="">Seleccione año</option>
                                                {matrixYears.map((year) => (
                                                  <option key={year} value={year}>{year}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Código único</label>
                                              <input
                                                type="text"
                                                value={row.codigo_unico || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, codigo_unico: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                                placeholder="Ej: SENADI-2025-00123"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Nombre</label>
                                              <input
                                                type="text"
                                                value={row.nombre || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, nombre: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                                placeholder="Nombre del registro"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Descripción</label>
                                              <input
                                                type="text"
                                                value={row.descripcion || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, descripcion: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                                placeholder="Descripción breve"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Estado</label>
                                              <select
                                                value={row.estado || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, estado: e.target.value } : item);
                                                  setPropiedadPayload({ ...propiedadPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                              >
                                                {PROPIEDAD_INTELECTUAL_ESTADOS.map((option) => (
                                                  <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => setPropiedadPayload({
                                          aplica: 'si',
                                          registros: [
                                            ...(propiedadPayload.registros.length ? propiedadPayload.registros : [{ categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' }]),
                                            { categoria: '', anio: '', codigo_unico: '', nombre: '', descripcion: '', estado: '' },
                                          ],
                                        })}
                                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white hover:bg-[#244d8f]"
                                      >
                                        <span className="material-icons-round text-sm">add</span>
                                        Agregar registro
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                                <div className="flex items-center gap-2 mb-3">
                                  <SmallPBadge code="P37" display="40" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Transferencia tecnológica</p>
                                </div>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">Registre cada mecanismo de transferencia tecnológica asociado al año y nombre del resultado.</p>
                                <div className="space-y-4">
                                  <div className="flex flex-wrap gap-3">
                                    {['si', 'no'].map((option) => {
                                      const checked = transferenciaPayload.aplica === option;
                                      return (
                                        <label
                                          key={option}
                                          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${checked
                                              ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20'
                                              : 'border-slate-200 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                                            }`}
                                        >
                                          <input
                                            type="radio"
                                            className="sr-only"
                                            checked={checked}
                                            onChange={() =>
                                              setTransferenciaPayload(
                                                option === 'si'
                                                  ? { aplica: 'si', registros: transferenciaPayload.registros.length ? transferenciaPayload.registros : [{ anio: currentYear, nombre_resultado: '', mecanismo: '', otro_mecanismo: '' }] }
                                                  : { aplica: 'no', registros: [] }
                                              )
                                            }
                                          />
                                          <span>{option === 'si' ? 'Sí' : 'No'}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  {transferenciaPayload.aplica === 'si' && (
                                    <div className="space-y-3">
                                      {(transferenciaPayload.registros.length ? transferenciaPayload.registros : [{ anio: '', nombre_resultado: '', tipos: [] }]).map((row, index, arr) => (
                                        <div key={`transfer-${index}`} className="rounded-2xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                                          <div className="mb-3 flex items-center justify-between">
                                            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Registro #{index + 1}</p>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const nextRows = arr.filter((_, rowIndex) => rowIndex !== index);
                                                setTransferenciaPayload({ aplica: 'si', registros: nextRows.length ? nextRows : [{ anio: '', nombre_resultado: '', tipos: [] }] });
                                              }}
                                              className="rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-900/20"
                                            >
                                              Quitar
                                            </button>
                                          </div>
                                          <div className="grid gap-3 lg:grid-cols-4">
                                            <div>
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Año</label>
                                              <select
                                                value={row.anio || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, anio: e.target.value } : item);
                                                  setTransferenciaPayload({ ...transferenciaPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                              >
                                                <option value="">Seleccione año</option>
                                                {matrixYears.map((year) => (
                                                  <option key={year} value={year}>{year}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div className="lg:col-span-2">
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block font-medium">Nombre del resultado o mecanismo</label>
                                              <input
                                                type="text"
                                                value={row.nombre_resultado || ''}
                                                onChange={(e) => {
                                                  const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, nombre_resultado: e.target.value } : item);
                                                  setTransferenciaPayload({ ...transferenciaPayload, registros: nextRows });
                                                }}
                                                className={inputCls}
                                                placeholder="Ej: Licencia de plataforma, spin-off, convenio"
                                              />
                                            </div>
                                            <div className="lg:col-span-4">
                                              <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block font-medium">Tipo de transferencia</label>
                                              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                                                {TRANSFERENCIA_TECNOLOGICA_TIPOS.filter((option) => option.value).map((option) => {
                                                  const checked = Array.isArray(row.tipos) && row.tipos.includes(option.value);
                                                  return (
                                                    <CheckboxCard
                                                      key={`${index}-${option.value}`}
                                                      checked={checked}
                                                      label={option.label}
                                                      onChange={() => {
                                                        const currentTipos = Array.isArray(row.tipos) ? row.tipos : [];
                                                        const nextTipos = checked
                                                          ? currentTipos.filter((item) => item !== option.value)
                                                          : [...currentTipos, option.value];
                                                        const nextRows = arr.map((item, rowIndex) => rowIndex === index ? { ...item, tipos: nextTipos } : item);
                                                        setTransferenciaPayload({ ...transferenciaPayload, registros: nextRows });
                                                      }}
                                                    />
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => setTransferenciaPayload({
                                          aplica: 'si',
                                          registros: [...(transferenciaPayload.registros.length ? transferenciaPayload.registros : [{ anio: '', nombre_resultado: '', tipos: [] }]), { anio: '', nombre_resultado: '', tipos: [] }],
                                        })}
                                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#2D5BA6] px-3 py-2 text-xs font-semibold text-white hover:bg-[#244d8f]"
                                      >
                                        <span className="material-icons-round text-sm">add</span>
                                        Agregar registro
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SCard>

                        </div>
                      );
                    })()}

                    {/* ── Step 6: Vigilancia tecnológica ───────────────────── */}
                    {step === 6 && (() => {
                      const setVtField = (field, value) => setVtResp((prev) => ({ ...prev, [field]: value }));
                      const toggleVtProduct = (value, checked) => {
                        const current = Array.isArray(vtResp.p48_productos_vt) ? vtResp.p48_productos_vt : [];
                        setVtField('p48_productos_vt', checked ? [...current, value] : current.filter((item) => item !== value));
                      };
                      const renderVtLikert = (field, code, label, displayCode = null) => (
                        <SCard className="p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <PBadge code={code} display={displayCode} />
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</p>
                          </div>
                          <LikertButtons value={Number(vtResp[field] || 0) || null} onChange={(n) => setVtField(field, String(n))} />
                        </SCard>
                      );
                      const renderVtConditionalLikert = (field, code, label, displayCode = null) => {
                        const parsed = parseSiNoLikert(vtResp[field]);
                        return (
                          <SCard className="p-5">
                            <div className="flex items-center gap-3 mb-4">
                              <PBadge code={code} display={displayCode} />
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              {['si', 'no'].map((value) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => setVtField(field, stringifySiNoLikert({ aplica: value, nivel: value === 'si' ? (parsed.nivel || '1') : '' }))}
                                  className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${parsed.aplica === value ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300' : 'border-slate-200 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
                                >
                                  {value === 'si' ? 'Sí' : 'No'}
                                </button>
                              ))}
                            </div>
                            {parsed.aplica === 'si' && (
                              <LikertButtons value={Number(parsed.nivel || 0) || null} onChange={(n) => setVtField(field, stringifySiNoLikert({ aplica: 'si', nivel: String(n) }))} />
                            )}
                          </SCard>
                        );
                      };
                      const parsedP42 = parsePlanVt(vtResp.p42_plan_formal_vt);
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">{STEPS[6].title}</h2>
                            <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">P41 – P53</span>
                          </div>

                          <SCard className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">radar</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Vigilancia Tecnológica e innovación</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Bloque alineado al formulario fuente en numeración visible y lógica de respuesta.</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                  <PBadge code="P39" display="41" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿La universidad cuenta con una unidad, sistema, responsable o procesos de Vigilancia Tecnológica?</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {VT_P39_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => setVtField('p39_unidad_vt', option.value)}
                                      className={`rounded-xl border-2 p-4 text-left transition-all ${vtResp.p39_unidad_vt === option.value
                                          ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300'
                                          : 'border-slate-200 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                                        }`}
                                    >
                                      <span className="text-sm font-semibold">
                                        {option.label}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {renderVtLikert('p40_vt_planes_estrategicos', 'P40', '¿La Vigilancia Tecnológica está incorporada en planes estratégicos, políticas o agendas de investigación institucionales?', '42')}
                              {renderVtLikert('p41_necesidades_vt', 'P41', '¿Se identifican y priorizan necesidades de información y factores críticos para realizar Vigilancia Tecnológica?', '43')}

                              <SCard className="p-5">
                                <div className="flex items-center gap-3 mb-4">
                                  <PBadge code="P42" display="44" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿Existe un plan formal de Vigilancia Tecnológica que incluya objetivos, actividades, cronograma y responsables?</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                  {['si', 'no'].map((value) => (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={() => setVtField('p42_plan_formal_vt', stringifyPlanVt({ aplica: value, alcance: value === 'si' ? (parsedP42.alcance || 'institucional') : undefined }))}
                                      className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${parsedP42.aplica === value ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300' : 'border-slate-200 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
                                    >
                                      {value === 'si' ? 'Sí' : 'No'}
                                    </button>
                                  ))}
                                </div>
                                {parsedP42.aplica === 'si' && (
                                  <div className="grid grid-cols-2 gap-3">
                                    {[
                                      ['institucional', 'Institucional'],
                                      ['departamentos', 'Por departamentos/unidades'],
                                    ].map(([value, label]) => (
                                      <button
                                        key={value}
                                        type="button"
                                        onClick={() => setVtField('p42_plan_formal_vt', stringifyPlanVt({ aplica: 'si', alcance: value }))}
                                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${parsedP42.alcance === value ? 'border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300' : 'border-slate-200 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
                                      >
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </SCard>

                              {renderVtConditionalLikert('p43_roles_vt', 'P43', '¿El equipo responsable de VT cuenta con roles definidos y competencias en búsqueda, análisis y difusión?', '45')}
                              {renderVtConditionalLikert('p44_herramientas_vt', 'P44', '¿La institución dispone de herramientas (bases de datos, alertas, software bibliométrico/patentes) para VT?', '46')}
                              {renderVtConditionalLikert('p45_busqueda_sistematica_vt', 'P45', '¿La búsqueda y monitoreo de información se realiza de forma sistemática y periódica en fuentes relevantes?', '47')}
                              {renderVtConditionalLikert('p46_tecnicas_analisis_vt', 'P46', '¿Se aplican técnicas de análisis (bibliometría, mapas tecnológicos, análisis de patentes, tendencias)?', '48')}
                              {renderVtConditionalLikert('p47_resultados_vt', 'P47', '¿Los resultados de la VT se transforman en conclusiones y recomendaciones útiles para la institución?', '49')}

                              <SCard className="p-5">
                                <div className="flex items-center gap-3 mb-4">
                                  <PBadge code="P48" display="50" />
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿Qué productos de VT se generan regularmente (boletines, informes, alertas, estados del arte)?</p>
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                  {VT_P48_OPTIONS.map((option) => {
                                    const selected = (vtResp.p48_productos_vt || []).includes(option.value);
                                    return (
                                      <label key={option.value} className={`flex items-center gap-3 rounded-xl border p-3 transition-all ${selected ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800'}`}>
                                        <input type="checkbox" checked={selected} onChange={(e) => toggleVtProduct(option.value, e.target.checked)} />
                                        <span className="text-sm text-slate-700 dark:text-slate-300">{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </SCard>

                              {renderVtConditionalLikert('p49_productos_decisiones_vt', 'P49', '¿Los productos de VT se utilizan para definir líneas de investigación, proyectos o decisiones estratégicas?', '51')}
                              {renderVtConditionalLikert('p50_evaluacion_vt', 'P50', '¿Se evalúa periódicamente la efectividad del sistema o proceso de VT?', '52')}
                              {renderVtConditionalLikert('p51_mejoras_vt', 'P51', '¿Se realizan mejoras al proceso de VT a partir de evaluaciones o retroalimentación?', '53')}
                            </div>
                          </SCard>
                        </div>
                      );
                    })()}

                    {/* ── Step 7: Plan nacional y ODS ──────────────────────── */}
                    {step === 7 && (() => {
                      const setExtraField = (field, value) => setExtra((ex) => ({ ...ex, [field]: value }));
                      const toggleExtra = (field, val) => setExtra((ex) => {
                        const cur = ex[field] || [];
                        return { ...ex, [field]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
                      });
                      const p52Values = extra.p52_ambitos_politica_publica || [];
                      const p52OtherNames = getP52OtherNames(p52Values);
                      const p64 = parseP64Payload(extra.p64_articulacion_actores_desarrollo);
                      const pndCurrentLabel = pndCatalog.periodo_actual || (anioNum >= 2025 ? '2025-2029' : anioNum === 2024 ? '2024-2025' : '2021-2025');
                      const pndPrevLabel = pndCatalog.periodo_previo || (anioNum >= 2025 ? '2024-2025' : anioNum === 2024 ? '2021-2025' : '2017-2020');
                      const pndObjectives = Array.isArray(pndCatalog.objetivos) ? pndCatalog.objetivos : [];
                      const legacyPndSelections = (extra.p57_objetivos_pnd || []).filter(
                        (value) => !pndObjectives.some((objective) => objective.codigo === value)
                      );

                      const LRow = ({ code, label, field, displayCode = null, scaleLabels = null }) => (
                        <div className="space-y-3 pt-5 border-t border-slate-100 dark:border-slate-700 first:pt-0 first:border-t-0">
                          <div className="flex items-center gap-2">
                            <PBadge code={code} display={displayCode} />
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                          </div>
                          <LikertButtons value={extra[field]} onChange={(n) => setExtraField(field, n)} scaleLabels={scaleLabels} />
                        </div>
                      );

                      return (
                        <div className="space-y-5">
                          <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">{STEPS[7].title}</h2>
                            <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">P54 – P65</span>
                          </div>

                          <SCard className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">flag</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Alineación con el Plan Nacional de Desarrollo</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Bloque alineado con la numeración visible y la lógica del formulario fuente.</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <LRow code="P55" displayCode="54" label={`¿En qué medida la planificación institucional de investigación (Plan Estratégico de Desarrollo Institucional (PEDI), Plan Operativo Anual (POA), otros) integra los objetivos del Plan Nacional de Desarrollo ${pndCurrentLabel}?`} field="p55_importancia_vinculacion_pnd" />
                              <LRow code="P56" displayCode="55" label={`¿En qué medida la planificación institucional de investigación integró los objetivos del PND anterior (${pndPrevLabel})?`} field="p56_grado_alineacion_pnd" />

                              <div className="space-y-3 pt-5 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <PBadge code="P57" display="56" />
                                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{`Indique a cuáles de los siguientes objetivos estratégicos del Plan Nacional de Desarrollo ${pndCurrentLabel} contribuyen las líneas y proyectos de investigación institucional`}</span>
                                </div>
                                {pndCatalog.warning && (
                                  <p className="text-xs text-amber-700 dark:text-amber-300">{pndCatalog.warning}</p>
                                )}
                                {legacyPndSelections.length > 0 && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Este borrador tiene selecciones PND guardadas con una versión anterior. Conviene revisarlas antes de enviar.</p>
                                )}
                                <div className="grid grid-cols-1 gap-3">
                                  {pndObjectives.map((objective) => (
                                    <label
                                      key={objective.codigo}
                                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${extra.p57_objetivos_pnd.includes(objective.codigo) ? 'bg-blue-50 border-[#2D5BA6] dark:bg-blue-900/20' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={extra.p57_objetivos_pnd.includes(objective.codigo)}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setExtra((ex) => ({
                                            ...ex,
                                            p57_objetivos_pnd: checked
                                              ? [...(ex.p57_objetivos_pnd || []), objective.codigo]
                                              : (ex.p57_objetivos_pnd || []).filter((value) => value !== objective.codigo),
                                          }));
                                        }}
                                        className="w-5 h-5 mt-0.5 rounded border-slate-300 text-[#2D5BA6] focus:ring-[#2D5BA6]"
                                      />
                                      <div>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300 mb-1">{objective.codigo}</span>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{objective.nombre}</p>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              <LRow code="P58" displayCode="57" label="¿En qué grado la universidad mantiene articulación activa con gobiernos locales o instituciones gubernamentales para contribuir al cumplimiento de los objetivos del Plan Nacional de Desarrollo (PND)?" field="p58_nivel_participacion_consejos" />
                              <LRow code="P59" displayCode="58" label="¿En qué grado la universidad mantiene articulación activa con el sector productivo para contribuir al cumplimiento de los objetivos del Plan Nacional de Desarrollo (PND)?" field="p59_articulacion_sector_productivo_pnd" />
                              <LRow code="P60" displayCode="59" label="¿En qué medida los resultados de investigación de la universidad han sido utilizados para la formulación, mejora o propuesta de políticas públicas?" field="p60_resultados_politicas_publicas" />

                              <div className="space-y-3 pt-5 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <PBadge code="P52" display="60" />
                                  <div>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿En cuáles ámbitos de la política pública ecuatoriana los resultados de investigación de la universidad han contribuido durante los últimos cinco años?</span>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Puede seleccionar múltiples ámbitos y especificar otros.</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {AMBITOS_POLITICA_PUBLICA.map((item) => (
                                    <CheckboxCard
                                      key={item}
                                      checked={p52Values.includes(item)}
                                      label={item}
                                      onChange={(e) => {
                                        const checked = e?.target?.checked;
                                        if (checked) {
                                          if (item === P52_NO_APLICA_LABEL) {
                                            setExtraField('p52_ambitos_politica_publica', [P52_NO_APLICA_LABEL]);
                                            return;
                                          }
                                          const withoutNoAplica = p52Values.filter((v) => v !== P52_NO_APLICA_LABEL);
                                          setExtraField('p52_ambitos_politica_publica', [...withoutNoAplica, item]);
                                          return;
                                        }
                                        const next = item === P52_OTHER_LABEL
                                          ? p52Values.filter((v) => v !== item && !String(v).startsWith(P52_OTHER_PREFIX))
                                          : p52Values.filter((v) => v !== item);
                                        setExtraField('p52_ambitos_politica_publica', next);
                                      }}
                                    />
                                  ))}
                                </div>
                                {p52Values.includes(P52_OTHER_LABEL) && (
                                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/50 dark:bg-blue-950/20">
                                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-2">¿Cuántos otros ámbitos?</label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={50}
                                        value={p52OtherNames.length || ''}
                                        onChange={(e) => {
                                        const count = Number.parseInt(sanitizeIntegerInput(e.target.value, { max: 50, maxDigits: 2 }) || '0', 10) || 0;
                                        const nextOthers = Array.from({ length: count }, (_, index) => `${P52_OTHER_PREFIX}${p52OtherNames[index] ?? ''}`);
                                        setExtraField('p52_ambitos_politica_publica', [...getP52BaseValues(p52Values), P52_OTHER_LABEL, ...nextOthers]);
                                      }}
                                      {...numericInputProps(50)}
                                      className={`${inputCls} max-w-32`}
                                    />
                                    <div className="mt-3 space-y-2">
                                      {p52OtherNames.map((name, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                          <span className="text-xs font-mono text-[#2D5BA6] w-16">Otro {index + 1}</span>
                                          <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => {
                                              const currentNames = getP52OtherNames(p52Values);
                                              const nextNames = currentNames.map((currentName, currentIndex) => currentIndex === index ? sanitizeInlineText(e.target.value) : currentName);
                                              setExtraField('p52_ambitos_politica_publica', [...getP52BaseValues(p52Values), P52_OTHER_LABEL, ...nextNames.map((v) => `${P52_OTHER_PREFIX}${v}`)]);
                                            }}
                                            className={inputCls}
                                            placeholder="Nombre del ámbito"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="space-y-3 pt-5 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <PBadge code="P53" display="61" />
                                  <div>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿Con cuáles sectores productivos, clasificados según la Clasificación Industrial Internacional Uniforme (CIIU), mantiene articulación activa la universidad?</span>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Clasificados según la Clasificación Industrial Internacional Uniforme (CIIU).</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {SECTORES_CIIU.map((sector) => (
                                    <CheckboxCard
                                      key={sector}
                                      checked={(extra.p53_sectores_ciiu_articulacion || []).includes(sector)}
                                      label={sector}
                                      onChange={() => toggleExtra('p53_sectores_ciiu_articulacion', sector)}
                                    />
                                  ))}
                                </div>
                              </div>

                              <div className="space-y-4 pt-5 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <PBadge code="P64" display="62" />
                                  <div>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">¿En qué medida los resultados de investigación han contribuido al fortalecimiento de los siguientes procesos de vinculación con la sociedad?</span>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Califique cada proceso en escala 1 a 5. Si no existe ningún proceso aplicable, marque “Ninguno / No existe”.</p>
                                  </div>
                                </div>

                                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                                  <input
                                    type="checkbox"
                                    checked={p64.ninguno}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setExtraField('p64_articulacion_actores_desarrollo', stringifyP64Payload({
                                        ...p64,
                                        ninguno: checked,
                                        otros_aplica: checked ? 'no' : p64.otros_aplica,
                                        otros: checked ? '' : p64.otros,
                                        valores: checked ? Object.fromEntries(VINCULACION_RESULTADOS_OPCIONES.map((option) => [option.key, ''])) : p64.valores,
                                      }));
                                    }}
                                  />
                                  Ninguno / No existe
                                </label>

                                <div className="space-y-3">
                                  {VINCULACION_RESULTADOS_OPCIONES.map((option) => (
                                    <div key={option.key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                          {option.label}
                                          {option.key === 'otros' && (
                                            <div className="mt-2 space-y-2">
                                              <div className="flex flex-wrap gap-2">
                                                {['no', 'si'].map((value) => (
                                                  <label key={value} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold ${p64.ninguno ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-500' : p64.otros_aplica === value ? 'cursor-pointer border-[#2D5BA6] bg-blue-50 text-[#2D5BA6] dark:bg-blue-900/20 dark:text-blue-300' : 'cursor-pointer border-slate-200 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                                                    <input
                                                      type="radio"
                                                      className="sr-only"
                                                      name="p64-otros-aplica"
                                                      checked={p64.otros_aplica === value}
                                                      disabled={p64.ninguno}
                                                      onChange={() => setExtraField('p64_articulacion_actores_desarrollo', stringifyP64Payload({
                                                        ...p64,
                                                        otros_aplica: value,
                                                        otros: value === 'si' ? p64.otros : '',
                                                        valores: { ...p64.valores, otros: value === 'si' ? p64.valores.otros : '' },
                                                      }))}
                                                    />
                                                    {value === 'si' ? 'Sí aplica' : 'No aplica'}
                                                  </label>
                                                ))}
                                              </div>
                                              {p64.otros_aplica === 'si' && (
                                                <input
                                                  type="text"
                                                  value={p64.otros}
                                                  disabled={p64.ninguno}
                                                  onChange={(e) => setExtraField('p64_articulacion_actores_desarrollo', stringifyP64Payload({ ...p64, otros: e.target.value }))}
                                                  className={inputCls}
                                                  placeholder="Especifique otro proceso"
                                                />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <div className="grid grid-cols-5 gap-1">
                                          {['1', '2', '3', '4', '5'].map((value) => (
                                            <label key={value} className={`w-9 h-9 rounded-lg border flex items-center justify-center text-sm font-bold cursor-pointer transition-all ${p64.ninguno || (option.key === 'otros' && p64.otros_aplica !== 'si')
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : p64.valores[option.key] === value
                                                  ? 'bg-[#2D5BA6] text-white border-[#2D5BA6]'
                                                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                                              }`}>
                                              <input
                                                type="radio"
                                                className="sr-only"
                                                name={`p64-${option.key}`}
                                                checked={p64.valores[option.key] === value}
                                                disabled={p64.ninguno || (option.key === 'otros' && p64.otros_aplica !== 'si')}
                                                onChange={() => setExtraField('p64_articulacion_actores_desarrollo', stringifyP64Payload({
                                                  ...p64,
                                                  valores: { ...p64.valores, [option.key]: value },
                                                }))}
                                              />
                                              {value}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </SCard>

                          <SCard className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                              <span className="material-icons-round text-[#2D5BA6] text-lg">eco</span>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Objetivos de Desarrollo Sostenible</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Alineación de la investigación institucional con los Objetivos de Desarrollo Sostenible (ODS).</p>
                              </div>
                            </div>
                            <div className="space-y-5">
                              <LRow code="P61" displayCode="63" label="¿En qué medida los ODS están formalmente incorporados en documentos institucionales de planificación de la investigación (PEDI, convocatorias, criterios de evaluación)?" field="p61_integracion_ods_investigacion" />

                              <div className="space-y-3 pt-5 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <PBadge code="P63" display="64" />
                                  <div>
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Objetivos de Desarrollo Sostenible (ODS) priorizados en la agenda de investigación</span>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Seleccione los Objetivos de Desarrollo Sostenible (ODS) que la institución de educación superior (IES) prioriza formalmente.</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                  {ODS_LIST_DETAILED.map(({ num, label, color }) => {
                                    const sel = extra.p63_objetivos_ods || [];
                                    const checked = sel.includes(String(num));
                                    return (
                                      <label
                                        key={num}
                                        className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 cursor-pointer transition-all select-none ${checked ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                          }`}
                                      >
                                        <input type="checkbox" className="sr-only" checked={checked}
                                          onChange={() => setExtra((ex) => ({
                                            ...ex,
                                            p63_objetivos_ods: checked
                                              ? (ex.p63_objetivos_ods || []).filter((x) => x !== String(num))
                                              : [...(ex.p63_objetivos_ods || []), String(num)].sort((a, b) => Number(a) - Number(b)),
                                          }))}
                                        />
                                        <img
                                          src={`/images/ods_${num}.svg`}
                                          alt={`ODS ${num}: ${label}`}
                                          className="w-14 h-14 rounded-lg object-cover"
                                          loading="lazy"
                                          onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div className="hidden w-14 h-14 rounded-lg items-center justify-center text-white font-bold text-xl flex-shrink-0" style={{ backgroundColor: color }}>
                                          {num}
                                        </div>
                                        <span className="text-[9px] font-semibold text-slate-400 dark:text-slate-500 leading-none">ODS {num}</span>
                                        <span className="text-[10px] text-center text-slate-600 dark:text-slate-300 leading-tight line-clamp-2">{label}</span>
                                        {checked && (
                                          <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#2D5BA6] flex items-center justify-center">
                                            <svg viewBox="0 0 10 8" fill="none" className="w-2 h-2">
                                              <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>

                              <LRow
                                code="P62"
                                displayCode="65"
                                label="¿En qué nivel se encuentran institucionalizados los mecanismos de seguimiento que evalúan la contribución de la investigación a los ODS priorizados?"
                                field="p62_medicion_impacto_ods"
                                scaleLabels={[
                                  'No existen mecanismos',
                                  'Existen iniciativas aisladas',
                                  'Existen mecanismos en desarrollo',
                                  'Existen mecanismos formales implementados',
                                  'Sistema institucional consolidado con indicadores y reportes periódicos',
                                ]}
                              />
                            </div>
                          </SCard>
                        </div>
                      );
                    })()}

                    <NavFooter />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

